import "./styles.css";
import * as THREE from "three";
import sillyGalaxyNamesCsv from "../public/silly_galaxy_names.csv?raw";

    const DEFAULTS = {
      theta1Deg: 95.0,
      phi1Deg: 20.0,
      spin1: 1.5,

      theta2Deg: 120.0,
      phi2Deg: 130.0,
      spin2: -1.0,

      L1: 1.0,
      L2: 1.0,
      m1: 1.0,
      m2: 1.0,
      g: 9.81,
      dt: 0.004,

      maxSpeed: 35.0,
      maxRecordedSteps: 5000,
    };

    const CONFIG = {
      stellarOutcomes: {
        lowMassStarMax: 160,
        massiveStarThreshold: 240,
        whiteDwarfFormationMassMax: 120,
        whiteDwarfChandrasekharLimit: 140,
        neutronStarMassThreshold: 180,
        neutronStarMaxMass: 320,
        blackHoleFormationMass: 350,
        pairInstabilityMassThreshold: 600,
        directCollapseMassThreshold: 900,
        unstableStarInstabilityThreshold: 1.0,
        supernovaInstabilityThreshold: 2.5,
        collisionSupernovaEnergyThreshold: 20000,
        collapseEnergyThreshold: 50000,
        starFuelBurnRate: 0.001,
        unstableCoolingRate: 0.05,
        unstableGasSheddingRate: 0.02,
        supernovaGasFraction: 0.45,
        supernovaFragmentFraction: 0.05,
        typeIaGasFraction: 0.95,
        pairInstabilityGasFraction: 1.0,
        directCollapseGasFraction: 0.08,
        maxSupernovaGasParticles: 300,
        maxSupernovaFragments: 60,
        maxKilonovaGasParticles: 150,
        supernovaShockSpeed: 8.0,
        maxSupernovaParticleSpeed: 14.0,
        compactObjectVisualScale: 5.0,
        compactObjectGravityStrength: 1.0,
      },
      trails: {
        gas: 0,
        fragments: 8,
        blobs: 16,
        moons: 24,
        planets: 80,
        stars: 160,
        pendulum: 300,
      },
      ring: {
        enabled: true,
        minPlanetMass: 60,
        minPlanetSpin: 0.35,
        minCapturedDebrisMass: 8,
        occurrenceChance: 0.32,
        rocheMultiplier: 2.5,
        innerRadiusMultiplier: 1.04,
        outerRadiusMultiplier: 4.0,
        bandCount: 7,
        maxRingParticles: 400,
        ringOpacity: 0.35,
        ringThickness: 0.04,
        ringParticleSpeed: 0.15,
        ringGrowthRate: 0.8,
        maxRingMass: 80,
      },
      moons: {
        enabled: true,
        moonMassThreshold: 3,
        moonCaptureSpeedThreshold: 3.5,
        moonInfluenceRadiusMultiplier: 12,
        maxMoonsPerPlanet: 8,
        orbitStabilisationStrength: 0.08,
        maxMoonOrbitInclination: 0.25,
      },
      galaxyPlane: {
        enabled: true,
        settlingStrength: 0.015,
        verticalDamping: 0.08,
        starSettlingStrength: 0.004,
        gasSettlingStrength: 0.035,
        fragmentSettlingStrength: 0.02,
        showPlaneDisc: true,
      },
      galaxy: {
        rotationEnabled: true,
        mass: 1000,
        minRadius: 0.25,
        rotationScale: 0.35,
        spinCoupling: 0.03,
        gasSpinCoupling: 0.08,
        fragmentSpinCoupling: 0.04,
        starSpinCoupling: 0.015,
        blackHoleEffectStrength: 0.015,
      },
      gas: {
        damping: 0.4,
        planeSettling: 0.04,
        galacticSpinCoupling: 0.09,
        lifetime: 80,
        fadeStartAge: 20,
        opacity: 0.18,
        maxParticles: 3000,
        absorbable: true,
      },
      parentAssignment: {
        starInfluenceThreshold: 8,
        starInfluenceRadiusMultiplier: 18,
        starCaptureSpeedThreshold: 2.2,
        starSwitchRatio: 1.8,
        starSwitchDelay: 3.0,
        planetInfluenceRadiusMultiplier: 12,
      },
      planetFormation: {
        planetMassThreshold: 25,
        minimumPlanetFormationAge: 6,
        protectedAge: 15,
        orbitTolerance: 0.5,
        maxPlanetsPerStar: 12,
        planetSafeZoneMultiplier: 3.5,
        starAccretionBudgetPerSecond: 8,
        stablePlanetAbsorptionResistance: 0.86,
      },
      G: 0.00035,
      blackHole: {
        mass: 18000,
      },
      galaxyScale: {
        seed: 742193,
        totalTargetStars: 1000000,
        radius: 22,
        discThickness: 1.6,
        blockSize: 6,
        maxOctreeDepth: 4,
        activeBlockLimit: 1,
        starsPerRenderBatch: 9000,
        lodDistanceGalaxy: 38,
        lodDistanceBlock: 14,
        autoCollapseDistantBlocks: true,
        showBlocks: false,
        showSpiralArms: true,
        showGalacticPlane: true,
        showDensityClouds: false,
        showStarPoints: true,
        showActiveBlockDetail: true,
      },
      growth: {
        enabled: true,
        minGasMass: 10,
        minGasDensity: 0.01,
        minDustDensity: 0.002,
        minCoolingLevel: 0.35,
        maxTemperature: 0.75,
        maxTurbulence: 0.65,
        baseStarFormationRate: 0.0001,
        gasDensityExponent: 1.4,
        starFormationEfficiency: 0.35,
        dustConsumptionFraction: 0.2,
        cooldown: 5.0,
        massTolerance: 0.001,
        externalInflowEnabled: false,
        externalGasInflowRate: 0.02,
        pendulumMatterCreationEnabled: true,
        spiralArmGrowthMultiplier: 2.5,
        gasCloudGrowthMultiplier: 1.8,
        supernovaEnrichmentMultiplier: 1.3,
        haloGrowthMultiplier: 0.2,
        depletionGasThreshold: 0.005,
        depletedCooldownMultiplier: 4.0,
      },
      blocks: {
        maxOctreeDepth: 7,
        highDensityThreshold: 0.4,
        activityThreshold: 0.25,
        minBlockSize: 0.35,
        autoSubdivideSelected: true,
        autoCollapseDistant: true,
      },
      lod: {
        galaxyDistance: 38,
        clusterDistance: 24,
        blockDistance: 14,
        activeDistance: 6,
        systemCollapseDistance: 24,
        systemDetailDistance: 8,
      },
      systemStability: {
        minStableAge: 20,
        minPlanets: 1,
        maxRecentCollisions: 2,
        maxRecentExplosions: 1,
        maxMassChangeRate: 0.05,
        orbitTolerance: 0.45,
        requireDominantStar: true,
        stopPendulumOnStableSystem: true,
      },
      systemTravel: {
        enabled: true,
        autoStartWhenStable: true,
        showGalacticOrbitPath: true,
        galacticOrbitPathOpacity: 0.18,
        systemMarkerSize: 3,
        systemPlaneDamping: 0.05,
        systemOrbitSpeedScale: 1.0,
        systemYawFollowStrength: 0.2,
        preserveLocalPhysics: true,
        corkscrewPlanetOrbits: true,
        collapseDetailWhenZoomedOut: true,
      },
      galaxyShape: {
        armCount: 2,
        armTightness: 3.5,
        discRadius: 22,
        discThickness: 1.6,
        bulgeRadius: 3.2,
        haloRadius: 36,
        spiralArmWidth: 0.35,
        starDensityScale: 1.0,
        gasDensityScale: 1.0,
        bulgeMass: 160000,
        discMass: 900000,
        haloMass: 600000,
        bulgeScale: 2.6,
        discScale: 8,
        haloScale: 20,
      },
    };

    const TRAIL_STRIDE = 1;
    const PHYSICS_SUBSTEPS_PER_FRAME = 4;
    const MAX_DT = 0.006;

    const VELOCITY_DAMPING = 1.0;

    const CONSTRAINT_KP = 28.0;
    const CONSTRAINT_KD = 2.5;

    const PLANE_ALPHA = 0.075;

    let running = false;
    let gridVisible = true;
    let stepCount = 0;

    let p1 = [0, 0, 0];
    let p2 = [0, 0, 0];
    let v1 = [0, 0, 0];
    let v2 = [0, 0, 0];

    let trail = [];
    let referenceReach = null;
    let mathTrail = [];
    let lastCrossingStep = -Infinity;
    let lastCrossingSegment = -1;
    let stellarClock = 0;
    let nextBodyId = 1;
    let nextStarSystemId = 1;

    const stellarBodies = [];
    const starSystemContainers = new Map();
    const recentStellarEvents = [];
    const MAX_STELLAR_BODIES = 360;
    const MAX_MATH_TRAIL_POINTS = 5000;
    const CROSSING_SKIP_SEGMENTS = 18;
    const CROSSING_COOLDOWN_STEPS = 24;
    const STAR_TYPES = new Set(["star", "unstableStar", "redGiant", "whiteDwarf", "neutronStar", "blackHole"]);
    const COMPACT_TYPES = new Set(["whiteDwarf", "neutronStar", "blackHole"]);
    const PLANET_TYPES = new Set(["planet", "roguePlanet"]);
    const ORBITAL_G = 0.006;
    const KEPLER_MIN_ECCENTRICITY = 0.05;
    const KEPLER_MAX_ECCENTRICITY = 0.72;
    const KEPLER_MAX_TRUE_ANOMALY_STEP = 0.18;
    const RING_START_OUTER_DIAMETER_MULTIPLIER = 1.12;
    const RING_MAX_OUTER_DIAMETER_MULTIPLIER = 4.0;
    const ACTIVE_SPACE_DEFAULT_MULTIPLIER = 1;
    const ACTIVE_SPACE_EXPANDED_MULTIPLIER = 3;
    const CENTRAL_STAR_GRAVITY_MULTIPLIER = 0.08;
    const BLOCK_DETAIL_MIN_SCREEN_PX = 100;
    const SYSTEM_MARKER_SHOW_MAX_SCREEN_PX = 48;
    const SYSTEM_MARKER_HIDE_DISTANCE_MULTIPLIER = 14;
    const INITIAL_DUST_VISUAL_CUBE_MIN_SIZE = 0.14;
    const INITIAL_DUST_VISUAL_CUBE_MAX_SIZE = 0.36;
    const FOCUS_DUST_VISUAL_CUBE_MIN_SIZE = 0.055;
    const FOCUS_DUST_VISUAL_CUBE_MAX_SIZE = 0.24;
    const INITIAL_DUST_POINT_PICK_MIN_RADIUS = 0.08;
    const INITIAL_DUST_POINT_PICK_MAX_RADIUS = 0.7;
    const SYSTEM_MARKER_BASE_COLOR = 0xfff06a;
    const SYSTEM_MARKER_ACTIVE_COLOR = 0xffffff;
    const SYSTEM_MARKER_LABEL_COLOR = "#fff6a6";

    const stellarStats = {
      supernovae: 0,
      coreCollapse: 0,
      typeIa: 0,
      kilonova: 0,
      pairInstability: 0,
      directCollapse: 0,
      lastEvent: "none",
      highestInstability: 0,
      largestMass: 0,
    };
    const systemTravelStats = {
      lastPromotedSystem: "none",
      lastDestabilisedSystem: "none",
      selectedSystemId: "none",
      selectedSystemRadius: 0,
      selectedSystemAngularVelocity: 0,
      selectedSystemOrbitalSpeed: 0,
      selectedSystemPlanets: 0,
      selectedSystemMoons: 0,
      selectedSystemStabilityAge: 0,
    };

    function parseCsvLine(line) {
      const values = [];
      let current = "";
      let quoted = false;

      for (let i = 0; i < line.length; i++) {
        const char = line[i];

        if (char === "\"") {
          if (quoted && line[i + 1] === "\"") {
            current += "\"";
            i++;
          } else {
            quoted = !quoted;
          }
        } else if (char === "," && !quoted) {
          values.push(current.trim());
          current = "";
        } else {
          current += char;
        }
      }

      values.push(current.trim());
      return values;
    }

    function parseSystemNamePool(csvText) {
      return String(csvText || "")
        .split(/\r?\n/)
        .slice(1)
        .map(line => parseCsvLine(line)[0])
        .filter(Boolean);
    }

    const systemNamePool = parseSystemNamePool(sillyGalaxyNamesCsv);

    function makeSystemFallbackName(block) {
      const numericId = Number(block?.sourcePointIndex);
      const suffix = Number.isFinite(numericId) ? String(numericId + 1).padStart(4, "0") : String(block?.id || "0000");

      return `Star System ${suffix}`;
    }

    const galacticCenter = new THREE.Vector3(0, 0, 0);
    const defaultGalacticNormal = new THREE.Vector3(0, 0, 1);
    const identityQuaternion = new THREE.Quaternion();
    let galacticNormal = defaultGalacticNormal.clone();
    let orbitVisualFrame = 0;
    let parentLinkFrame = 0;
    let activeSimulationOrigin = new THREE.Vector3(0, 0, 0);
    let cameraTarget = new THREE.Vector3(0, 0, 0);
    let cameraFocusProgress = 0;
    let focusedBlockScreenWidth = 0;
    let focusedBlockScreenHeight = 0;
    let focusedBlockScreenPixels = 0;
    let cameraMode = "Galaxy View";
    let cameraPanLocked = false;
    let currentLODMode = "galaxy";
    let lastFrameTime = performance.now();
    let fpsEstimate = 60;
    let galaxyManager = null;

    const statusEl = document.getElementById("status");
    const controlsEl = document.getElementById("controls");
    const panelEl = document.getElementById("panel");
    const accountStatusEl = document.getElementById("accountStatus");
    const accountSectorEl = document.getElementById("accountSector");
    const googleSignInEl = document.getElementById("googleSignIn");
    const saveAccountButton = document.getElementById("saveAccountButton");
    const loadAccountButton = document.getElementById("loadAccountButton");
    const logoutButton = document.getElementById("logoutButton");
    const panButtons = {
      up: document.getElementById("panUpButton"),
      down: document.getElementById("panDownButton"),
      left: document.getElementById("panLeftButton"),
      right: document.getElementById("panRightButton"),
    };
    const ACCOUNT_API_BASE = "/mental/api";
    const IS_LOCAL_DEV_HOST = ["localhost", "127.0.0.1", "::1"].includes(window.location.hostname);
    const GALAXY_STATE_SLIDER_KEYS = new Set([
      "galaxySeed",
      "totalTargetStars",
      "galaxyRadius",
      "discThickness",
      "blockSize",
      "maxOctreeDepth",
      "activeBlockLimit",
      "starsPerRenderBatch",
      "lodDistanceGalaxy",
      "lodDistanceBlock",
      "spiralArmCount",
      "spiralTightness",
      "bulgeMass",
      "discMass",
      "haloMass",
      "blackHoleMass",
    ]);

    let accountConfigured = false;
    let accountUser = null;
    let accountHasSettings = false;
    let accountUniverseAllocation = null;
    let googleIdentityScriptPromise = null;

    const sliders = {};
    const stellarToggles = {};

    function addSlider(key, label, hint, min, max, step, value, formatter = null, resetOnInput = true) {
      const wrapper = document.createElement("div");
      wrapper.className = "control";

      const labelLine = document.createElement("div");
      labelLine.className = "label-line";

      const labelEl = document.createElement("div");
      labelEl.className = "label";
      labelEl.textContent = label;

      const valueEl = document.createElement("div");
      valueEl.className = "value";

      const hintEl = document.createElement("div");
      hintEl.className = "hint";
      hintEl.textContent = hint;

      const input = document.createElement("input");
      input.type = "range";
      input.min = min;
      input.max = max;
      input.step = step;
      input.value = value;

      const formatValue = () => {
        const n = Number(input.value);
        valueEl.textContent = formatter ? formatter(n) : n.toFixed(2);
      };

      input.addEventListener("input", () => {
        formatValue();

        if (!resetOnInput) {
          updateStellarStatsDisplay();
          return;
        }

        const params = getParams();
        updateTrailCapacity(params.maxRecordedSteps);

        if (!running) {
          resetFromSliders();
        } else {
          updateSceneScale(params, false);
        }
      });

      labelLine.appendChild(labelEl);
      labelLine.appendChild(valueEl);

      wrapper.appendChild(labelLine);
      wrapper.appendChild(hintEl);
      wrapper.appendChild(input);

      controlsEl.appendChild(wrapper);

      sliders[key] = {
        input,
        valueEl,
        formatValue,
      };

      formatValue();
    }

    function addSectionTitle(text) {
      const title = document.createElement("div");
      title.className = "section-title";
      title.textContent = text;
      controlsEl.appendChild(title);
    }

    function addToggle(key, label, hint, checked) {
      const wrapper = document.createElement("label");
      wrapper.className = "toggle-control";

      const copy = document.createElement("div");
      copy.className = "toggle-copy";

      const labelEl = document.createElement("div");
      labelEl.className = "toggle-label";
      labelEl.textContent = label;

      const hintEl = document.createElement("div");
      hintEl.className = "hint";
      hintEl.textContent = hint;

      const input = document.createElement("input");
      input.type = "checkbox";
      input.checked = checked;

      const pill = document.createElement("span");
      pill.className = "toggle-pill";

      input.addEventListener("input", () => updateStellarStatsDisplay());

      copy.appendChild(labelEl);
      copy.appendChild(hintEl);
      wrapper.appendChild(copy);
      wrapper.appendChild(input);
      wrapper.appendChild(pill);

      controlsEl.appendChild(wrapper);
      stellarToggles[key] = input;
    }

    addSectionTitle("Galaxy Scale");
    addSlider("galaxySeed", "Galaxy Seed", "Deterministic seed used for all procedural blocks.", 1, 999999, 1, CONFIG.galaxyScale.seed, n => n.toFixed(0), false);
    addSlider("totalTargetStars", "Total Target Stars", "Approximate number of stars represented by the procedural galaxy.", 10000, 1000000, 10000, CONFIG.galaxyScale.totalTargetStars, n => n.toLocaleString("en-GB", { maximumFractionDigits: 0 }), false);
    addSlider("galaxyRadius", "Galaxy Radius", "Visual radius of the generated galactic disc.", 6, 60, 1, CONFIG.galaxyScale.radius, n => n.toFixed(0), false);
    addSlider("discThickness", "Disc Thickness", "Vertical thickness of the procedural galaxy disc.", 0.2, 8, 0.1, CONFIG.galaxyScale.discThickness, n => n.toFixed(1), false);
    addSlider("spiralArmCount", "Spiral Arm Count", "Number of main procedural spiral arms.", 1, 6, 1, CONFIG.galaxyShape.armCount, n => n.toFixed(0), false);
    addSlider("spiralTightness", "Spiral Tightness", "Controls how tightly the spiral arms wind.", 0.5, 8, 0.1, CONFIG.galaxyShape.armTightness, n => n.toFixed(1), false);
    addSlider("bulgeMass", "Bulge Mass", "Mass contribution from the central stellar bulge.", 1000, 500000, 1000, CONFIG.galaxyShape.bulgeMass, n => n.toLocaleString("en-GB", { maximumFractionDigits: 0 }), false);
    addSlider("discMass", "Disc Mass", "Mass contribution from the galactic disc.", 1000, 2000000, 5000, CONFIG.galaxyShape.discMass, n => n.toLocaleString("en-GB", { maximumFractionDigits: 0 }), false);
    addSlider("haloMass", "Halo Mass", "Mass contribution from the outer halo.", 1000, 2000000, 5000, CONFIG.galaxyShape.haloMass, n => n.toLocaleString("en-GB", { maximumFractionDigits: 0 }), false);
    addSlider("blackHoleMass", "Black Hole Mass", "Central visual black hole mass for near-centre orbital field.", 0, 200000, 1000, CONFIG.blackHole.mass, n => n.toLocaleString("en-GB", { maximumFractionDigits: 0 }), false);
    addSlider("blockSize", "Block Size", "Controls how large each galaxy sector is before it subdivides.", 2, 20, 0.5, CONFIG.galaxyScale.blockSize, n => n.toFixed(1), false);
    addSlider("maxOctreeDepth", "Max Octree Depth", "Higher values allow smaller detailed blocks but use more memory.", 1, 6, 1, CONFIG.galaxyScale.maxOctreeDepth, n => n.toFixed(0), false);
    addSlider("activeBlockLimit", "Active Block Limit", "Maximum number of blocks allowed to run detailed simulation at once.", 1, 4, 1, CONFIG.galaxyScale.activeBlockLimit, n => n.toFixed(0), false);
    addSlider("starsPerRenderBatch", "Stars Per Render Batch", "Maximum procedural point stars rendered in galaxy view.", 500, 30000, 500, CONFIG.galaxyScale.starsPerRenderBatch, n => n.toLocaleString("en-GB", { maximumFractionDigits: 0 }), false);
    addSlider("lodDistanceGalaxy", "LOD Distance Galaxy", "Camera distance above which blocks render as galaxy summaries.", 8, 80, 1, CONFIG.galaxyScale.lodDistanceGalaxy, n => n.toFixed(0), false);
    addSlider("lodDistanceBlock", "LOD Distance Block", "Camera distance for selected block star-point detail.", 3, 40, 1, CONFIG.galaxyScale.lodDistanceBlock, n => n.toFixed(0), false);
    addToggle("showBlocks", "Show Cube Seeds", "Displays selectable dust cube seed outlines. Octree parent boxes stay hidden.", CONFIG.galaxyScale.showBlocks);
    addToggle("showSpiralArms", "Show Spiral Arms", "Emphasises density along procedural spiral arms.", true);
    addToggle("showGalacticPlane", "Show Galactic Plane", "Displays the global galaxy disc plane.", true);
    addToggle("showDensityClouds", "Show Density Clouds", "Renders far blocks as soft gas/star density clouds.", CONFIG.galaxyScale.showDensityClouds);
    addToggle("showStarPoints", "Show Star Points", "Renders compact GPU point samples for large star populations.", true);
    addToggle("showActiveBlockDetail", "Show Active Block Detail", "Shows pendulum physics and local bodies for the active block.", true);
    addToggle("autoCollapseDistantBlocks", "Auto Collapse Distant Blocks", "Sleeps active details when returning to far galaxy view.", true);
    addToggle("enablePhysicalGrowth", "Physical Growth", "Allows gas and dust inside blocks to become stars without changing total mass.", CONFIG.growth.enabled);
    addToggle("externalGasInflowEnabled", "External Gas Inflow", "Adds tracked low-rate external gas to outer spiral arm blocks.", CONFIG.growth.externalInflowEnabled);
    addToggle("pendulumMatterCreationEnabled", "Pendulum Creates Matter", "Counts m2 path-crossing blobs as physical mass entering the active block.", CONFIG.growth.pendulumMatterCreationEnabled);

    const galaxyStatsEl = document.createElement("div");
    galaxyStatsEl.id = "galaxyStats";
    controlsEl.appendChild(galaxyStatsEl);

    addSectionTitle("Local Simulation");
    addSlider("theta1Deg", "θ1 tilt", "Upper rod angle from down", 0, 179, 1, DEFAULTS.theta1Deg, n => `${n.toFixed(0)}°`);
    addSlider("phi1Deg", "φ1 azimuth", "Upper rod compass direction", -180, 180, 1, DEFAULTS.phi1Deg, n => `${n.toFixed(0)}°`);
    addSlider("spin1", "spin1", "Upper sideways start speed", -8, 8, 0.1, DEFAULTS.spin1, n => n.toFixed(1));

    addSlider("theta2Deg", "θ2 tilt", "Lower rod angle from down", 0, 179, 1, DEFAULTS.theta2Deg, n => `${n.toFixed(0)}°`);
    addSlider("phi2Deg", "φ2 azimuth", "Lower rod compass direction", -180, 180, 1, DEFAULTS.phi2Deg, n => `${n.toFixed(0)}°`);
    addSlider("spin2", "spin2", "Lower sideways start speed", -8, 8, 0.1, DEFAULTS.spin2, n => n.toFixed(1));

    addSlider("L1", "L1 length", "Length of upper rod", 0.2, 3.0, 0.05, DEFAULTS.L1, n => n.toFixed(2));
    addSlider("L2", "L2 length", "Length of lower rod", 0.2, 3.0, 0.05, DEFAULTS.L2, n => n.toFixed(2));

    addSlider("m1", "m1 mass", "Mass of upper bob", 0.1, 5.0, 0.1, DEFAULTS.m1, n => n.toFixed(1));
    addSlider("m2", "m2 mass", "Mass of lower bob", 0.1, 5.0, 0.1, DEFAULTS.m2, n => n.toFixed(1));

    addSlider("g", "gravity", "Downward acceleration", 0.0, 30.0, 0.1, DEFAULTS.g, n => n.toFixed(1));
    addSlider("dt", "dt step", "Smaller is stabler/slower", 0.001, 0.006, 0.001, DEFAULTS.dt, n => n.toFixed(3));

    addSlider("maxSpeed", "max speed", "Velocity clamp, higher keeps motion alive", 5.0, 100.0, 0.5, DEFAULTS.maxSpeed, n => n.toFixed(1));
    addSlider("maxRecordedSteps", "max trail", "Recorded trail points, max 5000", 10, 5000, 10, DEFAULTS.maxRecordedSteps, n => n.toFixed(0));

    addSectionTitle("Stellar outcomes");
    addToggle("enableStellarOutcomes", "Enable stellar outcomes", "Star collision and remnant rules.", true);
    addToggle("enableStellarAging", "Aging", "Fuel burn can turn old stars into remnants.", false);
    addToggle("enableSupernovae", "Supernovae", "Violent stars can flash and eject gas shells.", true);
    addToggle("enableCompactObjects", "Compact objects", "White dwarfs, neutron stars, and black holes.", true);
    addToggle("enablePulsarBeams", "Pulsar beams", "Neutron stars render rotating beams.", true);
    addToggle("enableTidalDisruption", "Tidal disruption", "Compact objects strip gas before capture.", true);

    addSlider("whiteDwarfChandrasekharLimit", "White dwarf limit", "Type Ia trigger mass.", 60, 260, 5, CONFIG.stellarOutcomes.whiteDwarfChandrasekharLimit, n => n.toFixed(0), false);
    addSlider("neutronStarMassThreshold", "Neutron star limit", "Minimum remnant mass for neutron stars.", 80, 420, 5, CONFIG.stellarOutcomes.neutronStarMassThreshold, n => n.toFixed(0), false);
    addSlider("blackHoleFormationMass", "Black hole mass", "Minimum mass for stellar black holes.", 120, 900, 10, CONFIG.stellarOutcomes.blackHoleFormationMass, n => n.toFixed(0), false);
    addSlider("collisionSupernovaEnergyThreshold", "Supernova energy", "Collision energy required for prompt collapse.", 1000, 80000, 1000, CONFIG.stellarOutcomes.collisionSupernovaEnergyThreshold, n => n.toFixed(0), false);
    addSlider("supernovaInstabilityThreshold", "Instability threshold", "Unstable stars explode beyond this value.", 0.5, 6.0, 0.1, CONFIG.stellarOutcomes.supernovaInstabilityThreshold, n => n.toFixed(1), false);
    addSlider("starFuelBurnRate", "Fuel burn rate", "Aging speed for stars.", 0.0, 0.01, 0.0005, CONFIG.stellarOutcomes.starFuelBurnRate, n => n.toFixed(4), false);
    addSlider("supernovaShockSpeed", "Shock strength", "Impulse speed from supernova shells.", 1.0, 20.0, 0.5, CONFIG.stellarOutcomes.supernovaShockSpeed, n => n.toFixed(1), false);
    addSlider("supernovaGasFraction", "Supernova gas", "Fraction ejected into gas and fragments.", 0.05, 1.0, 0.05, CONFIG.stellarOutcomes.supernovaGasFraction, n => n.toFixed(2), false);
    addSlider("compactObjectGravityStrength", "Compact gravity", "Gravity multiplier for compact remnants.", 0.0, 4.0, 0.1, CONFIG.stellarOutcomes.compactObjectGravityStrength, n => n.toFixed(1), false);
    addSlider("blackHoleEffectStrength", "Black hole effect", "Keeps black holes mostly visual unless increased.", 0.0, 2.0, 0.01, CONFIG.galaxy.blackHoleEffectStrength, n => n.toFixed(2), false);

    addSectionTitle("Galaxy structure");
    addToggle("showStarGalacticOrbits", "Show star galactic orbits", "Displays faint star paths around the galaxy centre.", true);
    addToggle("showPlanetOrbits", "Show planet trails", "Displays short actual-motion trails for planets.", true);
    addToggle("showMoonOrbits", "Show moon trails", "Displays short actual-motion trails for moons.", true);
    addToggle("showFragmentTrails", "Show fragment trails", "Short fragment trails make debris motion readable.", true);
    addToggle("showGasCloud", "Show gas cloud", "Shows faint gas forming the rotating galactic cloud.", true);
    addToggle("showParentLinks", "Parent links", "Shows which star or planet owns each orbiting body.", false);
    addToggle("enableGalacticPlaneSettling", "Galactic plane settling", "Pulls matter gently into a flat galaxy disc.", true);
    addToggle("enableGalacticRotation", "Galactic rotation", "Makes gas, stars and systems rotate around the galaxy centre.", true);

    addSlider("fragmentTrailLength", "Fragment trail length", "Shorter trails reduce visual chaos.", 0, 40, 1, CONFIG.trails.fragments, n => n.toFixed(0), false);
    addSlider("planetTrailLength", "Planet trail length", "Longer planet paths reveal stable systems.", 0, 160, 5, CONFIG.trails.planets, n => n.toFixed(0), false);
    addSlider("starTrailLength", "Star trail length", "Faint galactic paths for stars.", 0, 260, 5, CONFIG.trails.stars, n => n.toFixed(0), false);
    addSlider("gasCloudRotation", "Gas cloud rotation", "Controls how strongly explosion gas joins the rotating galaxy cloud.", 0, 0.25, 0.01, CONFIG.gas.galacticSpinCoupling, n => n.toFixed(2), false);

    addSectionTitle("Star System Travel");
    addToggle("enableStableSystemTravel", "Enable Stable System Travel", "Stable star systems orbit the galactic centre as whole containers.", CONFIG.systemTravel.enabled);
    addToggle("autoPromoteStableSystems", "Auto Promote Stable Systems", "Automatically starts galactic travel when a system becomes stable.", CONFIG.systemTravel.autoStartWhenStable);
    addToggle("showGalacticSystemOrbits", "Show Galactic System Orbits", "Draws faint orbit paths around the galactic centre.", CONFIG.systemTravel.showGalacticOrbitPath);
    addToggle("collapseDistantSystems", "Collapse Distant Systems", "Shows distant solar systems as single star markers.", CONFIG.systemTravel.collapseDetailWhenZoomedOut);
    addToggle("preserveLocalSystemOrbits", "Preserve Local Orbits", "Keeps planets and moons orbiting inside the moving system.", CONFIG.systemTravel.preserveLocalPhysics);
    addSlider("systemOrbitSpeed", "System Orbit Speed", "Scales how fast stable systems move around the galaxy.", 0, 5, 0.05, CONFIG.systemTravel.systemOrbitSpeedScale, n => n.toFixed(2), false);
    addSlider("systemStabilityAge", "System Stability Age", "Minimum time before a star system can become stable.", 1, 120, 1, CONFIG.systemStability.minStableAge, n => n.toFixed(0), false);
    addSlider("systemPlaneDamping", "Galactic Plane Damping", "Gently pulls travelling systems toward the galactic plane.", 0, 0.5, 0.01, CONFIG.systemTravel.systemPlaneDamping, n => n.toFixed(2), false);

    addSectionTitle("Rings and moons");
    addToggle("showPlanetRings", "Show planet rings", "Displays debris rings around high-spin planets.", true);
    addToggle("enableRingFormation", "Ring formation", "Allows captured debris inside the Roche zone to become rings.", true);
    addToggle("enableMoonFormation", "Moon formation", "Allows captured fragments outside the Roche zone to become moons.", true);
    addSlider("ringParticleCount", "Ring particle count", "Caps debris density used by Saturn-like rings.", 40, 400, 10, CONFIG.ring.maxRingParticles, n => n.toFixed(0), false);
    addSlider("ringOpacity", "Ring opacity", "Controls how visible Saturn-like rings are.", 0.05, 0.85, 0.05, CONFIG.ring.ringOpacity, n => n.toFixed(2), false);
    addSlider("ringWidth", "Ring width", "Outer ring diameter relative to the planet.", 1.8, 4.0, 0.1, CONFIG.ring.outerRadiusMultiplier, n => n.toFixed(1), false);
    addSlider("moonCaptureSpeed", "Moon capture speed", "Fragments must be slow enough to become moons.", 0.4, 7.0, 0.1, CONFIG.moons.moonCaptureSpeedThreshold, n => n.toFixed(1), false);

    addSectionTitle("Presets");
    const presetRowA = document.createElement("div");
    presetRowA.className = "button-row two";
    const presetRowB = document.createElement("div");
    presetRowB.className = "button-row two";
    const presetRowC = document.createElement("div");
    presetRowC.className = "button-row two";

    for (const [row, key, label] of [
      [presetRowA, "galaxyDisc", "Galaxy Disc"],
      [presetRowA, "saturnGarden", "Saturn Garden"],
      [presetRowB, "solarSystems", "Solar Systems"],
      [presetRowB, "chaoticBirth", "Chaotic Birth"],
      [presetRowC, "performance", "Performance"],
    ]) {
      const button = document.createElement("button");
      button.type = "button";
      button.textContent = label;
      button.addEventListener("click", () => applySimulationPreset(key));
      row.appendChild(button);
    }

    controlsEl.appendChild(presetRowA);
    controlsEl.appendChild(presetRowB);
    controlsEl.appendChild(presetRowC);

    const stellarStatsEl = document.createElement("div");
    stellarStatsEl.id = "stellarStats";
    controlsEl.appendChild(stellarStatsEl);

    function getParams() {
      return {
        theta1Deg: Number(sliders.theta1Deg.input.value),
        phi1Deg: Number(sliders.phi1Deg.input.value),
        spin1: Number(sliders.spin1.input.value),

        theta2Deg: Number(sliders.theta2Deg.input.value),
        phi2Deg: Number(sliders.phi2Deg.input.value),
        spin2: Number(sliders.spin2.input.value),

        L1: Number(sliders.L1.input.value),
        L2: Number(sliders.L2.input.value),
        m1: Number(sliders.m1.input.value),
        m2: Number(sliders.m2.input.value),
        g: Number(sliders.g.input.value),
        dt: Number(sliders.dt.input.value),

        maxSpeed: Number(sliders.maxSpeed.input.value),
        maxRecordedSteps: Math.round(Number(sliders.maxRecordedSteps.input.value)),
      };
    }

    function getStellarConfig() {
      const base = CONFIG.stellarOutcomes;

      return {
        ...base,
        enableStellarOutcomes: stellarToggles.enableStellarOutcomes.checked,
        enableStellarAging: stellarToggles.enableStellarAging.checked,
        enableSupernovae: stellarToggles.enableSupernovae.checked,
        enableCompactObjects: stellarToggles.enableCompactObjects.checked,
        enablePulsarBeams: stellarToggles.enablePulsarBeams.checked,
        enableTidalDisruption: stellarToggles.enableTidalDisruption.checked,
        whiteDwarfChandrasekharLimit: Number(sliders.whiteDwarfChandrasekharLimit.input.value),
        neutronStarMassThreshold: Number(sliders.neutronStarMassThreshold.input.value),
        blackHoleFormationMass: Number(sliders.blackHoleFormationMass.input.value),
        collisionSupernovaEnergyThreshold: Number(sliders.collisionSupernovaEnergyThreshold.input.value),
        supernovaInstabilityThreshold: Number(sliders.supernovaInstabilityThreshold.input.value),
        starFuelBurnRate: Number(sliders.starFuelBurnRate.input.value),
        supernovaShockSpeed: Number(sliders.supernovaShockSpeed.input.value),
        supernovaGasFraction: Number(sliders.supernovaGasFraction.input.value),
        compactObjectGravityStrength: Number(sliders.compactObjectGravityStrength.input.value),
        showStarGalacticOrbits: stellarToggles.showStarGalacticOrbits.checked,
        showPlanetOrbits: stellarToggles.showPlanetOrbits.checked,
        showMoonOrbits: stellarToggles.showMoonOrbits.checked,
        showFragmentTrails: stellarToggles.showFragmentTrails.checked,
        showGasCloud: stellarToggles.showGasCloud.checked,
        showParentLinks: stellarToggles.showParentLinks.checked,
        enableGalacticPlaneSettling: stellarToggles.enableGalacticPlaneSettling.checked,
        enableGalacticRotation: stellarToggles.enableGalacticRotation.checked,
        showPlanetRings: stellarToggles.showPlanetRings.checked,
        enableRingFormation: stellarToggles.enableRingFormation.checked,
        enableMoonFormation: stellarToggles.enableMoonFormation.checked,
        trails: {
          ...CONFIG.trails,
          fragments: Number(sliders.fragmentTrailLength.input.value),
          planets: Number(sliders.planetTrailLength.input.value),
          stars: Number(sliders.starTrailLength.input.value),
        },
        ring: {
          ...CONFIG.ring,
          enabled: stellarToggles.enableRingFormation.checked,
          maxRingParticles: Number(sliders.ringParticleCount.input.value),
          ringOpacity: Number(sliders.ringOpacity.input.value),
          outerRadiusMultiplier: Number(sliders.ringWidth.input.value),
        },
        moons: {
          ...CONFIG.moons,
          enabled: stellarToggles.enableMoonFormation.checked,
          moonCaptureSpeedThreshold: Number(sliders.moonCaptureSpeed.input.value),
        },
        galaxyPlane: {
          ...CONFIG.galaxyPlane,
          enabled: stellarToggles.enableGalacticPlaneSettling.checked,
        },
        galaxy: {
          ...CONFIG.galaxy,
          rotationEnabled: stellarToggles.enableGalacticRotation.checked,
          blackHoleEffectStrength: Number(sliders.blackHoleEffectStrength.input.value),
        },
        gas: {
          ...CONFIG.gas,
          galacticSpinCoupling: Number(sliders.gasCloudRotation.input.value),
        },
        systemStability: {
          ...CONFIG.systemStability,
          minStableAge: Number(sliders.systemStabilityAge.input.value),
        },
        systemTravel: {
          ...CONFIG.systemTravel,
          enabled: stellarToggles.enableStableSystemTravel.checked,
          autoStartWhenStable: stellarToggles.autoPromoteStableSystems.checked,
          showGalacticOrbitPath: stellarToggles.showGalacticSystemOrbits.checked,
          collapseDetailWhenZoomedOut: stellarToggles.collapseDistantSystems.checked,
          preserveLocalPhysics: stellarToggles.preserveLocalSystemOrbits.checked,
          systemOrbitSpeedScale: Number(sliders.systemOrbitSpeed.input.value),
          systemPlaneDamping: Number(sliders.systemPlaneDamping.input.value),
        },
        lod: CONFIG.lod,
        parentAssignment: CONFIG.parentAssignment,
        planetFormation: CONFIG.planetFormation,
      };
    }

    function getGalaxyConfig() {
      return {
        G: CONFIG.G,
        seed: Math.round(Number(sliders.galaxySeed.input.value)),
        totalTargetStars: Math.round(Number(sliders.totalTargetStars.input.value)),
        radius: Number(sliders.galaxyRadius.input.value),
        discThickness: Number(sliders.discThickness.input.value),
        blockSize: Number(sliders.blockSize.input.value),
        maxOctreeDepth: Math.round(Number(sliders.maxOctreeDepth.input.value)),
        activeBlockLimit: Math.round(Number(sliders.activeBlockLimit.input.value)),
        starsPerRenderBatch: Math.round(Number(sliders.starsPerRenderBatch.input.value)),
        lodDistanceGalaxy: Number(sliders.lodDistanceGalaxy.input.value),
        lodDistanceBlock: Number(sliders.lodDistanceBlock.input.value),
        autoCollapseDistantBlocks: stellarToggles.autoCollapseDistantBlocks.checked,
        showBlocks: stellarToggles.showBlocks.checked,
        showSpiralArms: stellarToggles.showSpiralArms.checked,
        showGalacticPlane: stellarToggles.showGalacticPlane.checked,
        showDensityClouds: stellarToggles.showDensityClouds.checked,
        showStarPoints: stellarToggles.showStarPoints.checked,
        showActiveBlockDetail: stellarToggles.showActiveBlockDetail.checked,
        growth: {
          ...CONFIG.growth,
          enabled: stellarToggles.enablePhysicalGrowth.checked,
          externalInflowEnabled: stellarToggles.externalGasInflowEnabled.checked,
          pendulumMatterCreationEnabled: stellarToggles.pendulumMatterCreationEnabled.checked,
        },
        blocks: {
          ...CONFIG.blocks,
          maxOctreeDepth: Math.round(Number(sliders.maxOctreeDepth.input.value)),
          autoCollapseDistant: stellarToggles.autoCollapseDistantBlocks.checked,
        },
        lod: {
          ...CONFIG.lod,
          galaxyDistance: Number(sliders.lodDistanceGalaxy.input.value),
          clusterDistance: Math.max(Number(sliders.lodDistanceBlock.input.value), Number(sliders.lodDistanceGalaxy.input.value) * 0.62),
          blockDistance: Number(sliders.lodDistanceBlock.input.value),
          activeDistance: Math.max(1.5, Number(sliders.lodDistanceBlock.input.value) * 0.42),
        },
        blackHoleMass: Number(sliders.blackHoleMass.input.value),
        galaxyShape: {
          ...CONFIG.galaxyShape,
          armCount: Math.round(Number(sliders.spiralArmCount.input.value)),
          armTightness: Number(sliders.spiralTightness.input.value),
          discRadius: Number(sliders.galaxyRadius.input.value),
          discThickness: Number(sliders.discThickness.input.value),
          bulgeMass: Number(sliders.bulgeMass.input.value),
          discMass: Number(sliders.discMass.input.value),
          haloMass: Number(sliders.haloMass.input.value),
        },
      };
    }

    function getGalaxyConfigKey(config) {
      return [
        config.seed,
        config.totalTargetStars,
        config.radius.toFixed(2),
        config.discThickness.toFixed(2),
        config.blockSize.toFixed(2),
        config.maxOctreeDepth,
        config.starsPerRenderBatch,
        config.galaxyShape.armCount,
        config.galaxyShape.armTightness.toFixed(2),
      ].join("|");
    }

    function setSliderValue(key, value) {
      if (!sliders[key]) {
        return;
      }

      sliders[key].input.value = value;
      sliders[key].formatValue();
    }

    function setToggleValue(key, value) {
      if (stellarToggles[key]) {
        stellarToggles[key].checked = value;
      }
    }

    function accountFetch(path, options = {}) {
      const headers = {
        Accept: "application/json",
        ...(options.headers || {}),
      };

      if (options.body && !headers["Content-Type"]) {
        headers["Content-Type"] = "application/json";
      }

      return fetch(ACCOUNT_API_BASE + "/" + path, {
        credentials: "include",
        ...options,
        headers,
      }).then(async response => {
        const data = await response.json().catch(() => null);

        if (!response.ok || !data || data.ok === false) {
          throw new Error(data?.error || "Account request failed (" + response.status + ")");
        }

        return data;
      });
    }

    function loadGoogleIdentityScript() {
      if (window.google?.accounts?.id) {
        return Promise.resolve();
      }

      if (googleIdentityScriptPromise) {
        return googleIdentityScriptPromise;
      }

      googleIdentityScriptPromise = new Promise((resolve, reject) => {
        const scriptEl = document.createElement("script");
        scriptEl.src = "https://accounts.google.com/gsi/client";
        scriptEl.async = true;
        scriptEl.defer = true;
        scriptEl.onload = () => resolve();
        scriptEl.onerror = () => reject(new Error("Could not load Google sign-in."));
        document.head.appendChild(scriptEl);
      });

      return googleIdentityScriptPromise;
    }

    function formatUniverseAllocation(allocation) {
      if (!allocation) {
        return "";
      }

      const origin = Array.isArray(allocation.origin) ? allocation.origin : [allocation.originX, allocation.originY];
      const size = allocation.pieceSize ?? allocation.size;
      const parts = [];

      if (allocation.id || allocation.pieceId) {
        parts.push("sector " + (allocation.id || allocation.pieceId));
      }

      if (Number.isFinite(Number(size))) {
        parts.push(Number(size).toFixed(0) + " x " + Number(size).toFixed(0));
      }

      if (origin.every(value => Number.isFinite(Number(value)))) {
        parts.push("origin " + Number(origin[0]).toFixed(0) + ", " + Number(origin[1]).toFixed(0));
      }

      return parts.join(" | ");
    }

    function updateAccountUi() {
      if (!accountConfigured) {
        accountStatusEl.textContent = "Google save is not configured.";
        googleSignInEl.hidden = true;
        saveAccountButton.disabled = true;
        loadAccountButton.disabled = true;
        logoutButton.hidden = true;
        accountSectorEl.hidden = true;
        return;
      }

      if (accountUser) {
        const displayName = accountUser.name || accountUser.email || "Google account";
        accountStatusEl.textContent = "Signed in as " + displayName;
        googleSignInEl.hidden = true;
        saveAccountButton.disabled = false;
        loadAccountButton.disabled = !accountHasSettings;
        logoutButton.hidden = false;

        const sectorText = formatUniverseAllocation(accountUniverseAllocation);
        accountSectorEl.hidden = !sectorText;
        accountSectorEl.textContent = sectorText;
      } else {
        accountStatusEl.textContent = "Sign in with Google to save and load this universe.";
        googleSignInEl.hidden = false;
        saveAccountButton.disabled = true;
        loadAccountButton.disabled = true;
        logoutButton.hidden = true;
        accountSectorEl.hidden = true;
        accountSectorEl.textContent = "";
      }
    }

    function applyAccountSession(data) {
      accountUser = data.user || null;
      accountHasSettings = Boolean(data.hasSettings);
      accountUniverseAllocation = data.universeAllocation || null;
      updateAccountUi();
    }

    function collectAccountSettings() {
      const savedSliders = {};
      const savedToggles = {};

      for (const [key, slider] of Object.entries(sliders)) {
        if (!GALAXY_STATE_SLIDER_KEYS.has(key)) {
          savedSliders[key] = Number(slider.input.value);
        }
      }

      for (const [key, toggle] of Object.entries(stellarToggles)) {
        savedToggles[key] = Boolean(toggle.checked);
      }

      return {
        version: 1,
        kind: "procedural-block-galaxy-settings",
        sliders: savedSliders,
        toggles: savedToggles,
        camera: {
          azimuth: cameraAzim,
          elevation: cameraElev,
          distance: cameraDistance,
          target: [cameraTarget.x, cameraTarget.y, cameraTarget.z],
        },
        savedAt: new Date().toISOString(),
      };
    }

    function applyAccountSettings(settings) {
      if (!settings || typeof settings !== "object") {
        return;
      }

      for (const [key, value] of Object.entries(settings.toggles || {})) {
        setToggleValue(key, Boolean(value));
      }

      for (const [key, value] of Object.entries(settings.sliders || {})) {
        setSliderValue(key, value);
      }

      const cameraState = settings.camera;
      if (cameraState && typeof cameraState === "object") {
        if (Number.isFinite(Number(cameraState.azimuth))) {
          cameraAzim = Number(cameraState.azimuth);
        }

        if (Number.isFinite(Number(cameraState.elevation))) {
          cameraElev = Math.max(-1.35, Math.min(1.35, Number(cameraState.elevation)));
        }

        if (Number.isFinite(Number(cameraState.distance))) {
          setCameraDistance(Number(cameraState.distance));
        }

        if (Array.isArray(cameraState.target) && cameraState.target.length >= 3) {
          const [x, y, z] = cameraState.target.map(Number);
          if ([x, y, z].every(Number.isFinite)) {
            cameraTarget.set(x, y, z);
          }
        }

        updateCamera();
      }

      updateStellarStatsDisplay();
    }

    function buildUniverseSummary(state) {
      const evolvedBlocks = state?.evolvedBlocks && typeof state.evolvedBlocks === "object"
        ? Object.keys(state.evolvedBlocks).length
        : 0;
      const growthBlocks = state?.blockGrowthState && typeof state.blockGrowthState === "object"
        ? Object.keys(state.blockGrowthState).length
        : 0;

      return {
        kind: "procedural-block-galaxy",
        selectedBlockId: state?.selectedBlockId || null,
        activeBlockId: state?.activeBlockId || null,
        expandedBlocks: Array.isArray(state?.expandedBlockIds) ? state.expandedBlockIds.length : 0,
        evolvedBlocks,
        growthBlocks,
        physicalGrowthMass: state?.growthStats?.physicalGrowthMass || 0,
        totalTargetStars: state?.config?.totalTargetStars || null,
        savedAt: new Date().toISOString(),
      };
    }

    async function saveAccountUniverse() {
      if (!accountUser || !galaxyManager) {
        return;
      }

      try {
        saveAccountButton.disabled = true;
        statusEl.textContent = "Saving universe...";
        const state = galaxyManager.saveState();
        const payload = {
          settings: collectAccountSettings(),
          universe: {
            version: 1,
            kind: "procedural-block-galaxy",
            state,
          },
          summary: buildUniverseSummary(state),
        };

        const data = await accountFetch("settings.php", {
          method: "POST",
          body: JSON.stringify(payload),
        });

        accountHasSettings = true;
        accountUniverseAllocation = data.universeAllocation || accountUniverseAllocation;
        updateAccountUi();
        statusEl.textContent = "Universe saved" + (data.savedAt ? " | " + data.savedAt : "");
      } catch (error) {
        console.error(error);
        statusEl.textContent = error.message || "Could not save universe";
        updateAccountUi();
      }
    }

    async function loadAccountUniverse() {
      if (!accountUser || !galaxyManager) {
        return;
      }

      try {
        loadAccountButton.disabled = true;
        statusEl.textContent = "Loading universe...";
        const data = await accountFetch("settings.php");
        const universeState = data.universe?.state || data.universe;

        if (!universeState) {
          statusEl.textContent = "No saved universe state";
          accountHasSettings = false;
          updateAccountUi();
          return;
        }

        running = false;
        galaxyManager.loadState(universeState);
        applyAccountSettings(data.settings);
        accountHasSettings = true;
        accountUniverseAllocation = data.universeAllocation || accountUniverseAllocation;
        updateAccountUi();
        statusEl.textContent = "Universe loaded" + (data.savedAt ? " | " + data.savedAt : "");
      } catch (error) {
        console.error(error);
        statusEl.textContent = error.message || "Could not load universe";
        updateAccountUi();
      }
    }

    async function signOutAccount() {
      try {
        await accountFetch("logout.php", { method: "POST", body: JSON.stringify({}) });
      } catch (error) {
        console.error(error);
      }

      accountUser = null;
      accountHasSettings = false;
      accountUniverseAllocation = null;
      if (window.google?.accounts?.id) {
        window.google.accounts.id.disableAutoSelect();
      }
      updateAccountUi();
      statusEl.textContent = "Signed out";
    }

    async function initialiseAccount() {
      if (IS_LOCAL_DEV_HOST) {
        accountConfigured = false;
        updateAccountUi();
        return;
      }

      try {
        const config = await accountFetch("auth-config.php");
        accountConfigured = Boolean(config.configured && config.clientId);

        if (!accountConfigured) {
          updateAccountUi();
          return;
        }

        await loadGoogleIdentityScript();
        window.google.accounts.id.initialize({
          client_id: config.clientId,
          callback: async response => {
            try {
              accountStatusEl.textContent = "Signing in...";
              const session = await accountFetch("session.php", {
                method: "POST",
                body: JSON.stringify({ credential: response.credential }),
              });
              applyAccountSession(session);
              statusEl.textContent = "Signed in";
            } catch (error) {
              console.error(error);
              accountStatusEl.textContent = error.message || "Google sign-in failed.";
              updateAccountUi();
            }
          },
        });

        window.google.accounts.id.renderButton(googleSignInEl, {
          theme: "outline",
          size: "large",
          width: Math.min(330, Math.max(220, googleSignInEl.clientWidth || 300)),
        });

        const session = await accountFetch("session.php");
        applyAccountSession(session.signedIn ? session : { ...session, user: null });
      } catch (error) {
        console.error(error);
        accountConfigured = false;
        accountStatusEl.textContent = "Google save is unavailable.";
        updateAccountUi();
      }
    }


    function applySimulationPreset(key) {
      if (key === "galaxyDisc") {
        setToggleValue("enableGalacticPlaneSettling", true);
        setToggleValue("enableGalacticRotation", true);
        setToggleValue("showGasCloud", true);
        setSliderValue("fragmentTrailLength", 5);
        setSliderValue("planetTrailLength", 70);
        setSliderValue("starTrailLength", 180);
        setSliderValue("gasCloudRotation", 0.13);
        setSliderValue("supernovaGasFraction", 0.38);
      } else if (key === "saturnGarden") {
        setToggleValue("enableRingFormation", true);
        setToggleValue("showPlanetRings", true);
        setToggleValue("enableMoonFormation", true);
        setSliderValue("ringParticleCount", 320);
        setSliderValue("ringOpacity", 0.5);
        setSliderValue("ringWidth", 3.7);
        setSliderValue("moonCaptureSpeed", 4.2);
        setSliderValue("fragmentTrailLength", 10);
      } else if (key === "solarSystems") {
        setToggleValue("showPlanetOrbits", true);
        setToggleValue("showMoonOrbits", true);
        setToggleValue("showParentLinks", false);
        setToggleValue("enableStableSystemTravel", true);
        setToggleValue("autoPromoteStableSystems", true);
        setToggleValue("showGalacticSystemOrbits", true);
        setToggleValue("collapseDistantSystems", true);
        setToggleValue("preserveLocalSystemOrbits", true);
        setSliderValue("fragmentTrailLength", 4);
        setSliderValue("planetTrailLength", 110);
        setSliderValue("starTrailLength", 140);
        setSliderValue("compactObjectGravityStrength", 0.4);
        setSliderValue("systemOrbitSpeed", 1.25);
        setSliderValue("systemStabilityAge", 8);
      } else if (key === "chaoticBirth") {
        setToggleValue("showFragmentTrails", true);
        setSliderValue("fragmentTrailLength", 24);
        setSliderValue("supernovaGasFraction", 0.7);
        setSliderValue("collisionSupernovaEnergyThreshold", 9000);
        setSliderValue("compactObjectGravityStrength", 0.9);
        setSliderValue("gasCloudRotation", 0.07);
      } else if (key === "performance") {
        setToggleValue("showFragmentTrails", false);
        setToggleValue("showParentLinks", false);
        setToggleValue("showGasCloud", true);
        setSliderValue("fragmentTrailLength", 0);
        setSliderValue("planetTrailLength", 35);
        setSliderValue("starTrailLength", 65);
        setSliderValue("ringParticleCount", 80);
        setSliderValue("ringOpacity", 0.28);
        setSliderValue("gasCloudRotation", 0.08);
      }

      updateStellarStatsDisplay();
      statusEl.textContent = `Preset: ${key}`;
    }

    function setDefaultSliders() {
      for (const [key, value] of Object.entries(DEFAULTS)) {
        sliders[key].input.value = value;
        sliders[key].formatValue();
      }
    }

    function dot(a, b) {
      return a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
    }

    function norm(a) {
      return Math.sqrt(dot(a, a));
    }

    function add(a, b) {
      return [a[0] + b[0], a[1] + b[1], a[2] + b[2]];
    }

    function sub(a, b) {
      return [a[0] - b[0], a[1] - b[1], a[2] - b[2]];
    }

    function scale(a, s) {
      return [a[0] * s, a[1] * s, a[2] * s];
    }

    function toVec3(a) {
      return new THREE.Vector3(a[0], a[1], a[2]);
    }

    function fromVec3(v) {
      return [v.x, v.y, v.z];
    }

    function randomUnitVector() {
      const z = Math.random() * 2 - 1;
      const t = Math.random() * Math.PI * 2;
      const r = Math.sqrt(Math.max(0, 1 - z * z));
      return new THREE.Vector3(r * Math.cos(t), r * Math.sin(t), z);
    }

    function finiteVector(v) {
      return Number.isFinite(v.x) && Number.isFinite(v.y) && Number.isFinite(v.z);
    }

    function clampVectorLength(v, maxLength) {
      const length = v.length();

      if (length > maxLength && length > 1e-9) {
        v.multiplyScalar(maxLength / length);
      }
    }

    function segmentDistanceSq(a0, a1, b0, b1) {
      const u = new THREE.Vector3().subVectors(a1, a0);
      const v = new THREE.Vector3().subVectors(b1, b0);
      const w = new THREE.Vector3().subVectors(a0, b0);
      const a = u.dot(u);
      const b = u.dot(v);
      const c = v.dot(v);
      const d = u.dot(w);
      const e = v.dot(w);
      const denominator = a * c - b * b;

      let sN;
      let sD = denominator;
      let tN;
      let tD = denominator;

      if (denominator < 1e-9) {
        sN = 0;
        sD = 1;
        tN = e;
        tD = c;
      } else {
        sN = b * e - c * d;
        tN = a * e - b * d;

        if (sN < 0) {
          sN = 0;
          tN = e;
          tD = c;
        } else if (sN > sD) {
          sN = sD;
          tN = e + b;
          tD = c;
        }
      }

      if (tN < 0) {
        tN = 0;

        if (-d < 0) {
          sN = 0;
        } else if (-d > a) {
          sN = sD;
        } else {
          sN = -d;
          sD = a;
        }
      } else if (tN > tD) {
        tN = tD;

        if (-d + b < 0) {
          sN = 0;
        } else if (-d + b > a) {
          sN = sD;
        } else {
          sN = -d + b;
          sD = a;
        }
      }

      const sc = Math.abs(sN) < 1e-9 ? 0 : sN / sD;
      const tc = Math.abs(tN) < 1e-9 ? 0 : tN / tD;
      const delta = w.add(u.multiplyScalar(sc)).sub(v.multiplyScalar(tc));

      return delta.lengthSq();
    }

    function directionFromAngles(thetaDeg, phiDeg) {
      const theta = THREE.MathUtils.degToRad(thetaDeg);
      const phi = THREE.MathUtils.degToRad(phiDeg);
      const s = Math.sin(theta);

      return [
        s * Math.cos(phi),
        s * Math.sin(phi),
        -Math.cos(theta),
      ];
    }

    function dDirectionDPhi(thetaDeg, phiDeg) {
      const theta = THREE.MathUtils.degToRad(thetaDeg);
      const phi = THREE.MathUtils.degToRad(phiDeg);
      const s = Math.sin(theta);

      return [
        -s * Math.sin(phi),
        s * Math.cos(phi),
        0.0,
      ];
    }

    function makeInitialState(params) {
      const u1 = directionFromAngles(params.theta1Deg, params.phi1Deg);
      const u2 = directionFromAngles(params.theta2Deg, params.phi2Deg);

      const du1 = dDirectionDPhi(params.theta1Deg, params.phi1Deg);
      const du2 = dDirectionDPhi(params.theta2Deg, params.phi2Deg);

      const newP1 = scale(u1, params.L1);
      const newP2 = add(newP1, scale(u2, params.L2));

      const newV1 = scale(du1, params.L1 * params.spin1);
      const newV2 = add(newV1, scale(du2, params.L2 * params.spin2));

      return {
        p1: newP1,
        p2: newP2,
        v1: newV1,
        v2: newV2,
      };
    }

    function clampVelocity(v, maxSpeed) {
      const speed = norm(v);

      if (speed > maxSpeed) {
        return scale(v, maxSpeed / speed);
      }

      return v;
    }

    function computeAccelerationsFast(pos1, pos2, vel1, vel2, params) {
      const L1 = params.L1;
      const L2 = params.L2;
      const m1 = params.m1;
      const m2 = params.m2;
      const g = params.g;

      const d = sub(pos2, pos1);
      const dv = sub(vel2, vel1);

      const p1DotP1 = dot(pos1, pos1);
      const dDotD = dot(d, d);
      const p1DotD = dot(pos1, d);

      const c1 = p1DotP1 - L1 * L1;
      const c2 = dDotD - L2 * L2;

      const c1Dot = 2.0 * dot(pos1, vel1);
      const c2Dot = 2.0 * dot(d, dv);

      const jdotv1 = 2.0 * dot(vel1, vel1);
      const jdotv2 = 2.0 * dot(dv, dv);

      const rhs1 = -jdotv1 - CONSTRAINT_KP * c1 - CONSTRAINT_KD * c1Dot;
      const rhs2 = -jdotv2 - CONSTRAINT_KP * c2 - CONSTRAINT_KD * c2Dot;

      const jmInvF1 = -2.0 * g * pos1[2];
      const jmInvF2 = 0.0;

      const b1 = rhs1 - jmInvF1;
      const b2 = rhs2 - jmInvF2;

      const invM1 = 1.0 / m1;
      const invM2 = 1.0 / m2;

      const a11 = 4.0 * p1DotP1 * invM1;
      const a12 = -4.0 * p1DotD * invM1;
      const a22 = 4.0 * dDotD * (invM1 + invM2);

      const det = a11 * a22 - a12 * a12;

      let lambda1 = 0.0;
      let lambda2 = 0.0;

      if (Math.abs(det) >= 1e-12) {
        lambda1 = (b1 * a22 - b2 * a12) / det;
        lambda2 = (a11 * b2 - a12 * b1) / det;
      }

      const constraintForce1 = sub(
        scale(pos1, 2.0 * lambda1),
        scale(d, 2.0 * lambda2)
      );

      const constraintForce2 = scale(d, 2.0 * lambda2);

      const acc1 = add([0.0, 0.0, -g], scale(constraintForce1, invM1));
      const acc2 = add([0.0, 0.0, -g], scale(constraintForce2, invM2));

      return {
        a1: acc1,
        a2: acc2,
      };
    }

    function projectConstraintsFast(pos1, pos2, vel1, vel2, params) {
      const L1 = params.L1;
      const L2 = params.L2;

      let n1 = norm(pos1);

      if (n1 > 1e-12) {
        pos1 = scale(pos1, L1 / n1);
      } else {
        pos1 = [0.0, 0.0, -L1];
      }

      let d = sub(pos2, pos1);
      let nd = norm(d);

      if (nd > 1e-12) {
        pos2 = add(pos1, scale(d, L2 / nd));
      } else {
        pos2 = add(pos1, [0.0, 0.0, -L2]);
      }

      const p1Norm2 = Math.max(dot(pos1, pos1), 1e-12);
      vel1 = sub(vel1, scale(pos1, dot(pos1, vel1) / p1Norm2));

      d = sub(pos2, pos1);

      let vrel = sub(vel2, vel1);
      const dNorm2 = Math.max(dot(d, d), 1e-12);

      vrel = sub(vrel, scale(d, dot(d, vrel) / dNorm2));
      vel2 = add(vel1, vrel);

      return {
        p1: pos1,
        p2: pos2,
        v1: vel1,
        v2: vel2,
      };
    }

    function physicsStep(params) {
      const dt = Math.min(params.dt, MAX_DT) / PHYSICS_SUBSTEPS_PER_FRAME;
      const maxSpeed = params.maxSpeed;

      const acc = computeAccelerationsFast(p1, p2, v1, v2, params);

      v1 = add(v1, scale(acc.a1, dt));
      v2 = add(v2, scale(acc.a2, dt));

      v1 = scale(v1, VELOCITY_DAMPING);
      v2 = scale(v2, VELOCITY_DAMPING);

      v1 = clampVelocity(v1, maxSpeed);
      v2 = clampVelocity(v2, maxSpeed);

      p1 = add(p1, scale(v1, dt));
      p2 = add(p2, scale(v2, dt));

      const projected = projectConstraintsFast(p1, p2, v1, v2, params);

      p1 = projected.p1;
      p2 = projected.p2;
      v1 = projected.v1;
      v2 = projected.v2;

      stepCount++;
    }

    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0x05070a);

    const camera = new THREE.PerspectiveCamera(
      50,
      window.innerWidth / window.innerHeight,
      0.01,
      1000
    );

    camera.up.set(0, 0, 1);

    const renderer = new THREE.WebGLRenderer({
      antialias: true,
    });

    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 1.5));
    renderer.domElement.style.position = "fixed";
    renderer.domElement.style.left = "0";
    renderer.domElement.style.top = "0";

    document.body.appendChild(renderer.domElement);

    function getRendererViewportSize() {
      const panelRect = panelEl?.getBoundingClientRect();
      const isMobileLayout = window.matchMedia("(max-width: 760px)").matches;
      const width = isMobileLayout
        ? window.innerWidth
        : Math.max(320, window.innerWidth - (panelRect?.width || 0));
      const height = isMobileLayout
        ? Math.max(260, window.innerHeight - (panelRect?.height || 0))
        : window.innerHeight;

      return {
        width: Math.round(width),
        height: Math.round(height),
      };
    }

    function applyRendererLayout() {
      const viewport = getRendererViewportSize();
      camera.aspect = viewport.width / viewport.height;
      camera.updateProjectionMatrix();
      renderer.setSize(viewport.width, viewport.height);
      renderer.setPixelRatio(Math.min(window.devicePixelRatio, 1.5));
    }

    applyRendererLayout();

    const ambientLight = new THREE.AmbientLight(0x8ea0b8, 0.55);
    scene.add(ambientLight);

    const keyLight = new THREE.DirectionalLight(0xffffff, 1.8);
    keyLight.position.set(3, -4, 5);
    scene.add(keyLight);

    const rimLight = new THREE.PointLight(0x88ccff, 1.4, 16);
    rimLight.position.set(-4, 3, 4);
    scene.add(rimLight);

    const referenceGroup = new THREE.Group();
    scene.add(referenceGroup);

    const rodGeometry = new THREE.BufferGeometry();
    const rodPositions = new Float32Array(9);

    rodGeometry.setAttribute(
      "position",
      new THREE.BufferAttribute(rodPositions, 3)
    );

    const rodMaterial = new THREE.LineBasicMaterial({
      color: 0xffffff,
      linewidth: 2,
    });

    const rodLine = new THREE.Line(rodGeometry, rodMaterial);
    scene.add(rodLine);

    const bobMaterial1 = new THREE.MeshBasicMaterial({
      color: 0xff6666,
    });

    const bobMaterial2 = new THREE.MeshBasicMaterial({
      color: 0xffff66,
    });

    const bob1Mesh = new THREE.Mesh(
      new THREE.SphereGeometry(0.045, 20, 12),
      bobMaterial1
    );

    const bob2Mesh = new THREE.Mesh(
      new THREE.SphereGeometry(0.06, 20, 12),
      bobMaterial2
    );

    scene.add(bob1Mesh);
    scene.add(bob2Mesh);

    let trailGeometry = new THREE.BufferGeometry();

    const trailMaterial = new THREE.LineBasicMaterial({
      color: 0x66ccff,
      transparent: true,
      opacity: 0.95,
    });

    const trailLine = new THREE.Line(trailGeometry, trailMaterial);
    scene.add(trailLine);
    let trailPositionBuffer = new Float32Array(0);
    let trailBufferCapacity = 0;

    const galaxyGroup = new THREE.Group();
    scene.add(galaxyGroup);

    const galaxyCloudGroup = new THREE.Group();
    galaxyGroup.add(galaxyCloudGroup);

    const galaxyBlockGroup = new THREE.Group();
    galaxyGroup.add(galaxyBlockGroup);

    const galaxyStarPointGroup = new THREE.Group();
    galaxyGroup.add(galaxyStarPointGroup);

    const focusStarPointGroup = new THREE.Group();
    galaxyGroup.add(focusStarPointGroup);

    const selectionGroup = new THREE.Group();
    galaxyGroup.add(selectionGroup);

    const startedSystemMarkerGroup = new THREE.Group();
    galaxyGroup.add(startedSystemMarkerGroup);

    const stellarGroup = new THREE.Group();
    scene.add(stellarGroup);

    const orbitGroup = new THREE.Group();
    scene.add(orbitGroup);

    const bodyTrailGroup = new THREE.Group();
    scene.add(bodyTrailGroup);

    const starSystemTravelGroup = new THREE.Group();
    scene.add(starSystemTravelGroup);

    const parentLinkGroup = new THREE.Group();
    scene.add(parentLinkGroup);

    const galacticPlaneGeometry = new THREE.CircleGeometry(1, 96);
    const galacticPlaneMaterial = new THREE.MeshBasicMaterial({
      color: 0x7aa7ff,
      transparent: true,
      opacity: 0.055,
      depthWrite: false,
      side: THREE.DoubleSide,
    });
    const galacticPlaneMesh = new THREE.Mesh(galacticPlaneGeometry, galacticPlaneMaterial);
    scene.add(galacticPlaneMesh);

    const galacticCenterMesh = new THREE.Mesh(
      new THREE.SphereGeometry(0.05, 32, 16),
      new THREE.MeshBasicMaterial({
        color: 0x000000,
        transparent: false,
      })
    );
    const galacticCenterGlow = new THREE.Mesh(
      new THREE.SphereGeometry(0.082, 32, 16),
      new THREE.MeshBasicMaterial({
        color: 0xffffff,
        transparent: true,
        opacity: 0.16,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
      })
    );
    galacticCenterMesh.add(galacticCenterGlow);
    scene.add(galacticCenterMesh);

    function seededRandom(seed) {
      let state = seed >>> 0;

      return () => {
        state += 0x6D2B79F5;
        let t = state;
        t = Math.imul(t ^ (t >>> 15), t | 1);
        t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
        return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
      };
    }

    function hashStringToInt(value) {
      let hash = 2166136261;

      for (let i = 0; i < value.length; i++) {
        hash ^= value.charCodeAt(i);
        hash = Math.imul(hash, 16777619);
      }

      return hash >>> 0;
    }

    function hashBlockSeed(galaxySeed, blockId, level, coordinates) {
      return hashStringToInt(`${galaxySeed}:${blockId}:${level}:${coordinates.join(",")}`);
    }

    function shortestAngleDistance(a, b) {
      let diff = Math.abs(a - b) % (Math.PI * 2);
      return diff > Math.PI ? Math.PI * 2 - diff : diff;
    }

    function getEnclosedMass(radius, config = getGalaxyConfig()) {
      const r = Math.max(0.05, radius);
      const shape = config.galaxyShape;
      const blackHole = config.blackHoleMass * Math.max(0, getStellarConfig().galaxy.blackHoleEffectStrength);
      const bulge = shape.bulgeMass * (r / (r + shape.bulgeScale));
      const disc = shape.discMass * (1 - Math.exp(-r / shape.discScale));
      const halo = shape.haloMass * (r / (r + shape.haloScale));

      return blackHole + bulge + disc + halo;
    }

    function getEnclosedGalacticMass(radius, config = getGalaxyConfig()) {
      return getEnclosedMass(radius, config);
    }

    function getOrbitalSpeed(radius, config = getGalaxyConfig()) {
      const r = Math.max(0.05, radius);
      return Math.sqrt(config.G * getEnclosedGalacticMass(r, config) / r);
    }

    function getGalacticOrbitalSpeed(radius, config = getGalaxyConfig()) {
      return getOrbitalSpeed(radius, config);
    }

    function getGalacticAngularVelocity(radius, config = getGalaxyConfig()) {
      return getOrbitalSpeed(radius, config) / Math.max(0.05, radius);
    }

    function disposeGalaxyObject(object) {
      object.traverse(child => {
        if (child.geometry) {
          child.geometry.dispose();
        }

        if (child.material) {
          if (child.material.map) {
            child.material.map.dispose();
          }

          child.material.dispose();
        }
      });
    }

    function clearGalaxyGroup(group) {
      while (group.children.length > 0) {
        const child = group.children.pop();
        disposeGalaxyObject(child);
      }
    }

    function createGlowDotTexture(color = SYSTEM_MARKER_LABEL_COLOR) {
      const canvas = document.createElement("canvas");
      const size = 96;
      const centre = size / 2;
      const ctx = canvas.getContext("2d");
      canvas.width = size;
      canvas.height = size;

      const gradient = ctx.createRadialGradient(centre, centre, 1, centre, centre, centre);
      gradient.addColorStop(0, "#ffffff");
      gradient.addColorStop(0.22, color);
      gradient.addColorStop(0.48, "rgba(255, 224, 86, 0.55)");
      gradient.addColorStop(1, "rgba(255, 224, 86, 0)");
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, size, size);

      const texture = new THREE.CanvasTexture(canvas);
      texture.colorSpace = THREE.SRGBColorSpace;
      texture.needsUpdate = true;
      return texture;
    }

    function createTextSprite(lines, color = SYSTEM_MARKER_LABEL_COLOR) {
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      const safeLines = lines.map(line => String(line || "").toUpperCase());
      const fontSize = 32;
      const smallFontSize = 20;
      const paddingX = 24;
      const paddingY = 16;

      ctx.font = `500 ${fontSize}px system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif`;
      const titleWidth = ctx.measureText(safeLines[0] || "").width;
      ctx.font = `400 ${smallFontSize}px system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif`;
      const subtitleWidth = ctx.measureText(safeLines[1] || "").width;
      canvas.width = Math.ceil(Math.max(180, titleWidth, subtitleWidth) + paddingX * 2);
      canvas.height = safeLines.length > 1 ? 86 : 58;

      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.shadowColor = "rgba(255, 220, 70, 0.95)";
      ctx.shadowBlur = 16;
      ctx.fillStyle = color;
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.font = `500 ${fontSize}px system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif`;
      ctx.fillText(safeLines[0] || "", canvas.width / 2, paddingY + fontSize * 0.62);

      if (safeLines[1]) {
        ctx.shadowBlur = 9;
        ctx.fillStyle = "#9fe9ff";
        ctx.font = `400 ${smallFontSize}px system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif`;
        ctx.fillText(safeLines[1], canvas.width / 2, canvas.height - paddingY - smallFontSize * 0.18);
      }

      const texture = new THREE.CanvasTexture(canvas);
      texture.colorSpace = THREE.SRGBColorSpace;
      texture.needsUpdate = true;

      const material = new THREE.SpriteMaterial({
        map: texture,
        transparent: true,
        depthTest: false,
        depthWrite: false,
      });
      const sprite = new THREE.Sprite(material);
      sprite.userData.textureAspect = canvas.width / Math.max(1, canvas.height);
      return sprite;
    }

    function spiralDensityAt(position, config) {
      const shape = config.galaxyShape;
      const radius = Math.max(0.05, Math.hypot(position.x, position.y));
      const angle = Math.atan2(position.y, position.x);
      let bestDistance = Math.PI;

      for (let arm = 0; arm < shape.armCount; arm++) {
        const armAngle = Math.log(radius / Math.max(0.1, shape.bulgeRadius)) * shape.armTightness + arm * Math.PI * 2 / shape.armCount;
        bestDistance = Math.min(bestDistance, shortestAngleDistance(angle, armAngle));
      }

      return Math.exp(-(bestDistance * bestDistance) / Math.max(0.001, shape.spiralArmWidth * shape.spiralArmWidth));
    }

    function generateBlockSummary(block, config) {
      const shape = config.galaxyShape;
      const radius = Math.hypot(block.center.x, block.center.y);
      const height = Math.abs(block.center.z);
      const discFalloff = Math.exp(-radius / Math.max(0.1, shape.discRadius * 0.42));
      const heightFalloff = Math.exp(-(height * height) / Math.max(0.05, shape.discThickness * shape.discThickness));
      const bulge = Math.exp(-(radius * radius) / Math.max(0.05, shape.bulgeRadius * shape.bulgeRadius));
      const halo = Math.max(0.02, 1 - radius / Math.max(1, shape.haloRadius)) * 0.16;
      const spiral = config.showSpiralArms ? spiralDensityAt(block.center, config) : 0.35;
      const random = seededRandom(block.seed);
      const noise = 0.68 + random() * 0.64;
      const density = Math.max(0.002, (discFalloff * heightFalloff * (0.38 + spiral * 1.2) + bulge * 1.8 + halo) * noise);
      const volumeWeight = Math.pow(block.size / Math.max(1, config.radius * 2), 3);
      const starCount = Math.max(1, Math.round(config.totalTargetStars * volumeWeight * density * 11.2));
      const gasDensity = Math.min(1, (spiral * 0.55 + random() * 0.22) * heightFalloff * shape.gasDensityScale);
      const dustDensity = Math.min(1, gasDensity * (0.45 + random() * 0.35));
      const metallicity = Math.min(2.5, 0.45 + bulge * 0.65 + spiral * 0.35 + random() * 0.25);
      const averageMass = 0.25 + random() * 1.7 + bulge * 0.45;
      const blockVolume = getBlockVolume(block);
      const starMass = starCount * averageMass;
      const gasMass = gasDensity * blockVolume * 14;
      const dustMass = dustDensity * blockVolume * 4;
      const planetMass = starMass * 0.035 * clamp01(metallicity / 2.5);
      const fragmentMass = dustMass * 0.08;
      const darkMatterMass = (starMass + gasMass + dustMass + planetMass + fragmentMass) * (0.35 + halo * 2.5);
      const planetCount = Math.max(0, Math.round(starCount * 0.16 * clamp01(metallicity / 2.5)));
      const moonCount = Math.max(0, Math.round(planetCount * 0.38 * (0.6 + dustDensity)));
      const ringedPlanetCount = Math.max(0, Math.round(planetCount * 0.08 * (0.5 + dustDensity)));
      const temperature = clamp01(0.18 + bulge * 0.38 + random() * 0.28 - gasDensity * 0.12);
      const turbulence = clamp01(0.12 + spiral * 0.28 + random() * 0.2);
      const coolingLevel = clamp01(1.0 - temperature * 0.7 - turbulence * 0.35 + gasDensity * 0.28);
      const galacticRadius = Math.max(0.05, radius);
      const angle = Math.atan2(block.center.y, block.center.x);
      const angularVelocity = getGalacticAngularVelocity(galacticRadius, config) * (0.08 + random() * 0.06);

      block.totalMass = starMass + gasMass + dustMass + planetMass + fragmentMass + darkMatterMass;
      block.starMass = starMass;
      block.gasMass = gasMass;
      block.dustMass = dustMass;
      block.planetMass = planetMass;
      block.fragmentMass = fragmentMass;
      block.darkMatterMass = darkMatterMass;
      block.starCount = starCount;
      block.planetCount = planetCount;
      block.moonCount = moonCount;
      block.ringedPlanetCount = ringedPlanetCount;
      block.gasDensity = gasDensity;
      block.dustDensity = dustDensity;
      block.metallicity = metallicity;
      block.temperature = temperature;
      block.turbulence = turbulence;
      block.coolingLevel = coolingLevel;
      block.averageMass = averageMass;
      block.averageVelocity = getOrbitalSpeed(galacticRadius, config);
      block.angularVelocity = angularVelocity;
      block.galacticRadius = galacticRadius;
      block.galacticAngle = angle;
      block.heightAbovePlane = block.center.z;
      block.spiralArmOffset = (spiral - 0.5) * block.size * 0.08;
      block.spiralArmInfluence = 0.6 + spiral * 1.4;
      block.starFormationActivity = gasDensity * (0.35 + spiral);
      block.starFormationCooldown = block.starFormationCooldown || 0;
      block.lastGrowthTime = block.lastGrowthTime || 0;
      block.externalSourceMass = block.externalSourceMass || 0;
      block.growthState = block.growthState || "quiet";
      updateBlockEvolutionState(block);
      block.evolutionState = block.evolutionState || (block.starFormationActivity > 0.55 ? "star-forming" : (bulge > 0.4 ? "old-bulge" : "dormant"));
      block.age = 0.4 + random() * 12.5;
      validateBlockMass(block, config.growth);
    }

    function gaussianRandom(random) {
      const u1 = Math.max(0.000001, random());
      const u2 = random();
      return Math.sqrt(-2 * Math.log(u1)) * Math.cos(Math.PI * 2 * u2);
    }

    function generateGalaxyStarField(config, count) {
      const positions = new Float32Array(count * 3);
      const colors = new Float32Array(count * 3);
      const random = seededRandom(hashBlockSeed(config.seed, "global-starfield", count, [
        Math.round(config.radius * 100),
        Math.round(config.discThickness * 100),
        config.galaxyShape.armCount,
      ]));
      const shape = config.galaxyShape;
      const armCount = Math.max(1, shape.armCount);
      const discRadius = Math.max(1, shape.discRadius || config.radius);
      const bulgeRadius = Math.max(0.2, shape.bulgeRadius);
      const discThickness = Math.max(0.05, shape.discThickness);

      for (let i = 0; i < count; i++) {
        const componentRoll = random();
        let radius;
        let theta;
        let z;

        if (componentRoll < 0.18) {
          radius = Math.pow(random(), 0.35) * bulgeRadius * 1.35;
          theta = random() * Math.PI * 2;
          z = gaussianRandom(random) * discThickness * 0.38;
        } else if (componentRoll < 0.9) {
          radius = Math.pow(random(), 0.72) * discRadius;
          const arm = Math.floor(random() * armCount);
          const armBase = arm * Math.PI * 2 / armCount;
          const armCurve = Math.log(Math.max(0.2, radius / bulgeRadius)) * shape.armTightness;
          const scatter = gaussianRandom(random) * (0.16 + radius / discRadius * 0.28);
          theta = armBase + armCurve + scatter;
          z = gaussianRandom(random) * discThickness * (0.12 + radius / discRadius * 0.05);
        } else {
          radius = Math.pow(random(), 0.45) * Math.max(config.radius, shape.haloRadius * 0.75);
          theta = random() * Math.PI * 2;
          z = gaussianRandom(random) * discThickness * 1.15;
        }

        const warp = Math.sin(theta * 2 + radius * 0.13) * Math.min(0.45, radius / discRadius * 0.32);
        const x = Math.cos(theta) * radius;
        const y = Math.sin(theta) * radius;

        positions[i * 3 + 0] = x;
        positions[i * 3 + 1] = y;
        positions[i * 3 + 2] = z + warp;

        const color = new THREE.Color(0xfff2c6);
        const colorRoll = random();

        if (colorRoll < 0.16) {
          color.set(0xaed6ff);
        } else if (colorRoll < 0.28) {
          color.set(0xffcf8a);
        } else if (colorRoll < 0.36) {
          color.set(0xff9a75);
        } else if (colorRoll < 0.42) {
          color.set(0xd9f8ff);
        }

        const brightness = 0.62 + random() * 0.38;
        colors[i * 3 + 0] = color.r * brightness;
        colors[i * 3 + 1] = color.g * brightness;
        colors[i * 3 + 2] = color.b * brightness;
      }

      return {
        positions,
        colors,
        count,
      };
    }

    function generateBlockStars(block, config, maxSamples) {
      const count = Math.max(0, Math.min(maxSamples, Math.round(Math.sqrt(block.starCount) * 2.2)));
      const positions = new Float32Array(count * 3);
      const velocities = new Float32Array(count * 3);
      const colors = new Float32Array(count * 3);
      const sizes = new Float32Array(count);
      const types = new Uint8Array(count);
      const random = seededRandom(block.seed ^ 0x9E3779B9);

      for (let i = 0; i < count; i++) {
        const localRadius = Math.cbrt(random()) * block.size * 0.48;
        const theta = random() * Math.PI * 2;
        const z = (random() - 0.5) * block.size * 0.42 * Math.max(0.18, block.gasDensity + 0.2);
        const x = Math.cos(theta) * localRadius;
        const y = Math.sin(theta) * localRadius;

        positions[i * 3 + 0] = block.center.x + x;
        positions[i * 3 + 1] = block.center.y + y;
        positions[i * 3 + 2] = block.center.z + z;

        const speed = block.averageVelocity * (0.75 + random() * 0.5);
        velocities[i * 3 + 0] = -Math.sin(block.galacticAngle) * speed;
        velocities[i * 3 + 1] = Math.cos(block.galacticAngle) * speed;
        velocities[i * 3 + 2] = 0;

        let type = 0;
        let color = new THREE.Color(0xfff2c6);

        if (random() < block.starFormationActivity * 0.18) {
          type = 5;
          color.set(0xaed6ff);
        } else if (random() < 0.12) {
          type = 6;
          color.set(0xff9a75);
        } else if (random() < 0.055) {
          type = 2;
          color.set(0xd9f8ff);
        } else if (random() < 0.018) {
          type = 3;
          color.set(0x8ab7ff);
        } else if (random() < 0.012) {
          type = 4;
          color.set(0x333333);
        } else if (random() < 0.18) {
          type = 1;
          color.set(0xffcf8a);
        }

        colors[i * 3 + 0] = color.r;
        colors[i * 3 + 1] = color.g;
        colors[i * 3 + 2] = color.b;
        sizes[i] = type === 5 ? 1.4 : (type === 1 ? 1.2 : 0.75 + random() * 0.65);
        types[i] = type;
      }

      block.starField = {
        positions,
        velocities,
        colors,
        sizes,
        types,
        count,
      };

      return block.starField;
    }

    function generateBlockGas(block) {
      return {
        gasDensity: block.gasDensity,
        dustDensity: block.dustDensity,
        color: new THREE.Color().setHSL(0.56 + block.gasDensity * 0.1, 0.75, 0.18 + block.gasDensity * 0.22),
      };
    }

    function clamp01(value) {
      return Math.max(0, Math.min(1, Number.isFinite(value) ? value : 0));
    }

    function seededUnit(value) {
      const text = String(value);
      let hash = 2166136261;

      for (let i = 0; i < text.length; i++) {
        hash ^= text.charCodeAt(i);
        hash = Math.imul(hash, 16777619);
      }

      return (hash >>> 0) / 4294967295;
    }

    function seededRange(value, min, max) {
      return min + (max - min) * seededUnit(value);
    }

    function getBlockVolume(block) {
      return Math.max(0.000001, block.size * block.size * block.size);
    }

    function getPendulumLocalReach(params = null) {
      const source = params ?? {
        L1: Number(sliders?.L1?.input?.value) || DEFAULTS.L1,
        L2: Number(sliders?.L2?.input?.value) || DEFAULTS.L2,
      };

      return Math.max(0.1, Number(source.L1) + Number(source.L2) + 0.25);
    }

    function getBlockDisplaySize(block) {
      const logicalSize = Number(block?.size) || 0;
      const visualSize = Number(block?.visualSize) || logicalSize;

      if (block?.isDustCube) {
        return Math.max(0.000001, visualSize);
      }

      return Math.max(0.000001, visualSize, logicalSize);
    }

    function getBlockSimulationSpaceMultiplier(block) {
      if (!block?.isDustCube) {
        return ACTIVE_SPACE_DEFAULT_MULTIPLIER;
      }

      const multiplier = Number(block.simulationSpaceMultiplier);
      return Math.max(ACTIVE_SPACE_DEFAULT_MULTIPLIER, Number.isFinite(multiplier) ? multiplier : ACTIVE_SPACE_DEFAULT_MULTIPLIER);
    }

    function getBlockSimulationDisplaySize(block) {
      return getBlockDisplaySize(block) * getBlockSimulationSpaceMultiplier(block);
    }

    function getBlockOutlineDisplaySize(block) {
      if (
        block?.isDustCube &&
        (block.isActive || block === galaxyManager?.selectedBlock) &&
        getBlockSimulationSpaceMultiplier(block) > ACTIVE_SPACE_DEFAULT_MULTIPLIER
      ) {
        return getBlockSimulationDisplaySize(block);
      }

      return getBlockDisplaySize(block);
    }

    function getSimulationWorldScale(params = null, block = null) {
      const activeBlock = block ?? galaxyManager?.activeBlock ?? null;

      if (!activeBlock?.isDustCube) {
        return 1;
      }

      const cubeSize = getBlockDisplaySize(activeBlock);
      const fullLocalSpan = getPendulumLocalReach(params) * 2;

      return Math.max(0.00001, cubeSize / Math.max(0.000001, fullLocalSpan));
    }

    function getSimulationReferenceReach(params = null, block = null) {
      const activeBlock = block ?? galaxyManager?.activeBlock ?? null;
      const centerReach = getPendulumLocalReach(params) * getSimulationWorldScale(params, activeBlock);

      return activeBlock?.isDustCube
        ? centerReach * getBlockSimulationSpaceMultiplier(activeBlock)
        : centerReach;
    }

    function getSimulationOrientation(block = null) {
      const activeBlock = block ?? galaxyManager?.activeBlock ?? null;

      return activeBlock?.isDustCube ? activeBlock.orientation : identityQuaternion;
    }

    function orientSimulationVector(vector, block = null) {
      return vector.applyQuaternion(getSimulationOrientation(block));
    }

    function localSimulationToWorld(value, params = null, block = null) {
      return toVec3(value)
        .multiplyScalar(getSimulationWorldScale(params, block))
        .applyQuaternion(getSimulationOrientation(block))
        .add(activeSimulationOrigin);
    }

    function localSimulationVelocityToWorld(value, params = null, block = null) {
      return toVec3(value)
        .multiplyScalar(getSimulationWorldScale(params, block))
        .applyQuaternion(getSimulationOrientation(block));
    }

    function blockLocalPositionFromWorld(worldPosition, block) {
      const local = worldPosition.clone().sub(block.center);

      if (block?.isDustCube) {
        local.applyQuaternion(block.orientation.clone().invert());
      }

      return local;
    }

    function blockLocalVectorFromWorld(worldVector, block) {
      const local = worldVector.clone();

      if (block?.isDustCube) {
        local.applyQuaternion(block.orientation.clone().invert());
      }

      return local;
    }

    function blockWorldPositionFromLocal(localPosition, block) {
      const world = toVec3(localPosition);

      if (block?.isDustCube) {
        world.applyQuaternion(block.orientation);
      }

      return world.add(block.center);
    }

    function blockWorldVectorFromLocal(localVector, block) {
      const world = toVec3(localVector);

      if (block?.isDustCube) {
        world.applyQuaternion(block.orientation);
      }

      return world;
    }

    function bodyRadiusScaleFromContext(options = null) {
      const explicitScale = Number(options?.radiusScale);

      if (Number.isFinite(explicitScale) && explicitScale > 0) {
        return explicitScale;
      }

      return getSimulationWorldScale();
    }

    function updateBlockBounds(block) {
      if (!block.bounds) {
        block.bounds = {
          min: new THREE.Vector3(),
          max: new THREE.Vector3(),
        };
      }

      const boundsSize = block?.isDustCube ? getBlockSimulationDisplaySize(block) : block.size;
      block.bounds.min.copy(block.center).addScalar(-boundsSize * 0.5);
      block.bounds.max.copy(block.center).addScalar(boundsSize * 0.5);
    }

    function sumBlockMass(block) {
      return (
        (block.starMass || 0) +
        (block.gasMass || 0) +
        (block.dustMass || 0) +
        (block.planetMass || 0) +
        (block.fragmentMass || 0) +
        (block.darkMatterMass || 0)
      );
    }

    function validateBlockMass(block, growthConfig = CONFIG.growth) {
      const calculated = sumBlockMass(block);
      const tolerance = growthConfig?.massTolerance ?? CONFIG.growth.massTolerance;

      if (Math.abs(calculated - (block.totalMass || 0)) > tolerance) {
        if ((block.manager?.massWarningCount || 0) < 5) {
          console.warn("Mass mismatch in block", block.id, {
            expected: block.totalMass,
            calculated,
          });
          block.manager && (block.manager.massWarningCount = (block.manager.massWarningCount || 0) + 1);
        }

        block.totalMass = calculated;
      }

      return block.totalMass;
    }

    function updateBlockDensities(block) {
      const volume = getBlockVolume(block);
      block.gasDensity = clamp01((block.gasMass || 0) / (volume * 14));
      block.dustDensity = clamp01((block.dustMass || 0) / (volume * 4));
      block.starDensity = clamp01((block.starMass || 0) / Math.max(1, block.totalMass || 1));
      block.starFormationActivity = clamp01(
        block.gasDensity * 0.65 +
        block.dustDensity * 0.35 +
        Math.max(0, (block.spiralArmInfluence || 1) - 1) * 0.18
      );
    }

    function updateBlockEvolutionState(block) {
      updateBlockDensities(block);

      if ((block.totalMass || 0) <= 0.000001) {
        block.growthState = "empty";
      } else if (block.isActive) {
        block.growthState = "activePendulumBlock";
      } else if (block.gasDensity > 0.08 && block.starFormationActivity > 0.45) {
        block.growthState = "starForming";
      } else if (block.starCount > 200 || block.starMass > block.totalMass * 0.35) {
        block.growthState = "stellarCluster";
      } else if (block.planetCount > 0 || block.ringedPlanetCount > 0) {
        block.growthState = "solarSystemRegion";
      } else if (block.gasDensity > 0.02) {
        block.growthState = "gasCloud";
      } else if (block.dustDensity > 0.01) {
        block.growthState = "dustCloud";
      } else if (block.gasDensity < CONFIG.growth.depletionGasThreshold && block.dustDensity < CONFIG.growth.minDustDensity) {
        block.growthState = "depleted";
      } else if (block.savedDetailState) {
        block.growthState = "evolved";
      } else {
        block.growthState = "quiet";
      }

      block.evolutionState = block.growthState;
      return block.growthState;
    }

    const BLOCK_MASS_FIELDS = [
      "totalMass",
      "starMass",
      "gasMass",
      "dustMass",
      "planetMass",
      "fragmentMass",
      "darkMatterMass",
    ];

    const BLOCK_COUNT_FIELDS = [
      "starCount",
      "planetCount",
      "moonCount",
      "ringedPlanetCount",
    ];

    function createBlockGrowthSnapshot(block) {
      const snapshot = {
        growthState: block.growthState,
        evolutionState: block.evolutionState,
        gasDensity: block.gasDensity,
        dustDensity: block.dustDensity,
        starDensity: block.starDensity,
        metallicity: block.metallicity,
        temperature: block.temperature,
        turbulence: block.turbulence,
        coolingLevel: block.coolingLevel,
        averageMass: block.averageMass,
        averageVelocity: block.averageVelocity,
        angularVelocity: block.angularVelocity,
        age: block.age,
        starFormationActivity: block.starFormationActivity,
        starFormationCooldown: block.starFormationCooldown,
        lastGrowthTime: block.lastGrowthTime,
        externalSourceMass: block.externalSourceMass,
        spiralArmInfluence: block.spiralArmInfluence,
        galacticRadius: block.galacticRadius,
        galacticAngle: block.galacticAngle,
        heightAbovePlane: block.heightAbovePlane,
        spiralArmOffset: block.spiralArmOffset,
        simulationSpaceMultiplier: getBlockSimulationSpaceMultiplier(block),
      };

      for (const field of BLOCK_MASS_FIELDS) {
        snapshot[field] = block[field] || 0;
      }

      for (const field of BLOCK_COUNT_FIELDS) {
        snapshot[field] = block[field] || 0;
      }

      return snapshot;
    }

    function applyBlockGrowthSnapshot(block, snapshot) {
      if (!block || !snapshot) {
        return;
      }

      for (const field of BLOCK_MASS_FIELDS) {
        if (Number.isFinite(Number(snapshot[field]))) {
          block[field] = Math.max(0, Number(snapshot[field]));
        }
      }

      for (const field of BLOCK_COUNT_FIELDS) {
        if (Number.isFinite(Number(snapshot[field]))) {
          block[field] = Math.max(0, Math.round(Number(snapshot[field])));
        }
      }

      const scalarFields = [
        "gasDensity",
        "dustDensity",
        "starDensity",
        "metallicity",
        "temperature",
        "turbulence",
        "coolingLevel",
        "averageMass",
        "averageVelocity",
        "angularVelocity",
        "age",
        "starFormationActivity",
        "starFormationCooldown",
        "lastGrowthTime",
        "externalSourceMass",
        "spiralArmInfluence",
        "galacticRadius",
        "galacticAngle",
        "heightAbovePlane",
        "spiralArmOffset",
      ];

      for (const field of scalarFields) {
        if (Number.isFinite(Number(snapshot[field]))) {
          block[field] = Number(snapshot[field]);
        }
      }

      block.growthState = snapshot.growthState || block.growthState;
      block.evolutionState = snapshot.evolutionState || block.evolutionState;

      if (block.isDustCube && Number.isFinite(Number(snapshot.simulationSpaceMultiplier))) {
        block.simulationSpaceMultiplier = Math.max(
          ACTIVE_SPACE_DEFAULT_MULTIPLIER,
          Number(snapshot.simulationSpaceMultiplier)
        );
      }

      validateBlockMass(block, block.manager?.config?.growth);
      updateBlockEvolutionState(block);
      updateBlockBounds(block);
    }

    function aggregateChildMassIntoParent(parent) {
      if (!parent?.children?.length) {
        return;
      }

      for (const field of BLOCK_MASS_FIELDS) {
        parent[field] = parent.children.reduce((sum, child) => sum + (child[field] || 0), 0);
      }

      for (const field of BLOCK_COUNT_FIELDS) {
        parent[field] = parent.children.reduce((sum, child) => sum + (child[field] || 0), 0);
      }

      const massTotal = Math.max(0.000001, parent.totalMass || 0);
      const weightedAverage = field => parent.children.reduce((sum, child) => {
        const weight = Math.max(0, child.totalMass || 0) / massTotal;
        return sum + (child[field] || 0) * weight;
      }, 0);

      parent.metallicity = weightedAverage("metallicity") || parent.metallicity;
      parent.temperature = clamp01(weightedAverage("temperature"));
      parent.turbulence = clamp01(weightedAverage("turbulence"));
      parent.coolingLevel = clamp01(weightedAverage("coolingLevel"));
      parent.starFormationCooldown = Math.min(...parent.children.map(child => child.starFormationCooldown || 0));
      parent.lastGrowthTime = Math.max(...parent.children.map(child => child.lastGrowthTime || 0));
      parent.externalSourceMass = parent.children.reduce((sum, child) => sum + (child.externalSourceMass || 0), 0);
      updateBlockEvolutionState(parent);
      validateBlockMass(parent, parent.manager?.config?.growth);
    }

    class BlockGrowthState {
      constructor(block) {
        this.blockId = block.id;
        this.state = block.growthState || "quiet";
        this.lastGrowthTime = block.lastGrowthTime || 0;
        this.cooldown = block.starFormationCooldown || 0;
      }
    }

    class GrowthRules {
      constructor(config) {
        this.config = config;
      }

      shouldPhysicallyGrowBlock(block) {
        const growth = this.config.growth;

        return Boolean(
          growth.enabled &&
          block.gasMass > growth.minGasMass &&
          block.gasDensity > growth.minGasDensity &&
          block.dustDensity > growth.minDustDensity &&
          block.coolingLevel > growth.minCoolingLevel &&
          block.temperature < growth.maxTemperature &&
          block.turbulence < growth.maxTurbulence &&
          block.starFormationCooldown <= 0
        );
      }

      shouldSubdivideBlock(block, cameraDistance, isSelected) {
        const blocks = this.config.blocks;

        if (
          block.level >= Math.min(this.config.maxOctreeDepth, blocks.maxOctreeDepth) ||
          block.size <= blocks.minBlockSize
        ) {
          return false;
        }

        return Boolean(
          isSelected ||
          cameraDistance < Math.max(block.size * 1.4, this.config.lod.blockDistance) ||
          block.starDensity > blocks.highDensityThreshold ||
          block.starFormationActivity > blocks.activityThreshold ||
          block.containsImportantObject
        );
      }
    }

    class StarFormationModel {
      constructor(config) {
        this.config = config;
      }

      calculateStarFormationRate(block) {
        const growth = this.config.growth;
        const gasFactor = Math.pow(Math.max(0, block.gasDensity), growth.gasDensityExponent);
        const coolingFactor = clamp01(block.coolingLevel);
        const turbulenceFactor = 1.0 - clamp01(block.turbulence / growth.maxTurbulence);
        const spiralFactor = block.spiralArmInfluence || 1.0;
        const gasCloudFactor = block.growthState === "gasCloud" ? growth.gasCloudGrowthMultiplier : 1.0;
        const enrichmentFactor = (block.savedDetailState?.activeSupernovaRemnants || 0) > 0
          ? growth.supernovaEnrichmentMultiplier
          : 1.0;
        const haloFactor = Math.abs(block.center.z) > this.config.discThickness * 2
          ? growth.haloGrowthMultiplier
          : 1.0;

        return growth.baseStarFormationRate *
          gasFactor *
          coolingFactor *
          turbulenceFactor *
          spiralFactor *
          gasCloudFactor *
          enrichmentFactor *
          haloFactor *
          getBlockVolume(block);
      }

      estimateNewStarsFromMass(massToConvert, block) {
        const averageMass = Math.max(0.15, block.averageMass || 1);
        return Math.max(0, Math.floor(massToConvert / averageMass));
      }
    }

    class MassDistribution {
      constructor(config) {
        this.config = config;
      }

      calculateChildDensityWeight(child, parent) {
        const spiral = this.config.showSpiralArms ? spiralDensityAt(child.center, this.config) : 0.35;
        const height = Math.abs(child.center.z);
        const heightFalloff = Math.exp(-(height * height) / Math.max(0.05, this.config.discThickness * this.config.discThickness));
        const random = seededRandom(hashBlockSeed(parent.seed, child.id, child.level, child.coordinates));
        return Math.max(0.0001, (0.3 + spiral * 1.5 + heightFalloff * 0.65) * (0.75 + random() * 0.5));
      }

      distributeParentMass(parent, children) {
        if (!children.length) {
          return;
        }

        const weights = children.map(child => this.calculateChildDensityWeight(child, parent));
        const totalWeight = Math.max(0.000001, weights.reduce((sum, weight) => sum + weight, 0));
        const countFields = ["starCount", "planetCount", "moonCount", "ringedPlanetCount"];
        const massFields = ["totalMass", "starMass", "gasMass", "dustMass", "planetMass", "fragmentMass", "darkMatterMass"];

        for (let i = 0; i < children.length; i++) {
          const child = children[i];
          const fraction = weights[i] / totalWeight;

          for (const field of massFields) {
            child[field] = (parent[field] || 0) * fraction;
          }

          for (const field of countFields) {
            child[field] = Math.max(0, Math.round((parent[field] || 0) * fraction));
          }

          child.averageMass = parent.averageMass;
          child.averageVelocity = parent.averageVelocity;
          child.angularVelocity = parent.angularVelocity * (0.92 + fraction * 0.3);
          child.metallicity = Math.max(0, parent.metallicity * (0.9 + fraction * 0.18));
          child.temperature = clamp01((parent.temperature || 0.3) * (0.88 + fraction * 0.3));
          child.turbulence = clamp01((parent.turbulence || 0.2) * (0.75 + fraction * 0.5));
          child.coolingLevel = clamp01((parent.coolingLevel || 0.5) * (0.82 + fraction * 0.42));
          child.spiralArmInfluence = 0.6 + spiralDensityAt(child.center, this.config) * 1.4;
          child.starFormationCooldown = parent.starFormationCooldown;
          child.lastGrowthTime = parent.lastGrowthTime;
          child.externalSourceMass = (parent.externalSourceMass || 0) * fraction;
          child.growthState = parent.growthState;
          updateBlockDensities(child);
          updateBlockEvolutionState(child);
        }

        this.correctRoundingErrors(parent, children);
      }

      correctRoundingErrors(parent, children) {
        const massFields = ["totalMass", "starMass", "gasMass", "dustMass", "planetMass", "fragmentMass", "darkMatterMass"];
        const countFields = ["starCount", "planetCount", "moonCount", "ringedPlanetCount"];
        const targetChild = children.reduce((largest, child) => child.totalMass > largest.totalMass ? child : largest, children[0]);

        for (const field of massFields) {
          const sum = children.reduce((total, child) => total + (child[field] || 0), 0);
          targetChild[field] = Math.max(0, (targetChild[field] || 0) + ((parent[field] || 0) - sum));
        }

        for (const field of countFields) {
          const sum = children.reduce((total, child) => total + (child[field] || 0), 0);
          targetChild[field] = Math.max(0, (targetChild[field] || 0) + ((parent[field] || 0) - sum));
        }

        for (const child of children) {
          validateBlockMass(child, this.config.growth);
          updateBlockEvolutionState(child);
        }
      }
    }

    class BlockSubdivisionController {
      constructor(manager) {
        this.manager = manager;
        this.massDistribution = new MassDistribution(manager.config);
      }

      subdivideBlock(parent) {
        if (parent.children.length > 0) {
          return parent.children;
        }

        if (parent.level >= this.manager.config.maxOctreeDepth) {
          return parent.children;
        }

        const childSize = parent.size * 0.5;
        const offset = childSize * 0.5;
        const children = [];
        let childIndex = 0;

        for (const dx of [-1, 1]) {
          for (const dy of [-1, 1]) {
            for (const dz of [-1, 1]) {
              const childCenter = parent.center.clone().add(new THREE.Vector3(dx * offset, dy * offset, dz * offset));
              const childCoordinates = [
                parent.coordinates[0] * 2 + (dx > 0 ? 1 : 0),
                parent.coordinates[1] * 2 + (dy > 0 ? 1 : 0),
                parent.coordinates[2] * 2 + (dz > 0 ? 1 : 0),
              ];
              children.push(new GalaxyBlock(
                this.manager,
                `${parent.id}.${childIndex}`,
                parent.level + 1,
                parent.id,
                childCenter,
                childSize,
                childCoordinates,
                { skipSummary: true }
              ));
              childIndex++;
            }
          }
        }

        parent.children = children;
        this.massDistribution.config = this.manager.config;
        this.massDistribution.distributeParentMass(parent, children);
        parent.isExpanded = true;
        return parent.children;
      }
    }

    class BlockMatterExchange {
      constructor(manager) {
        this.manager = manager;
      }

      findNeighbourBlocks(block) {
        const reach = block.size * 1.25;
        return this.manager.renderBlocks.filter(candidate =>
          candidate !== block &&
          candidate.level === block.level &&
          candidate.center.distanceTo(block.center) <= reach
        );
      }

      transferMatterBetweenBlocks(blockA, blockB, dt) {
        const gasDelta = blockA.gasDensity - blockB.gasDensity;
        const dustDelta = blockA.dustDensity - blockB.dustDensity;
        let changed = false;

        if (Math.abs(gasDelta) > 0.02) {
          const source = gasDelta > 0 ? blockA : blockB;
          const target = gasDelta > 0 ? blockB : blockA;
          const transfer = Math.min(source.gasMass, Math.abs(gasDelta) * getBlockVolume(source) * 0.015 * dt);
          source.gasMass -= transfer;
          target.gasMass += transfer;
          source.totalMass = Math.max(0, source.totalMass - transfer);
          target.totalMass += transfer;
          changed = transfer > 0;
        }

        if (Math.abs(dustDelta) > 0.012) {
          const source = dustDelta > 0 ? blockA : blockB;
          const target = dustDelta > 0 ? blockB : blockA;
          const transfer = Math.min(source.dustMass, Math.abs(dustDelta) * getBlockVolume(source) * 0.006 * dt);
          source.dustMass -= transfer;
          target.dustMass += transfer;
          source.totalMass = Math.max(0, source.totalMass - transfer);
          target.totalMass += transfer;
          changed = changed || transfer > 0;
        }

        if (changed) {
          validateBlockMass(blockA, this.manager.config.growth);
          validateBlockMass(blockB, this.manager.config.growth);
          updateBlockEvolutionState(blockA);
          updateBlockEvolutionState(blockB);
        }

        return changed;
      }

      update(dt) {
        let changed = false;
        const checked = new Set();

        for (const block of this.manager.renderBlocks) {
          for (const neighbour of this.findNeighbourBlocks(block)) {
            const key = block.id < neighbour.id ? `${block.id}:${neighbour.id}` : `${neighbour.id}:${block.id}`;

            if (checked.has(key)) {
              continue;
            }

            checked.add(key);
            changed = this.transferMatterBetweenBlocks(block, neighbour, dt) || changed;
          }
        }

        return changed;
      }
    }

    class PendulumBlockGrowthAdapter {
      constructor(manager) {
        this.manager = manager;
      }

      recordCreatedMass(block, mass, type = "blob") {
        if (!block || !this.manager.config.growth.pendulumMatterCreationEnabled || mass <= 0) {
          return;
        }

        block.totalMass += mass;
        block.externalSourceMass = (block.externalSourceMass || 0) + mass;
        block.lastGrowthTime = stellarClock;

        if (type === "gas") {
          block.gasMass += mass;
        } else if (type === "planet") {
          block.planetMass += mass;
          block.planetCount++;
        } else if (type === "star") {
          block.starMass += mass;
          block.starCount++;
        } else {
          block.fragmentMass += mass;
        }

        updateBlockEvolutionState(block);
        this.manager.growthStats.pendulumCreatedMass += mass;
      }

      summarizeActiveSimulation(block) {
        if (!block) {
          return null;
        }

        const summary = ensureBlockSummaryState(block);
        let starMass = 0;
        let planetMass = 0;
        let gasMass = 0;
        let fragmentMass = 0;
        let stars = 0;
        let planets = 0;
        let moons = 0;
        let ringed = 0;
        let compact = 0;
        const majorBodiesSummary = [];
        const storedBodies = [];

        for (const body of stellarBodies) {
          if (body.type !== "supernovaEvent" && storedBodies.length < 180) {
            storedBodies.push(serializeBodyForBlock(body, block));
          }

          if (isStarLike(body)) {
            stars++;
            starMass += body.mass;
            compact += isCompactObject(body) ? 1 : 0;
          } else if (PLANET_TYPES.has(body.type)) {
            planets++;
            planetMass += body.mass;
            ringed += body.ring ? 1 : 0;
          } else if (body.type === "moon") {
            moons++;
            planetMass += body.mass;
          } else if (body.type === "gas") {
            gasMass += body.mass;
          } else if (body.type === "fragment" || body.type === "blob" || body.type === "planetesimal") {
            fragmentMass += body.mass;
          }

          if ((isStarLike(body) || PLANET_TYPES.has(body.type) || body.type === "moon") && majorBodiesSummary.length < 60) {
            majorBodiesSummary.push({
              bodyId: body.id,
              parentBodyId: body.parent?.id ?? body.parentId ?? null,
              type: body.type,
              mass: body.mass,
              radius: body.radius,
              radiusScale: body.radiusScale,
              position: blockLocalPositionFromWorld(body.position, block).toArray(),
              velocity: blockLocalVectorFromWorld(body.velocity, block).toArray(),
              coordinateFrame: "blockLocal",
              orbitType: body.orbitType,
              captureState: body.captureState,
              stableOrbitAge: body.stableOrbitAge,
              spin: body.spin,
              spinAxis: blockLocalVectorFromWorld(body.spinAxis, block).toArray(),
              capturedDebrisMass: body.capturedDebrisMass,
              ringMass: body.ring?.mass ?? 0,
              ringCandidate: body.ringCandidate,
              surfaceSeed: body.surfaceSeed,
              orbitElements: serializeOrbitElementsForBlock(body.orbitElements, block),
            });
          }
        }

        block.starCount = Math.max(block.starCount, stars);
        block.planetCount = planets;
        block.moonCount = moons;
        block.ringedPlanetCount = ringed;
        block.starMass = starMass;
        block.planetMass = planetMass;
        block.gasMass = gasMass;
        block.fragmentMass = fragmentMass;
        block.dustMass = Math.max(block.dustMass || 0, fragmentMass * 0.18);
        block.totalMass = sumBlockMass(block);
        validateBlockMass(block, this.manager.config.growth);
        block.metallicity = Math.min(3, block.metallicity + compact * 0.02 + gasMass * 0.00002);
        block.averageVelocity = block.averageVelocity || getOrbitalSpeed(Math.max(0.05, block.center.length()), this.manager.config);
        block.lastGrowthTime = stellarClock;
        updateBlockEvolutionState(block);

        summary.starsCreated = stars;
        summary.planetsCreated = planets;
        summary.moonsCreated = moons;
        summary.ringedPlanets = ringed;
        summary.gasRemaining = gasMass;
        summary.dustRemaining = block.dustMass;
        summary.largestStarMass = Math.max(0, ...stellarBodies.filter(isStarLike).map(body => body.mass));
        summary.totalStellarMass = starMass;
        summary.totalPlanetMass = planetMass;
        summary.totalMass = block.totalMass;
        summary.starMass = block.starMass;
        summary.gasMass = block.gasMass;
        summary.dustMass = block.dustMass;
        summary.planetMass = block.planetMass;
        summary.fragmentMass = block.fragmentMass;
        summary.darkMatterMass = block.darkMatterMass;
        summary.starCount = block.starCount;
        summary.planetCount = block.planetCount;
        summary.moonCount = block.moonCount;
        summary.ringedPlanetCount = block.ringedPlanetCount;
        summary.growthState = block.growthState;
        summary.evolutionState = block.evolutionState;
        summary.gasDensity = block.gasDensity;
        summary.dustDensity = block.dustDensity;
        summary.starDensity = block.starDensity;
        summary.temperature = block.temperature;
        summary.turbulence = block.turbulence;
        summary.coolingLevel = block.coolingLevel;
        summary.starFormationActivity = block.starFormationActivity;
        summary.starFormationCooldown = block.starFormationCooldown;
        summary.lastGrowthTime = block.lastGrowthTime;
        summary.externalSourceMass = block.externalSourceMass;
        summary.spiralArmInfluence = block.spiralArmInfluence;
        summary.galacticRadius = block.galacticRadius;
        summary.galacticAngle = block.galacticAngle;
        summary.heightAbovePlane = block.heightAbovePlane;
        summary.spiralArmOffset = block.spiralArmOffset;
        summary.metallicity = block.metallicity;
        summary.averageVelocity = block.averageVelocity;
        summary.angularMomentum = galacticNormal.toArray();
        summary.numberOfSolarSystems = stellarBodies.filter(body => isStarLike(body) && countChildren(body, child => child.type === "planet") > 0).length;
        summary.activeSupernovaRemnants = compact;
        summary.lastEvolutionTime = stellarClock;
        summary.savedSeedOffset = block.seed ^ Math.round(stellarClock * 1000);
        summary.majorBodiesSummary = majorBodiesSummary;
        summary.storedBodies = storedBodies;

        return summary;
      }
    }

    class GalaxyGrowthManager {
      constructor(manager) {
        this.manager = manager;
        this.rules = new GrowthRules(manager.config);
        this.starFormation = new StarFormationModel(manager.config);
        this.matterExchange = new BlockMatterExchange(manager);
        this.pendulumAdapter = new PendulumBlockGrowthAdapter(manager);
      }

      refreshConfig(config) {
        this.rules.config = config;
        this.starFormation.config = config;
      }

      calculateGrowthProbability(block, dt) {
        const rate = this.starFormation.calculateStarFormationRate(block);
        const available = Math.max(0, block.gasMass + block.dustMass);
        return clamp01((rate * dt) / Math.max(1, available));
      }

      growBlockPhysically(block, dt) {
        const growth = this.manager.config.growth;
        const rate = this.starFormation.calculateStarFormationRate(block);
        const availableGas = Math.max(0, block.gasMass);
        const availableDust = Math.max(0, block.dustMass);
        const availableMass = availableGas + availableDust;
        const massToConvert = Math.min(availableMass, rate * dt);

        if (massToConvert <= 0) {
          return false;
        }

        const dustUse = Math.min(availableDust, massToConvert * growth.dustConsumptionFraction);
        const gasUse = Math.min(availableGas, massToConvert - dustUse);
        const actualConverted = gasUse + dustUse;
        const starMassGain = actualConverted * growth.starFormationEfficiency;
        const leftoverMass = actualConverted - starMassGain;

        block.gasMass = Math.max(0, block.gasMass - gasUse + leftoverMass * 0.65);
        block.dustMass = Math.max(0, block.dustMass - dustUse + leftoverMass * 0.2);
        block.fragmentMass = Math.max(0, (block.fragmentMass || 0) + leftoverMass * 0.15);
        block.starMass += starMassGain;
        block.starCount += this.starFormation.estimateNewStarsFromMass(starMassGain, block);
        block.metallicity = Math.min(3, block.metallicity + actualConverted * 0.00001);
        block.starFormationCooldown = growth.cooldown * (block.growthState === "depleted" ? growth.depletedCooldownMultiplier : 1);
        block.lastGrowthTime = stellarClock;
        updateBlockEvolutionState(block);
        validateBlockMass(block, growth);
        this.manager.growthStats.physicalGrowthMass += actualConverted;
        return true;
      }

      isOuterDiscGasInflowRegion(block) {
        const radius = Math.hypot(block.center.x, block.center.y);
        const outerDiscStart = this.manager.config.radius * 0.55;
        return radius >= outerDiscStart && Math.abs(block.center.z) <= this.manager.config.discThickness * 1.7;
      }

      applyExternalGasInflow(block, dt) {
        const growth = this.manager.config.growth;

        if (!growth.externalInflowEnabled || !this.isOuterDiscGasInflowRegion(block)) {
          return false;
        }

        const spiral = block.spiralArmInfluence || 1;
        const inflowMass = growth.externalGasInflowRate * dt * spiral * Math.max(0.1, block.size / Math.max(1, this.manager.config.blockSize));

        block.gasMass += inflowMass;
        block.totalMass += inflowMass;
        block.externalSourceMass = (block.externalSourceMass || 0) + inflowMass;
        this.manager.growthStats.externalMassAdded += inflowMass;
        updateBlockEvolutionState(block);
        return inflowMass > 0;
      }

      applyCooling(block, dt) {
        block.coolingLevel = clamp01((block.coolingLevel || 0) + dt * 0.015 * (1 - block.temperature));
        block.temperature = clamp01((block.temperature || 0) - dt * 0.006 * block.coolingLevel);
      }

      applyTurbulenceDecay(block, dt) {
        block.turbulence = clamp01((block.turbulence || 0) * Math.exp(-dt * 0.025));
      }

      updateInactiveBlockEvolution(block, dt) {
        if (!block || block.isActive) {
          return false;
        }

        let changed = this.applyExternalGasInflow(block, dt);
        this.applyCooling(block, dt);
        this.applyTurbulenceDecay(block, dt);
        block.starFormationCooldown = Math.max(0, (block.starFormationCooldown || 0) - dt);

        if (this.rules.shouldPhysicallyGrowBlock(block)) {
          changed = this.growBlockPhysically(block, dt) || changed;
        } else {
          updateBlockEvolutionState(block);
        }

        return changed;
      }

      updateBlockLOD(block, cameraDistance) {
        const lod = this.manager.config.lod;

        if (cameraDistance > lod.galaxyDistance) {
          block.lodLevel = "summaryCloud";
        } else if (cameraDistance > lod.clusterDistance) {
          block.lodLevel = "starPoints";
        } else if (cameraDistance > lod.blockDistance) {
          block.lodLevel = "blockDetail";
        } else {
          block.lodLevel = "activeDetail";
        }

        return block.lodLevel;
      }

      findNeighbourBlocks(block) {
        return this.matterExchange.findNeighbourBlocks(block);
      }

      transferMatterBetweenBlocks(blockA, blockB, dt) {
        return this.matterExchange.transferMatterBetweenBlocks(blockA, blockB, dt);
      }

      findLeafBlockForPosition(position) {
        let best = null;
        let bestSize = Infinity;

        for (const block of this.manager.renderBlocks) {
          if (
            position.x >= block.bounds.min.x && position.x <= block.bounds.max.x &&
            position.y >= block.bounds.min.y && position.y <= block.bounds.max.y &&
            position.z >= block.bounds.min.z && position.z <= block.bounds.max.z &&
            block.size < bestSize
          ) {
            best = block;
            bestSize = block.size;
          }
        }

        return best;
      }

      addBodyMassToBlock(block, body, sourceBlock = null) {
        if (!block || !body) {
          return;
        }

        const summary = ensureBlockSummaryState(block);

        if (body.type !== "supernovaEvent") {
          summary.storedBodies = Array.isArray(summary.storedBodies) ? summary.storedBodies : [];

          if (summary.storedBodies.length < 180) {
            summary.storedBodies.push(serializeBodyForBlock(body, block));
          }
        }

        if (body.type === "gas") {
          block.gasMass += body.mass;
          summary.gasRemaining += body.mass;
        } else if (body.type === "fragment" || body.type === "blob" || body.type === "planetesimal") {
          block.fragmentMass += body.mass;
          summary.dustRemaining += body.mass;
        } else if (isStarLike(body)) {
          block.starMass += body.mass;
          block.starCount += 1;
          summary.starsCreated += 1;
          summary.totalStellarMass += body.mass;
          summary.largestStarMass = Math.max(summary.largestStarMass, body.mass);
        } else if (PLANET_TYPES.has(body.type)) {
          block.planetMass += body.mass;
          block.planetCount += 1;
          summary.planetsCreated += 1;
          summary.totalPlanetMass += body.mass;
        } else if (body.type === "moon") {
          block.planetMass += body.mass;
          block.moonCount += 1;
          summary.moonsCreated += 1;
        }

        if ((isStarLike(body) || PLANET_TYPES.has(body.type) || body.type === "moon") && summary.majorBodiesSummary.length < 60) {
          summary.majorBodiesSummary.push({
            type: body.type,
            mass: body.mass,
            radius: body.radius,
            radiusScale: body.radiusScale,
            position: blockLocalPositionFromWorld(body.position, block).toArray(),
            coordinateFrame: "blockLocal",
            ringMass: body.ring?.mass ?? 0,
          });
        }

        block.totalMass = sumBlockMass(block);
        summary.lastEvolutionTime = stellarClock;
        updateBlockEvolutionState(block);
        validateBlockMass(block, this.manager.config.growth);
      }

      handleBodyLeavingActiveBlock(body, sourceBlock) {
        const dustDestination = sourceBlock?.isDustCube
          ? this.manager.findDustCubeForPosition(body.position, sourceBlock)
          : null;
        const destination = dustDestination || this.findLeafBlockForPosition(body.position) || sourceBlock;
        this.addBodyMassToBlock(destination, body, sourceBlock);
        removeBody(body);
        return true;
      }

      update(dt) {
        this.refreshConfig(this.manager.config);
        let changed = false;

        for (const block of this.manager.renderBlocks) {
          changed = this.updateInactiveBlockEvolution(block, dt) || changed;
        }

        if (this.manager.growthExchangeTimer <= 0) {
          changed = this.matterExchange.update(dt) || changed;
          this.manager.growthExchangeTimer = 0.75;
        } else {
          this.manager.growthExchangeTimer -= dt;
        }

        return changed;
      }
    }

    class GalaxyBlock {
      constructor(manager, id, level, parentId, center, size, coordinates, options = {}) {
        this.manager = manager;
        this.id = id;
        this.level = level;
        this.parentId = parentId;
        this.children = [];
        this.center = center.clone();
        this.initialCenter = center.clone();
        this.size = size;
        this.coordinates = coordinates;
        this.bounds = null;
        updateBlockBounds(this);
        this.seed = hashBlockSeed(manager.config.seed, id, level, coordinates);
        this.isDustCube = Boolean(options.isDustCube);
        this.sourcePointIndex = Number.isFinite(Number(options.sourcePointIndex)) ? Number(options.sourcePointIndex) : null;
        this.sourceColor = options.sourceColor ? options.sourceColor.clone() : null;
        this.creationStarted = Boolean(options.creationStarted);
        this.systemName = typeof options.systemName === "string" ? options.systemName : "";
        this.systemCoordinate = typeof options.systemCoordinate === "string" ? options.systemCoordinate : "";
        this.systemStartedAt = typeof options.systemStartedAt === "string" ? options.systemStartedAt : "";
        this.systemMarker = null;
        this.orientation = options.orientation ? options.orientation.clone().normalize() : new THREE.Quaternion();
        this.visualSize = Number.isFinite(Number(options.visualSize)) && Number(options.visualSize) > 0
          ? Number(options.visualSize)
          : size;
        this.simulationSpaceMultiplier = Math.max(
          ACTIVE_SPACE_DEFAULT_MULTIPLIER,
          Number.isFinite(Number(options.simulationSpaceMultiplier))
            ? Number(options.simulationSpaceMultiplier)
            : ACTIVE_SPACE_DEFAULT_MULTIPLIER
        );
        if (this.isDustCube) {
          this.size = this.visualSize;
          updateBlockBounds(this);
        }
        this.totalMass = 0;
        this.starMass = 0;
        this.gasMass = 0;
        this.dustMass = 0;
        this.planetMass = 0;
        this.fragmentMass = 0;
        this.darkMatterMass = 0;
        this.starCount = 0;
        this.planetCount = 0;
        this.moonCount = 0;
        this.ringedPlanetCount = 0;
        this.gasDensity = 0;
        this.dustDensity = 0;
        this.starDensity = 0;
        this.metallicity = 1;
        this.temperature = 0.35;
        this.turbulence = 0.2;
        this.coolingLevel = 0.5;
        this.averageMass = 1;
        this.averageVelocity = 0;
        this.angularVelocity = 0;
        this.age = 0;
        this.evolutionState = "dormant";
        this.growthState = "quiet";
        this.starFormationActivity = 0;
        this.starFormationCooldown = 0;
        this.lastGrowthTime = 0;
        this.externalSourceMass = 0;
        this.isExpanded = false;
        this.isActive = false;
        this.isSimulated = false;
        this.lodLevel = "summaryCloud";
        this.summaryVisual = null;
        this.pickMesh = null;
        this.outlineMesh = null;
        this.detailedBodies = [];
        this.savedDetailState = null;
        this.starField = null;
        this.galacticRadius = 0;
        this.galacticAngle = 0;
        this.heightAbovePlane = 0;
        this.spiralArmOffset = 0;
        this.spiralArmInfluence = 1;

        if (!options.skipSummary) {
          this.generateSummary();
        }
      }

      generateSummary() {
        if (this.isDustCube && !this.savedDetailState) {
          updateBlockEvolutionState(this);
          validateBlockMass(this, this.manager.config.growth);
          return;
        }

        if (!this.isDustCube && this.children.length === 0) {
          generateBlockSummary(this, this.manager.config);
        }

        if (this.savedDetailState) {
          if (Number.isFinite(this.savedDetailState.totalMass)) {
            applyBlockGrowthSnapshot(this, this.savedDetailState);
            return;
          }

          this.starCount += this.savedDetailState.starsCreated || 0;
          this.planetCount += this.savedDetailState.planetsCreated || 0;
          this.moonCount += this.savedDetailState.moonsCreated || 0;
          this.ringedPlanetCount += this.savedDetailState.ringedPlanets || 0;
          this.starMass += this.savedDetailState.totalStellarMass || 0;
          this.planetMass += this.savedDetailState.totalPlanetMass || 0;
          this.gasMass += this.savedDetailState.gasRemaining || 0;
          this.dustMass += this.savedDetailState.dustRemaining || 0;
          this.metallicity = Math.min(3, this.metallicity + (this.savedDetailState.metallicity || 0) * 0.05);
        }

        updateBlockEvolutionState(this);
        validateBlockMass(this, this.manager.config.growth);
      }

      subdivide() {
        return this.manager.subdivisionController.subdivideBlock(this);
      }

      expand() {
        this.isExpanded = true;
        this.subdivide();
        this.manager.rebuildVisuals();
      }

      collapseToSummary() {
        this.saveEvolutionState();
        aggregateChildMassIntoParent(this);
        this.isExpanded = false;

        for (const child of this.children) {
          child.isExpanded = false;
          child.isActive = false;
          child.isSimulated = false;
        }

        this.manager.rebuildVisuals();
      }

      saveEvolutionState() {
        if (this.manager.activeBlock !== this) {
          return this.savedDetailState;
        }
        this.savedDetailState = this.manager.growthManager.pendulumAdapter.summarizeActiveSimulation(this);
        return this.savedDetailState;
      }

      restoreEvolutionState() {
        const storedBodies = Array.isArray(this.savedDetailState?.storedBodies)
          ? this.savedDetailState.storedBodies
          : this.savedDetailState?.majorBodiesSummary;

        if (!Array.isArray(storedBodies) || storedBodies.length === 0) {
          return false;
        }

        const restoredBodies = [];
        const restoredBySourceId = new Map();

        for (const summary of storedBodies.slice(0, 160)) {
          if (!summary?.type || summary.type === "supernovaEvent") {
            continue;
          }

          const positionArray = Array.isArray(summary.position) ? summary.position.map(Number) : [0, 0, 0];
          const velocityArray = Array.isArray(summary.velocity) ? summary.velocity.map(Number) : null;
          const restoredType = summary.type === "blackHole" ? "neutronStar" : summary.type;
          const usesBlockLocalFrame = summary.coordinateFrame === "blockLocal";

          const restoredBody = createBody(restoredType, {
            mass: summary.mass,
            position: usesBlockLocalFrame
              ? blockWorldPositionFromLocal(positionArray, this)
              : this.center.clone().add(new THREE.Vector3().fromArray(positionArray)),
            velocity: velocityArray && velocityArray.length >= 3 && velocityArray.every(Number.isFinite)
              ? (usesBlockLocalFrame
                ? blockWorldVectorFromLocal(velocityArray, this)
                : new THREE.Vector3().fromArray(velocityArray))
              : randomUnitVector().multiplyScalar(0.03),
            spin: Number(summary.spin) || undefined,
            radiusScale: Number.isFinite(Number(summary.radiusScale))
              ? Number(summary.radiusScale)
              : getSimulationWorldScale(null, this),
            temperature: Number(summary.temperature) || undefined,
            age: Number(summary.age) || 0,
            fuel: Number.isFinite(Number(summary.fuel)) ? Number(summary.fuel) : undefined,
            instability: Number(summary.instability) || 0,
            metallicity: Number.isFinite(Number(summary.metallicity)) ? Number(summary.metallicity) : undefined,
            orbitType: summary.orbitType,
            captureState: summary.captureState,
            stableOrbitAge: Number(summary.stableOrbitAge) || 0,
            spinAxis: Array.isArray(summary.spinAxis)
              ? blockWorldVectorFromLocal(summary.spinAxis, this)
              : undefined,
            angularMomentum: Array.isArray(summary.angularMomentum)
              ? blockWorldVectorFromLocal(summary.angularMomentum, this)
              : undefined,
            capturedDebrisMass: Number(summary.capturedDebrisMass) || 0,
            ringCandidate: typeof summary.ringCandidate === "boolean" ? summary.ringCandidate : undefined,
            surfaceSeed: Number.isFinite(Number(summary.surfaceSeed)) ? Number(summary.surfaceSeed) : undefined,
            orbitElements: restoreOrbitElementsFromBlock(summary.orbitElements, this),
            protectedAge: 1.5,
          });

          restoredBodies.push({ body: restoredBody, summary });

          if (summary.bodyId !== undefined && summary.bodyId !== null) {
            restoredBySourceId.set(String(summary.bodyId), restoredBody);
          }
        }

        for (const { body, summary } of restoredBodies) {
          const parent = summary.parentBodyId !== undefined && summary.parentBodyId !== null
            ? restoredBySourceId.get(String(summary.parentBodyId))
            : null;

          if (parent?.alive) {
            body.parent = parent;
            body.parentId = parent.id;
            body.orbitParentId = parent.id;

            if (!body.orbitElements && (PLANET_TYPES.has(body.type) || body.type === "moon")) {
              initialiseEllipticalOrbit(body, parent, body.type === "moon" ? parent.spinAxis : galacticNormal, body.type === "moon" ? 1.08 : 1);
            }
          }

          const ringMass = Number(summary.ringMass) || 0;

          if (ringMass > 0 && PLANET_TYPES.has(body.type)) {
            body.ringCandidate = true;
            body.capturedDebrisMass = Math.max(body.capturedDebrisMass || 0, ringMass);
            const ring = createPlanetRing(body, getStellarConfig());
            ring.mass = Math.min(getStellarConfig().ring.maxRingMass, ringMass);
            ring.density = Math.min(1, ring.mass / Math.max(1, getStellarConfig().ring.maxRingMass));
            renderPlanetRing(body, getStellarConfig());
          }
        }

        return true;
      }

      renderLOD(cameraDistance) {
        this.manager.growthManager.updateBlockLOD(this, cameraDistance);
      }

      runDetailedSimulation(dt) {
        if (this.isActive && this.isSimulated) {
          updateStellarBodies(dt, getParams());
        }
      }
    }

    class GalaxyManager {
      constructor() {
        this.config = getGalaxyConfig();
        this.configKey = "";
        this.root = null;
        this.renderBlocks = [];
        this.visibleBlocks = 0;
        this.renderedStars = 0;
        this.representedStars = 0;
        this.expandedBlocks = 0;
        this.activeBlock = null;
        this.selectedBlock = null;
        this.blockFocusLocked = false;
        this.starPoints = null;
        this.dustPointField = null;
        this.dustPointMetadata = null;
        this.dustCubeBlocks = new Map();
        this.focusStarPoints = null;
        this.focusStarBlockId = null;
        this.renderedFocusStars = 0;
        this.selectionMesh = null;
        this.raycaster = new THREE.Raycaster();
        this.pointer = new THREE.Vector2();
        this.blockCloudGeometry = new THREE.SphereGeometry(1, 18, 10);
        this.blockBoxGeometry = new THREE.BoxGeometry(1, 1, 1);
        this.growthStats = {
          externalMassAdded: 0,
          physicalGrowthMass: 0,
          pendulumCreatedMass: 0,
        };
        this.growthExchangeTimer = 0;
        this.growthVisualRefreshTimer = 0;
        this.massWarningCount = 0;
        this.subdivisionController = new BlockSubdivisionController(this);
        this.growthManager = new GalaxyGrowthManager(this);
        this.reset(this.config);
      }

      reset(config) {
        this.config = config;
        this.subdivisionController.massDistribution.config = config;
        this.growthManager.refreshConfig(config);
        this.configKey = getGalaxyConfigKey(config);
        clearGalaxyGroup(galaxyCloudGroup);
        clearGalaxyGroup(galaxyBlockGroup);
        clearGalaxyGroup(galaxyStarPointGroup);
        clearGalaxyGroup(focusStarPointGroup);
        clearGalaxyGroup(selectionGroup);
        clearGalaxyGroup(startedSystemMarkerGroup);
        this.starPoints = null;
        this.dustPointField = null;
        this.dustPointMetadata = null;
        this.dustCubeBlocks.clear();
        this.focusStarPoints = null;
        this.focusStarBlockId = null;
        this.renderedFocusStars = 0;
        this.selectedBlock = null;
        this.activeBlock = null;
        this.blockFocusLocked = false;
        this.renderBlocks = [];
        this.representedStars = 0;
        this.renderedStars = 0;
        this.expandedBlocks = 0;
        this.growthExchangeTimer = 0;
        this.growthVisualRefreshTimer = 0;
        this.massWarningCount = 0;

        const rootSize = config.radius * 2;
        this.root = new GalaxyBlock(this, "B", 0, null, new THREE.Vector3(0, 0, 0), rootSize, [0, 0, 0]);
        this.root.isExpanded = true;
        this.root.subdivide();

        if (config.maxOctreeDepth >= 2) {
          for (const child of this.root.children) {
            child.isExpanded = true;
            child.subdivide();
          }
        }

        this.rebuildVisuals();
      }

      collectRenderableBlocks(block = this.root, output = []) {
        if (!block) {
          return output;
        }

        if (block !== this.root && (!block.isExpanded || block.children.length === 0)) {
          output.push(block);
          return output;
        }

        const children = block.children.length > 0 ? block.children : block.subdivide();

        for (const child of children) {
          this.collectRenderableBlocks(child, output);
        }

        return output;
      }

      countExpandedBlocks(block = this.root) {
        if (!block) {
          return 0;
        }

        let count = block !== this.root && block.isExpanded ? 1 : 0;

        for (const child of block.children) {
          count += this.countExpandedBlocks(child);
        }

        return count;
      }

      rebuildVisuals() {
        clearGalaxyGroup(galaxyCloudGroup);
        clearGalaxyGroup(galaxyBlockGroup);
        clearGalaxyGroup(galaxyStarPointGroup);
        clearGalaxyGroup(focusStarPointGroup);
        clearGalaxyGroup(selectionGroup);
        clearGalaxyGroup(startedSystemMarkerGroup);
        this.renderBlocks = [
          ...this.collectRenderableBlocks(),
          ...this.dustCubeBlocks.values(),
        ];
        this.visibleBlocks = this.renderBlocks.length;
        this.representedStars = 0;
        this.expandedBlocks = 0;

        for (const block of this.renderBlocks) {
          this.representedStars += block.starCount;
          block.systemMarker = null;

          const displaySize = getBlockDisplaySize(block);
          const outlineDisplaySize = getBlockOutlineDisplaySize(block);
          const gas = generateBlockGas(block);
          const cloudMaterial = new THREE.MeshBasicMaterial({
            color: gas.color,
            transparent: true,
            opacity: Math.min(0.13, 0.018 + (block.gasDensity + block.dustDensity) * 0.045),
            blending: THREE.AdditiveBlending,
            depthWrite: false,
          });
          const cloud = new THREE.Mesh(this.blockCloudGeometry, cloudMaterial);
          cloud.position.copy(block.center);
          cloud.scale.setScalar(displaySize * (0.18 + block.gasDensity * 0.08));
          cloud.userData.blockId = block.id;
          block.summaryVisual = cloud;
          galaxyCloudGroup.add(cloud);

          const pickMaterial = new THREE.MeshBasicMaterial({
            color: 0x66ccff,
            transparent: true,
            opacity: 0.0,
            depthWrite: false,
          });
          const pickMesh = new THREE.Mesh(this.blockBoxGeometry, pickMaterial);
          pickMesh.position.copy(block.center);
          pickMesh.quaternion.copy(block.orientation);
          pickMesh.scale.setScalar(outlineDisplaySize);
          pickMesh.userData.blockId = block.id;
          block.pickMesh = pickMesh;
          galaxyBlockGroup.add(pickMesh);

          const outlineColor = block === this.selectedBlock
            ? 0xfff17a
            : (block.isDustCube && block.sourceColor ? block.sourceColor : 0xffffff);
          const outline = new THREE.LineSegments(
            new THREE.EdgesGeometry(this.blockBoxGeometry),
            new THREE.LineBasicMaterial({
              color: outlineColor,
              transparent: true,
              opacity: block === this.selectedBlock ? 0.22 : 0,
              depthTest: false,
              depthWrite: false,
            })
          );
          outline.position.copy(block.center);
          outline.quaternion.copy(block.orientation);
          outline.scale.setScalar(outlineDisplaySize);
          outline.renderOrder = 90;
          outline.visible = block.isDustCube && this.config.showBlocks && block !== this.selectedBlock;
          block.outlineMesh = outline;
          galaxyBlockGroup.add(outline);

          if (block.isDustCube && block.creationStarted) {
            this.createStartedSystemMarker(block, displaySize);
          }
        }

        this.expandedBlocks = this.countExpandedBlocks();
        this.rebuildStarPointField();
        this.rebuildFocusStarPointField(this.selectedBlock);
        this.highlightSelection();
      }

      getUsedSystemNames(exceptBlock = null) {
        const names = new Set();

        for (const block of this.dustCubeBlocks.values()) {
          if (block !== exceptBlock && block.systemName) {
            names.add(block.systemName);
          }
        }

        return names;
      }

      allocateSystemName(block) {
        if (!systemNamePool.length) {
          return makeSystemFallbackName(block);
        }

        const usedNames = this.getUsedSystemNames(block);
        const sourceIndex = Number.isFinite(Number(block.sourcePointIndex)) ? Number(block.sourcePointIndex) : block.seed;
        const startIndex = Math.abs(Math.round(sourceIndex)) % systemNamePool.length;

        for (let i = 0; i < systemNamePool.length; i++) {
          const candidate = systemNamePool[(startIndex + i) % systemNamePool.length];

          if (candidate && !usedNames.has(candidate)) {
            return candidate;
          }
        }

        return makeSystemFallbackName(block);
      }

      formatSystemCoordinate(block) {
        const index = Number.isFinite(Number(block.sourcePointIndex)) ? Number(block.sourcePointIndex) : 0;

        return `C${index.toString().padStart(4, "0")} ${block.center.x.toFixed(2)}, ${block.center.y.toFixed(2)}, ${block.center.z.toFixed(2)}`;
      }

      markSystemCreationStarted(block, rebuild = true) {
        if (!block?.isDustCube) {
          return false;
        }

        const changed = !block.creationStarted || !block.systemName || !block.systemCoordinate;
        block.creationStarted = true;
        block.systemName = block.systemName || this.allocateSystemName(block);
        block.systemCoordinate = block.systemCoordinate || this.formatSystemCoordinate(block);
        block.systemStartedAt = block.systemStartedAt || new Date().toISOString();
        block.growthState = block.growthState === "empty" ? "activeSystem" : block.growthState;
        block.evolutionState = block.evolutionState === "empty" ? "creatingSystem" : block.evolutionState;

        if (changed && rebuild) {
          this.rebuildVisuals();
        }

        return changed;
      }

      createStartedSystemMarker(block, displaySize = getBlockDisplaySize(block)) {
        const marker = new THREE.Group();
        marker.userData.blockId = block.id;
        marker.position.copy(block.center);

        const dot = new THREE.Sprite(new THREE.SpriteMaterial({
          map: createGlowDotTexture(block === this.activeBlock ? "#ffffff" : SYSTEM_MARKER_LABEL_COLOR),
          color: block === this.activeBlock ? SYSTEM_MARKER_ACTIVE_COLOR : SYSTEM_MARKER_BASE_COLOR,
          transparent: true,
          depthTest: false,
          depthWrite: false,
          blending: THREE.AdditiveBlending,
        }));
        dot.userData.blockId = block.id;
        dot.renderOrder = 140;
        marker.add(dot);

        const label = createTextSprite([block.systemName || makeSystemFallbackName(block), block.systemCoordinate || this.formatSystemCoordinate(block)]);
        label.userData.blockId = block.id;
        label.renderOrder = 141;
        marker.add(label);

        marker.userData.dot = dot;
        marker.userData.label = label;
        startedSystemMarkerGroup.add(marker);
        block.systemMarker = marker;
        this.updateStartedSystemMarker(block, displaySize);
      }

      updateStartedSystemMarker(block, displaySize = getBlockDisplaySize(block)) {
        const marker = block.systemMarker;

        if (!marker) {
          return;
        }

        const dot = marker.userData.dot;
        const label = marker.userData.label;
        const isActive = block === this.activeBlock;
        const isSelected = block === this.selectedBlock;
        const hideInCloseDetail = (isActive || isSelected) &&
          (
            focusedBlockScreenPixels >= SYSTEM_MARKER_SHOW_MAX_SCREEN_PX ||
            cameraDistance < Math.max(0.35, displaySize * SYSTEM_MARKER_HIDE_DISTANCE_MULTIPLIER)
          );
        const dotSize = Math.max(displaySize * 2.6, cameraDistance * 0.012) * (isActive || isSelected ? 1.25 : 1);
        const labelHeight = Math.max(displaySize * 1.4, cameraDistance * 0.026) * (isActive || isSelected ? 1.12 : 1);

        marker.position.copy(block.center);
        marker.quaternion.copy(block.orientation);
        marker.visible = !hideInCloseDetail;

        if (hideInCloseDetail) {
          return;
        }

        if (dot) {
          dot.scale.setScalar(dotSize);

          if (dot.material?.color) {
            dot.material.color.setHex(isActive ? SYSTEM_MARKER_ACTIVE_COLOR : SYSTEM_MARKER_BASE_COLOR);
          }
        }

        if (label) {
          label.position.set(0, dotSize * 1.05, 0);
          label.scale.set(labelHeight * (label.userData.textureAspect || 3), labelHeight, 1);
        }
      }

      rebuildStarPointField() {
        clearGalaxyGroup(galaxyStarPointGroup);

        if (!this.config.showStarPoints) {
          this.renderedStars = 0;
          this.dustPointField = null;
          return;
        }

        const maxStars = this.config.starsPerRenderBatch;
        const field = generateGalaxyStarField(this.config, maxStars);
        this.dustPointField = field;
        this.dustPointMetadata = [];

        const mesh = this.createDustCubeInstanceMesh(
          field.positions,
          field.colors,
          field.count,
          this.getInitialDustVisualCubeSize(),
          hashBlockSeed(this.config.seed, "initial-dust-cube-rotation", field.count, [
            Math.round(this.config.radius * 100),
            Math.round(this.config.discThickness * 100),
          ]),
          this.dustPointMetadata
        );
        mesh.userData.pointSource = "initialDust";
        this.starPoints = mesh;
        galaxyStarPointGroup.add(this.starPoints);
        this.renderedStars = field.count;
      }

      createDustCubeInstanceMesh(positions, colors, count, cubeSize, randomSeed, metadata = null) {
        const geometry = new THREE.BoxGeometry(1, 1, 1);
        const material = new THREE.MeshStandardMaterial({
          color: 0xffffff,
          vertexColors: true,
          transparent: true,
          opacity: 0.94,
          depthWrite: false,
          roughness: 0.42,
          metalness: 0.02,
          emissive: 0x101418,
          emissiveIntensity: 0.34,
          flatShading: true,
        });
        const mesh = new THREE.InstancedMesh(geometry, material, count);
        const matrix = new THREE.Matrix4();
        const position = new THREE.Vector3();
        const rotation = new THREE.Euler();
        const quaternion = new THREE.Quaternion();
        const scale = new THREE.Vector3();
        const color = new THREE.Color();
        const random = seededRandom(randomSeed >>> 0);

        for (let i = 0; i < count; i++) {
          const offset = i * 3;
          position.set(positions[offset], positions[offset + 1], positions[offset + 2]);
          rotation.set(random() * Math.PI, random() * Math.PI, random() * Math.PI);
          quaternion.setFromEuler(rotation);
          scale.setScalar(cubeSize * (0.82 + random() * 0.55));
          matrix.compose(position, quaternion, scale);
          color.setRGB(colors[offset], colors[offset + 1], colors[offset + 2]);
          mesh.setMatrixAt(i, matrix);
          mesh.setColorAt(i, color);

          if (metadata) {
            metadata[i] = {
              orientation: quaternion.clone(),
              visualScale: scale.x,
            };
          }
        }

        mesh.instanceMatrix.needsUpdate = true;

        if (mesh.instanceColor) {
          mesh.instanceColor.needsUpdate = true;
        }

        return mesh;
      }

      rebuildFocusStarPointField(block = this.selectedBlock) {
        clearGalaxyGroup(focusStarPointGroup);
        this.focusStarPoints = null;
        this.focusStarBlockId = null;
        this.renderedFocusStars = 0;

        if (!block || !this.config.showStarPoints) {
          return;
        }

        if (block.isDustCube) {
          return;
        }

        const representedCount = Math.max(block.starCount, block.starMass * 0.45, block.totalMass * 0.05, 120);
        const count = Math.min(
          this.config.starsPerRenderBatch,
          Math.max(220, Math.round(Math.sqrt(representedCount) * 5.5))
        );
        const random = seededRandom((block.seed ^ 0x9e3779b9) >>> 0);
        const positions = new Float32Array(count * 3);
        const colors = new Float32Array(count * 3);
        const half = block.size * 0.48;
        const planeThickness = Math.max(0.035, Math.min(half * 0.75, this.config.discThickness * 0.34 + block.size * 0.025));
        const palette = [
          [1.0, 0.94, 0.68],
          [0.68, 0.84, 1.0],
          [1.0, 0.72, 0.52],
          [0.82, 1.0, 0.88],
          [0.96, 0.82, 1.0],
        ];

        for (let i = 0; i < count; i++) {
          const armBias = random() < Math.min(0.82, 0.35 + block.spiralArmInfluence * 0.18);
          const angle = random() * Math.PI * 2;
          const radial = Math.sqrt(random()) * half * (armBias ? 1 : 0.68);
          const spread = armBias ? 0.28 : 1;
          const x = Math.cos(angle) * radial + (random() - 0.5) * half * spread;
          const y = Math.sin(angle) * radial + (random() - 0.5) * half * spread;
          const z = Math.max(
            -half,
            Math.min(half, (random() + random() + random() - 1.5) * planeThickness + (random() - 0.5) * half * 0.05)
          );
          const color = palette[Math.floor(random() * palette.length)];
          const brightness = 0.62 + random() * 0.38;
          const offset = i * 3;

          positions[offset] = Math.max(-half, Math.min(half, x));
          positions[offset + 1] = Math.max(-half, Math.min(half, y));
          positions[offset + 2] = z;
          colors[offset] = color[0] * brightness;
          colors[offset + 1] = color[1] * brightness;
          colors[offset + 2] = color[2] * brightness;
        }

        this.focusStarPoints = this.createDustCubeInstanceMesh(
          positions,
          colors,
          count,
          this.getFocusDustVisualCubeSize(block),
          (block.seed ^ 0x85ebca6b) >>> 0
        );
        this.focusStarPoints.position.copy(block.center);
        this.focusStarPoints.userData.blockId = block.id;
        focusStarPointGroup.add(this.focusStarPoints);
        this.focusStarBlockId = block.id;
        this.renderedFocusStars = count;
      }

      getInitialDustCubeSize() {
        return this.getInitialDustVisualCubeSize();
      }

      getInitialDustVisualCubeSize() {
        return Math.max(
          INITIAL_DUST_VISUAL_CUBE_MIN_SIZE,
          Math.min(INITIAL_DUST_VISUAL_CUBE_MAX_SIZE, this.config.radius * 0.009)
        );
      }

      getDustCubeSeedSize(pointIndex, requestedSize = null) {
        const requested = Number(requestedSize);
        const metadataSize = Number(this.dustPointMetadata?.[pointIndex]?.visualScale);
        const baseVisualSize = this.getInitialDustVisualCubeSize();
        const maxSeedSize = Math.max(INITIAL_DUST_VISUAL_CUBE_MAX_SIZE, baseVisualSize) * 1.45;

        if (Number.isFinite(requested) && requested > 0 && requested <= maxSeedSize) {
          return requested;
        }

        if (Number.isFinite(metadataSize) && metadataSize > 0) {
          return metadataSize;
        }

        return baseVisualSize;
      }

      getFocusDustVisualCubeSize(block) {
        return Math.max(
          FOCUS_DUST_VISUAL_CUBE_MIN_SIZE,
          Math.min(FOCUS_DUST_VISUAL_CUBE_MAX_SIZE, block.size * 0.035)
        );
      }

      createDustCubeBlock(pointIndex, position = null, sourceColor = null, size = null, orientation = null, visualSize = null, simulationSpaceMultiplier = null) {
        const id = `D.${pointIndex}`;
        const existing = this.dustCubeBlocks.get(id);

        if (existing) {
          return existing;
        }

        const cubeVisualSize = Math.max(0.04, this.getDustCubeSeedSize(pointIndex, visualSize));
        const center = position?.clone ? position.clone() : new THREE.Vector3();
        const cubeSize = cubeVisualSize;
        const color = sourceColor?.clone ? sourceColor.clone() : new THREE.Color(0xfff2c6);
        const cubeOrientation = orientation?.clone ? orientation.clone().normalize() : new THREE.Quaternion();
        const block = new GalaxyBlock(
          this,
          id,
          this.config.maxOctreeDepth,
          "initial-dust",
          center,
          cubeSize,
          [pointIndex, 0, 0],
          {
            skipSummary: true,
            isDustCube: true,
            sourcePointIndex: pointIndex,
            sourceColor: color,
            orientation: cubeOrientation,
            visualSize: cubeVisualSize,
            simulationSpaceMultiplier,
          }
        );

        block.growthState = "empty";
        block.evolutionState = "empty";
        block.galacticRadius = Math.max(0.05, Math.hypot(block.center.x, block.center.y));
        block.galacticAngle = Math.atan2(block.center.y, block.center.x);
        block.heightAbovePlane = block.center.z;
        block.averageVelocity = getOrbitalSpeed(block.galacticRadius, this.config);
        block.angularVelocity = 0;
        this.dustCubeBlocks.set(id, block);
        return block;
      }

      createDustCubeFromPoint(pointIndex) {
        const field = this.dustPointField;

        if (!field || pointIndex < 0 || pointIndex >= field.count) {
          return null;
        }

        const offset = pointIndex * 3;
        const position = new THREE.Vector3(
          field.positions[offset],
          field.positions[offset + 1],
          field.positions[offset + 2]
        );
        const sourceColor = new THREE.Color().setRGB(
          field.colors[offset],
          field.colors[offset + 1],
          field.colors[offset + 2]
        );
        const metadata = this.dustPointMetadata?.[pointIndex] ?? null;
        const orientation = metadata?.orientation ?? null;
        const visualScale = Number.isFinite(Number(metadata?.visualScale)) ? Number(metadata.visualScale) : null;

        return this.createDustCubeBlock(pointIndex, position, sourceColor, null, orientation, visualScale);
      }

      findNearestDustPointOnScreen(event, maxPixelDistance = 14) {
        const field = this.dustPointField;

        if (!field || !this.starPoints || !galaxyStarPointGroup.visible) {
          return -1;
        }

        const rect = renderer.domElement.getBoundingClientRect();
        const targetX = event.clientX - rect.left;
        const targetY = event.clientY - rect.top;
        const projected = new THREE.Vector3();
        const maxDistanceSq = maxPixelDistance * maxPixelDistance;
        let bestIndex = -1;
        let bestDistanceSq = maxDistanceSq;

        for (let i = 0; i < field.count; i++) {
          const offset = i * 3;
          projected.set(field.positions[offset], field.positions[offset + 1], field.positions[offset + 2]);
          projected.project(camera);

          if (
            !Number.isFinite(projected.x) ||
            !Number.isFinite(projected.y) ||
            projected.z < -1 ||
            projected.z > 1
          ) {
            continue;
          }

          const screenX = (projected.x * 0.5 + 0.5) * rect.width;
          const screenY = (-projected.y * 0.5 + 0.5) * rect.height;
          const distanceSq = (screenX - targetX) * (screenX - targetX) + (screenY - targetY) * (screenY - targetY);

          if (distanceSq < bestDistanceSq) {
            bestDistanceSq = distanceSq;
            bestIndex = i;
          }
        }

        return bestIndex;
      }

      findDustCubeForPosition(position, excludeBlock = null) {
        const field = this.dustPointField;

        if (!field || !position) {
          return null;
        }

        const maxDistance = this.getInitialDustCubeSize() * 1.35;
        const maxDistanceSq = maxDistance * maxDistance;
        let bestIndex = -1;
        let bestDistanceSq = maxDistanceSq;
        const candidate = new THREE.Vector3();

        for (let i = 0; i < field.count; i++) {
          if (excludeBlock?.sourcePointIndex === i) {
            continue;
          }

          const offset = i * 3;
          candidate.set(field.positions[offset], field.positions[offset + 1], field.positions[offset + 2]);
          const distanceSq = candidate.distanceToSquared(position);

          if (distanceSq < bestDistanceSq) {
            bestDistanceSq = distanceSq;
            bestIndex = i;
          }
        }

        return bestIndex >= 0 ? this.createDustCubeFromPoint(bestIndex) : null;
      }

      serializeDustCubeBlocks() {
        return Array.from(this.dustCubeBlocks.values()).map(block => ({
          id: block.id,
          sourcePointIndex: block.sourcePointIndex,
          center: block.center.toArray(),
          size: block.isDustCube ? getBlockDisplaySize(block) : block.size,
          visualSize: block.visualSize,
          simulationSpaceMultiplier: getBlockSimulationSpaceMultiplier(block),
          sourceColor: block.sourceColor ? block.sourceColor.toArray() : null,
          orientation: block.orientation ? block.orientation.toArray() : null,
          creationStarted: block.creationStarted,
          systemName: block.systemName,
          systemCoordinate: block.systemCoordinate,
          systemStartedAt: block.systemStartedAt,
        }));
      }

      restoreDustCubeBlocks(records = []) {
        if (!Array.isArray(records)) {
          return;
        }

        for (const record of records) {
          const pointIndex = Number(record?.sourcePointIndex);
          const centerArray = Array.isArray(record?.center) ? record.center.map(Number) : null;

          if (!Number.isFinite(pointIndex) || !centerArray || centerArray.length < 3 || !centerArray.every(Number.isFinite)) {
            continue;
          }

          const colorArray = Array.isArray(record.sourceColor) ? record.sourceColor.map(Number) : null;
          const sourceColor = colorArray && colorArray.length >= 3 && colorArray.every(Number.isFinite)
            ? new THREE.Color().setRGB(colorArray[0], colorArray[1], colorArray[2])
            : null;
          const orientationArray = Array.isArray(record.orientation) ? record.orientation.map(Number) : null;
          const orientation = orientationArray && orientationArray.length >= 4 && orientationArray.every(Number.isFinite)
            ? new THREE.Quaternion(orientationArray[0], orientationArray[1], orientationArray[2], orientationArray[3]).normalize()
            : null;

          const block = this.createDustCubeBlock(
            pointIndex,
            new THREE.Vector3(centerArray[0], centerArray[1], centerArray[2]),
            sourceColor,
            Number(record.size) || null,
            orientation,
            Number(record.visualSize) || null,
            Number(record.simulationSpaceMultiplier) || null
          );

          if (block) {
            block.creationStarted = Boolean(record.creationStarted);
            block.systemName = typeof record.systemName === "string" ? record.systemName : "";
            block.systemCoordinate = typeof record.systemCoordinate === "string" ? record.systemCoordinate : "";
            block.systemStartedAt = typeof record.systemStartedAt === "string" ? record.systemStartedAt : "";
          }
        }
      }

      findBlockById(id, block = this.root) {
        if (this.dustCubeBlocks.has(id)) {
          return this.dustCubeBlocks.get(id);
        }

        if (!block) {
          return null;
        }

        if (block.id === id) {
          return block;
        }

        for (const child of block.children) {
          const found = this.findBlockById(id, child);

          if (found) {
            return found;
          }
        }

        return null;
      }

      selectNearestCentralBlock() {
        let best = null;
        let bestDistance = Infinity;

        for (const block of this.renderBlocks) {
          const distance = block.center.lengthSq();

          if (distance < bestDistance) {
            bestDistance = distance;
            best = block;
          }
        }

        if (best) {
          this.selectBlock(best);
        }
      }

      selectBlock(block) {
        if (!block) {
          return;
        }

        if (this.activeBlock && this.activeBlock !== block) {
          this.collapseActiveBlock();
        }

        cameraPanLocked = false;
        this.selectedBlock = block;
        this.rebuildFocusStarPointField(block);
        this.highlightSelection();
        updateGalaxyStatsDisplay();
      }

      findBlockForObject(object) {
        let current = object;

        while (current) {
          if (current.userData?.blockId) {
            return this.findBlockById(current.userData.blockId);
          }

          current = current.parent;
        }

        return null;
      }

      pickBlock(event) {
        const rect = renderer.domElement.getBoundingClientRect();
        this.pointer.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
        this.pointer.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
        this.raycaster.setFromCamera(this.pointer, camera);
        this.raycaster.params.Points.threshold = Math.max(
          INITIAL_DUST_POINT_PICK_MIN_RADIUS,
          Math.min(INITIAL_DUST_POINT_PICK_MAX_RADIUS, cameraDistance * 0.01)
        );

        const markerHits = this.raycaster.intersectObjects(startedSystemMarkerGroup.children, true);

        for (const hit of markerHits) {
          const block = this.findBlockForObject(hit.object);

          if (block) {
            this.selectBlock(block);
            this.focusSelectedBlock();
            statusEl.textContent = block.systemName
              ? `Selected ${block.systemName} | ${block.systemCoordinate}`
              : `Selected block ${block.id}`;
            return block;
          }
        }

        if (this.starPoints && galaxyStarPointGroup.visible) {
          const pointHits = this.raycaster.intersectObject(this.starPoints, false);
          const pointHit = pointHits.find(hit => Number.isInteger(hit.instanceId) || Number.isInteger(hit.index));
          const pointIndex = Number.isInteger(pointHit?.instanceId)
            ? pointHit.instanceId
            : (Number.isInteger(pointHit?.index) ? pointHit.index : this.findNearestDustPointOnScreen(event));

          if (pointIndex >= 0) {
            const block = this.createDustCubeFromPoint(pointIndex);

            if (block) {
              if (!this.renderBlocks.includes(block)) {
                this.rebuildVisuals();
              }

              this.selectBlock(block);
              this.focusSelectedBlock();
              statusEl.textContent = `Selected empty dust cube ${block.id}`;
              return block;
            }
          }
        }

        const intersections = this.raycaster.intersectObjects(galaxyBlockGroup.children, false);

        for (const hit of intersections) {
          const block = this.findBlockForObject(hit.object);

          if (block) {
            this.selectBlock(block);
            statusEl.textContent = `Selected block ${block.id}`;
            return block;
          }
        }

        return null;
      }

      highlightSelection() {
        clearGalaxyGroup(selectionGroup);

        if (!this.selectedBlock) {
          return;
        }

        const selection = new THREE.LineSegments(
          new THREE.EdgesGeometry(this.blockBoxGeometry),
          new THREE.LineBasicMaterial({
            color: 0xfff17a,
            transparent: true,
            opacity: 0.95,
            depthTest: false,
            depthWrite: false,
          })
        );

        selection.position.copy(this.selectedBlock.center);
        selection.quaternion.copy(this.selectedBlock.orientation);
        selection.scale.setScalar(getBlockOutlineDisplaySize(this.selectedBlock));
        selection.renderOrder = 120;
        selectionGroup.add(selection);
        this.selectionMesh = selection;
      }

      expandSelected() {
        if (!this.selectedBlock) {
          return;
        }

        if (this.growthManager.rules.shouldSubdivideBlock(this.selectedBlock, cameraDistance, true)) {
          this.selectedBlock.expand();
          statusEl.textContent = `Expanded block ${this.selectedBlock.id}`;
        } else {
          statusEl.textContent = `Block ${this.selectedBlock.id} is already at structural detail limit`;
        }
      }

      collapseSelected() {
        const block = this.selectedBlock;

        if (!block) {
          return;
        }

        if (this.activeBlock === block) {
          this.collapseActiveBlock();
        }

        block.collapseToSummary();
        statusEl.textContent = `Collapsed block ${block.id}`;
      }

      activateSelected() {
        if (!this.selectedBlock) {
          statusEl.textContent = "Select a seed cube first";
          return;
        }

        const block = this.selectedBlock;

        if (!block) {
          return;
        }

        if (this.activeBlock && this.activeBlock !== block) {
          this.collapseActiveBlock();
        }

        this.activeBlock = block;
        this.blockFocusLocked = true;
        cameraPanLocked = false;
        block.isActive = true;
        block.isSimulated = true;
        this.markSystemCreationStarted(block, true);
        activeSimulationOrigin.copy(block.center);
        const displaySize = getBlockDisplaySize(block);
        setCameraDistance(block.isDustCube
          ? Math.max(0.08, displaySize * 4.8)
          : Math.max(2.2, Math.min(this.config.lodDistanceBlock * 0.75, displaySize * 1.7)));
        cameraMode = "Active Simulation View";
        this.updateProgressiveCameraFocus(true);
        clearStellarBodies();
        clearTrail();
        resetFromSliders();

        if (!block.restoreEvolutionState() && !block.isDustCube) {
          seedStellarScenario();
        }

        running = true;
        statusEl.textContent = block.systemName
          ? `Simulating ${block.systemName} | ${block.systemCoordinate}`
          : `Simulating block ${block.id}`;
        updateGalaxyStatsDisplay();
      }

      pauseActiveBlock() {
        if (this.activeBlock) {
          this.activeBlock.isSimulated = false;
        }

        running = false;
        statusEl.textContent = "Paused active block";
        updateGalaxyStatsDisplay();
      }

      collapseActiveBlock() {
        if (!this.activeBlock) {
          return;
        }

        this.activeBlock.saveEvolutionState();
        this.activeBlock.isActive = false;
        this.activeBlock.isSimulated = false;
        this.activeBlock.generateSummary();
        this.activeBlock = null;
        running = false;
        clearStellarBodies();
        clearTrail();
        this.rebuildVisuals();
        updateGalaxyStatsDisplay();
      }

      focusSelectedBlock() {
        if (!this.selectedBlock) {
          return;
        }

        const displaySize = getBlockOutlineDisplaySize(this.selectedBlock);
        this.blockFocusLocked = true;
        cameraPanLocked = false;
        setCameraDistance(this.selectedBlock.isDustCube
          ? Math.max(0.08, displaySize * 4.8)
          : Math.max(2.2, displaySize * 1.85));
        cameraMode = "Selected Block View";
        this.updateProgressiveCameraFocus(true);
      }

      returnToGalaxyView() {
        cameraTarget.set(0, 0, 0);
        cameraFocusProgress = 0;
        cameraPanLocked = false;
        this.blockFocusLocked = false;
        cameraAzim = 0.72;
        cameraElev = 1.24;
        setCameraDistance(Math.max(32, this.config.radius * 2.25, this.config.lodDistanceGalaxy + 10));
        cameraMode = "Galaxy View";
        updateCamera();
      }

      getBuildFocusBlock() {
        return this.activeBlock || this.selectedBlock;
      }

      updateProgressiveCameraFocus(immediate = false) {
        const focusBlock = this.getBuildFocusBlock();
        const origin = galacticCenter;

        if (cameraPanLocked) {
          cameraMode = "Panned View";
          updateCamera();
          return;
        }

        if (!focusBlock) {
          cameraFocusProgress = 0;
          cameraTarget.lerp(origin, immediate ? 1 : 0.12);
          cameraMode = "Galaxy View";
          updateCamera();
          return;
        }

        const focusStart = Math.max(this.config.lodDistanceGalaxy * 1.12, this.config.radius * 1.15);
        const focusSize = getBlockOutlineDisplaySize(focusBlock);
        const focusEnd = focusBlock.isDustCube
          ? Math.max(0.08, focusSize * 4.2)
          : Math.max(1.4, Math.min(this.config.lodDistanceBlock * 0.72, focusBlock.size * 1.15));
        const rawProgress = clamp01((focusStart - cameraDistance) / Math.max(0.001, focusStart - focusEnd));
        cameraFocusProgress = rawProgress * rawProgress * (3 - rawProgress * 2);
        const targetProgress = this.blockFocusLocked || this.activeBlock === focusBlock ? 1 : cameraFocusProgress;
        const focusTarget = origin.clone().lerp(focusBlock.center, targetProgress);
        cameraTarget.lerp(focusTarget, immediate ? 1 : 0.2);

        if (!this.blockFocusLocked && cameraFocusProgress < 0.08) {
          cameraMode = "Galaxy View";
        } else if (cameraDistance > this.config.lodDistanceBlock) {
          cameraMode = `Zooming to block ${focusBlock.id}`;
        } else if (this.activeBlock === focusBlock) {
          cameraMode = `Building block ${focusBlock.id}`;
        } else {
          cameraMode = `Selected block ${focusBlock.id}`;
        }

        updateCamera();
      }

      applyGalacticOrbit(block, dt) {
        if (block === this.root || block.isActive || block.isDustCube) {
          return;
        }

        const radius = Math.max(0.08, block.galacticRadius);
        block.galacticAngle += block.angularVelocity * dt;
        const spiralOffset = block.spiralArmOffset;
        block.center.x = Math.cos(block.galacticAngle) * radius + Math.cos(block.galacticAngle * 0.5) * spiralOffset;
        block.center.y = Math.sin(block.galacticAngle) * radius + Math.sin(block.galacticAngle * 0.5) * spiralOffset;
        block.center.z = THREE.MathUtils.lerp(block.center.z, block.heightAbovePlane * 0.82, 0.002);
        updateBlockBounds(block);
      }

      getProgressiveOutlineOpacity(block, isFocusBlock = false) {
        if (block === this.selectedBlock) {
          return 0;
        }

        if (!block.isDustCube) {
          return 0;
        }

        if (isFocusBlock) {
          return 0.35;
        }

        if (this.config.showBlocks) {
          return 0.2;
        }

        const fadeStart = Math.max(this.config.lodDistanceBlock * 2.2, this.config.radius * 0.75);
        const fadeEnd = Math.max(0.9, this.config.lodDistanceBlock * 0.55);
        const zoomProgress = clamp01((fadeStart - cameraDistance) / Math.max(0.001, fadeStart - fadeEnd));

        if (zoomProgress <= 0) {
          return 0;
        }

        return 0.08 + zoomProgress * 0.18;
      }

      updateBlockObjectPositions() {
        const focusBlock = this.getBuildFocusBlock();

        for (const block of this.renderBlocks) {
          const displaySize = getBlockDisplaySize(block);
          const outlineDisplaySize = getBlockOutlineDisplaySize(block);

          if (block.summaryVisual) {
            block.summaryVisual.visible = this.config.showDensityClouds;
            block.summaryVisual.position.copy(block.center);
            block.summaryVisual.scale.setScalar(displaySize * (0.18 + block.gasDensity * 0.08));
          }

          if (block.pickMesh) {
            block.pickMesh.position.copy(block.center);
            block.pickMesh.quaternion.copy(block.orientation);
            block.pickMesh.scale.setScalar(outlineDisplaySize);
            block.pickMesh.visible = true;
          }

          if (block.outlineMesh) {
            const isFocusBlock = block === focusBlock || block === this.selectedBlock;
            const outlineOpacity = this.getProgressiveOutlineOpacity(block, isFocusBlock);
            block.outlineMesh.visible = outlineOpacity > 0.001;
            block.outlineMesh.position.copy(block.center);
            block.outlineMesh.quaternion.copy(block.orientation);
            block.outlineMesh.scale.setScalar(outlineDisplaySize);

            if (block.outlineMesh.material) {
              block.outlineMesh.material.opacity = outlineOpacity;

              if (block === this.selectedBlock) {
                block.outlineMesh.material.color.setHex(0xfff17a);
              } else if (block.isDustCube && block.sourceColor) {
                block.outlineMesh.material.color.copy(block.sourceColor);
              } else {
                block.outlineMesh.material.color.setHex(0xffffff);
              }
            }
          }

          if (block.isDustCube && block.systemMarker) {
            this.updateStartedSystemMarker(block, displaySize);
          }
        }

        if (this.starPoints?.material) {
          this.starPoints.material.opacity = 0.62 + Math.sin(performance.now() * 0.0022) * 0.18;
        }

        if (this.focusStarPoints && focusBlock && this.focusStarBlockId !== focusBlock.id) {
          this.rebuildFocusStarPointField(focusBlock);
        }

        if (this.focusStarPoints && focusBlock) {
          this.focusStarPoints.position.copy(focusBlock.center);

          if (this.focusStarPoints.material) {
            this.focusStarPoints.material.opacity = 0.94;
          }
        }

        if (this.selectionMesh && this.selectedBlock) {
          this.selectionMesh.position.copy(this.selectedBlock.center);
          this.selectionMesh.quaternion.copy(this.selectedBlock.orientation);
          this.selectionMesh.scale.setScalar(getBlockOutlineDisplaySize(this.selectedBlock));

          if (this.selectionMesh.material) {
            this.selectionMesh.material.opacity = 0.95;
          }
        }
      }

      update(dt, config) {
        const key = getGalaxyConfigKey(config);

        if (key !== this.configKey) {
          this.reset(config);
          return;
        }

        this.config = config;
        this.subdivisionController.massDistribution.config = config;
        this.growthManager.refreshConfig(config);

        let growthChanged = this.growthManager.update(dt);

        if (
          config.blocks.autoSubdivideSelected &&
          this.selectedBlock &&
          !this.selectedBlock.isExpanded &&
          this.growthManager.rules.shouldSubdivideBlock(this.selectedBlock, cameraDistance, true)
        ) {
          this.selectedBlock.expand();
          growthChanged = true;
        }

        for (const block of this.renderBlocks) {
          block.renderLOD(cameraDistance);
          this.applyGalacticOrbit(block, dt);
        }

        this.updateProgressiveCameraFocus(false);

        const focusBlock = this.getBuildFocusBlock();
        const focusScreenSize = measureBlockScreenSize(focusBlock);
        focusedBlockScreenWidth = focusScreenSize.width;
        focusedBlockScreenHeight = focusScreenSize.height;
        focusedBlockScreenPixels = focusScreenSize.pixels;
        const focusBlockHasReadableDetail = Boolean(focusBlock) && focusedBlockScreenPixels >= BLOCK_DETAIL_MIN_SCREEN_PX;

        currentLODMode = !focusBlock && cameraDistance > config.lodDistanceGalaxy
          ? "galaxy"
          : (this.activeBlock
            ? (focusBlockHasReadableDetail ? "system" : "cluster")
            : (this.selectedBlock ? (focusBlockHasReadableDetail ? "block" : "cluster") : "block"));

        const isGalaxyOverview = currentLODMode === "galaxy";

        const showActiveDetail = config.showActiveBlockDetail &&
          Boolean(this.activeBlock) &&
          focusBlockHasReadableDetail;
        const showFocusStarField = config.showStarPoints &&
          Boolean(this.selectedBlock) &&
          !this.selectedBlock.isDustCube;
        const activeDustCubeDetail = Boolean(this.activeBlock?.isDustCube && focusBlockHasReadableDetail);

        galaxyGroup.visible = true;
        galaxyCloudGroup.visible = config.showDensityClouds && isGalaxyOverview;
        galaxyBlockGroup.visible = true;
        galaxyStarPointGroup.visible = config.showStarPoints && !activeDustCubeDetail;
        focusStarPointGroup.visible = showFocusStarField;
        selectionGroup.visible = Boolean(this.selectedBlock);
        startedSystemMarkerGroup.visible = true;
        stellarGroup.visible = showActiveDetail;
        orbitGroup.visible = showActiveDetail;
        bodyTrailGroup.visible = showActiveDetail;
        parentLinkGroup.visible = showActiveDetail;
        referenceGroup.visible = showActiveDetail;
        rodLine.visible = showActiveDetail;
        bob1Mesh.visible = showActiveDetail;
        bob2Mesh.visible = showActiveDetail;
        trailLine.visible = showActiveDetail;
        updateReferenceTransform();

        if (!showActiveDetail) {
          galacticPlaneMesh.visible = false;
        }

        galacticCenterMesh.visible = true;

        if (
          config.autoCollapseDistantBlocks &&
          this.activeBlock &&
          !running &&
          cameraDistance > config.lodDistanceGalaxy * 1.25 &&
          cameraFocusProgress < 0.02
        ) {
          this.collapseActiveBlock();
        }

        if (growthChanged) {
          this.growthVisualRefreshTimer -= dt;

          if (this.growthVisualRefreshTimer <= 0) {
            this.rebuildStarPointField();
            this.growthVisualRefreshTimer = 1.2;
          }
        }

        this.updateBlockObjectPositions();
        updateGalaxyStatsDisplay();
      }

      saveState() {
        const evolvedBlocks = {};
        const blockGrowthState = {};
        const expandedBlockIds = [];

        const collect = block => {
          blockGrowthState[block.id] = createBlockGrowthSnapshot(block);

          if (block !== this.root && block.isExpanded) {
            expandedBlockIds.push(block.id);
          }

          if (block.savedDetailState) {
            evolvedBlocks[block.id] = block.savedDetailState;
          }

          for (const child of block.children) {
            collect(child);
          }
        };

        if (this.activeBlock) {
          this.activeBlock.saveEvolutionState();
        }

        collect(this.root);

        for (const block of this.dustCubeBlocks.values()) {
          blockGrowthState[block.id] = createBlockGrowthSnapshot(block);

          if (block.savedDetailState) {
            evolvedBlocks[block.id] = block.savedDetailState;
          }
        }

        return {
          config: getGalaxyConfig(),
          expandedBlockIds,
          dustCubes: this.serializeDustCubeBlocks(),
          evolvedBlocks,
          blockGrowthState,
          growthStats: { ...this.growthStats },
          selectedBlockId: this.selectedBlock?.id ?? null,
          activeBlockId: this.activeBlock?.id ?? null,
          savedAt: new Date().toISOString(),
        };
      }

      loadState(state) {
        if (!state?.config) {
          return;
        }

        setSliderValue("galaxySeed", state.config.seed);
        setSliderValue("totalTargetStars", state.config.totalTargetStars);
        setSliderValue("galaxyRadius", state.config.radius);
        setSliderValue("discThickness", state.config.discThickness);
        setSliderValue("blockSize", state.config.blockSize);
        setSliderValue("maxOctreeDepth", state.config.maxOctreeDepth);
        setSliderValue("starsPerRenderBatch", state.config.starsPerRenderBatch);
        setSliderValue("spiralArmCount", state.config.galaxyShape.armCount);
        setSliderValue("spiralTightness", state.config.galaxyShape.armTightness);
        setSliderValue("bulgeMass", state.config.galaxyShape.bulgeMass);
        setSliderValue("discMass", state.config.galaxyShape.discMass);
        setSliderValue("haloMass", state.config.galaxyShape.haloMass);
        setSliderValue("blackHoleMass", state.config.blackHoleMass);
        setToggleValue("enablePhysicalGrowth", state.config.growth?.enabled ?? true);
        setToggleValue("externalGasInflowEnabled", state.config.growth?.externalInflowEnabled ?? false);
        setToggleValue("pendulumMatterCreationEnabled", state.config.growth?.pendulumMatterCreationEnabled ?? true);
        this.reset(getGalaxyConfig());
        this.restoreDustCubeBlocks(state.dustCubes);

        const expandedIds = Array.isArray(state.expandedBlockIds)
          ? [...state.expandedBlockIds].sort((a, b) => a.split(".").length - b.split(".").length)
          : [];

        for (const id of expandedIds) {
          const block = this.findBlockById(id);

          if (block && block.level < this.config.maxOctreeDepth) {
            block.isExpanded = true;
            block.subdivide();
          }
        }

        for (const [id, snapshot] of Object.entries(state.blockGrowthState ?? {})) {
          const block = this.findBlockById(id);

          if (block) {
            applyBlockGrowthSnapshot(block, snapshot);
          }
        }

        for (const [id, summary] of Object.entries(state.evolvedBlocks ?? {})) {
          const block = this.findBlockById(id);

          if (block) {
            block.savedDetailState = summary;
            block.generateSummary();
          }
        }

        for (const block of this.dustCubeBlocks.values()) {
          if (
            block.creationStarted ||
            block.savedDetailState ||
            block.totalMass > 0 ||
            block.starCount > 0 ||
            block.planetCount > 0
          ) {
            this.markSystemCreationStarted(block, false);
          }
        }

        if (state.growthStats) {
          this.growthStats = {
            externalMassAdded: Number(state.growthStats.externalMassAdded) || 0,
            physicalGrowthMass: Number(state.growthStats.physicalGrowthMass) || 0,
            pendulumCreatedMass: Number(state.growthStats.pendulumCreatedMass) || 0,
          };
        }

        const selected = state.selectedBlockId ? this.findBlockById(state.selectedBlockId) : null;

        if (selected) {
          this.selectBlock(selected);
        }

        this.rebuildVisuals();
      }
    }

    const stellarSphereGeometry = new THREE.SphereGeometry(1, 28, 16);
    const gasSphereGeometry = new THREE.SphereGeometry(1, 10, 6);
    const fragmentGeometry = new THREE.TetrahedronGeometry(1, 0);
    const supernovaGeometry = new THREE.SphereGeometry(1, 24, 12);

    function isStarLike(body) {
      return STAR_TYPES.has(body.type);
    }

    function isCompactObject(body) {
      return COMPACT_TYPES.has(body.type);
    }

    function stellarColorForBody(body) {
      if (body.color) {
        return body.color.clone ? body.color.clone() : new THREE.Color(body.color);
      }

      if (body.type === "blackHole") {
        return new THREE.Color(0x000000);
      }

      if (body.type === "whiteDwarf") {
        return new THREE.Color(0xdaf8ff);
      }

      if (body.type === "neutronStar") {
        return new THREE.Color(0x80aaff);
      }

      if (body.type === "unstableStar") {
        return new THREE.Color(0xff5a2a);
      }

      if (body.type === "redGiant") {
        return new THREE.Color(0xff8a3d);
      }

      if (body.type === "star") {
        const temperature = Math.max(0, Math.min(1, (body.temperature - 4500) / 6500));
        return new THREE.Color().setHSL(0.12 - temperature * 0.04, 0.95, 0.58 + temperature * 0.18);
      }

      if (body.type === "planet") {
        return new THREE.Color().setHSL((body.id * 0.173) % 1, 0.72, 0.58);
      }

      if (body.type === "roguePlanet") {
        return new THREE.Color().setHSL((body.id * 0.173 + 0.08) % 1, 0.62, 0.46);
      }

      if (body.type === "moon") {
        return new THREE.Color().setHSL((body.id * 0.071) % 1, 0.18, 0.58);
      }

      if (body.type === "gas") {
        return new THREE.Color().setHSL((body.id * 0.113 + stellarClock * 0.02) % 1, 0.9, 0.68);
      }

      if (body.type === "fragment") {
        return new THREE.Color(0xffffff);
      }

      if (body.type === "supernovaEvent") {
        return new THREE.Color(0xfff2b8);
      }

      return new THREE.Color().setHSL((body.id * 0.137) % 1, 0.94, 0.62);
    }

    function bodyRadiusFromMass(type, mass) {
      const config = getStellarConfig();
      const safeMass = Math.max(0.1, mass);
      let radius;

      if (type === "blackHole") {
        radius = 0.018 * Math.cbrt(safeMass / 100) * config.compactObjectVisualScale;
      } else if (type === "whiteDwarf") {
        radius = 0.014 * Math.cbrt(safeMass / 100) * config.compactObjectVisualScale;
      } else if (type === "neutronStar") {
        radius = 0.01 * Math.cbrt(safeMass / 100) * config.compactObjectVisualScale;
      } else if (type === "star" || type === "unstableStar" || type === "redGiant") {
        radius = 0.12 * Math.cbrt(safeMass / 100);
      } else if (type === "planet" || type === "roguePlanet") {
        radius = 0.065 * Math.cbrt(safeMass / 45);
      } else if (type === "moon") {
        radius = 0.035 * Math.cbrt(safeMass / 8);
      } else if (type === "gas") {
        radius = 0.026 * Math.cbrt(safeMass / 4);
      } else if (type === "fragment") {
        radius = 0.022 * Math.cbrt(safeMass / 3);
      } else if (type === "supernovaEvent") {
        radius = 0.08 * Math.cbrt(safeMass);
      } else {
        radius = 0.045 * Math.cbrt(safeMass / 12);
      }

      return Math.max(0.012, Math.min(0.9, radius));
    }

    function bodyLuminosity(body) {
      if (body.type === "blackHole") {
        return 0;
      }

      if (body.type === "neutronStar") {
        return 2.8 + body.spin * 0.12;
      }

      if (body.type === "whiteDwarf") {
        return 1.4;
      }

      if (body.type === "unstableStar") {
        return 2.0 + body.instability * 1.8;
      }

      if (body.type === "star" || body.type === "redGiant") {
        return 1.0 + Math.pow(Math.max(0.2, body.mass / 100), 1.25);
      }

      return body.type === "blob" ? 0.55 : 0.08;
    }

    function createPlanetSurfaceTexture(body) {
      const canvas = document.createElement("canvas");
      canvas.width = 256;
      canvas.height = 128;
      const ctx = canvas.getContext("2d");
      const seed = body.surfaceSeed ?? seededUnit(`surface:${body.id}`);
      const baseColor = stellarColorForBody(body);
      const baseHsl = { h: 0, s: 0, l: 0 };
      baseColor.getHSL(baseHsl);

      ctx.clearRect(0, 0, canvas.width, canvas.height);

      if (body.type === "moon") {
        const moonLightness = 46 + Math.round(seed * 14);
        ctx.fillStyle = `hsl(42, 8%, ${moonLightness}%)`;
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        for (let i = 0; i < 54; i++) {
          const x = seededRange(`moon-crater-x:${body.id}:${i}:${seed}`, 0, canvas.width);
          const y = seededRange(`moon-crater-y:${body.id}:${i}:${seed}`, 0, canvas.height);
          const radius = seededRange(`moon-crater-r:${body.id}:${i}:${seed}`, 1.6, 9.5);
          const shade = 34 + Math.round(seededUnit(`moon-crater-shade:${body.id}:${i}:${seed}`) * 22);
          ctx.beginPath();
          ctx.fillStyle = `hsl(45, 7%, ${shade}%)`;
          ctx.arc(x, y, radius, 0, Math.PI * 2);
          ctx.fill();
          ctx.beginPath();
          ctx.strokeStyle = `hsl(45, 10%, ${Math.min(72, shade + 28)}%)`;
          ctx.lineWidth = 0.8;
          ctx.arc(x - radius * 0.12, y - radius * 0.12, radius * 0.78, 0, Math.PI * 2);
          ctx.stroke();
        }
      } else {
        const gasGiant = body.mass > 85 || seededUnit(`gas-giant:${body.id}:${seed}`) > 0.45;
        const hue = (baseHsl.h + seededRange(`planet-hue:${body.id}:${seed}`, -0.04, 0.04) + 1) % 1;
        const saturation = Math.min(92, Math.max(42, Math.round((baseHsl.s * 100) + 16)));
        const lightness = Math.min(76, Math.max(38, Math.round((baseHsl.l * 100) + 4)));
        const bg = ctx.createLinearGradient(0, 0, 0, canvas.height);
        bg.addColorStop(0, `hsl(${Math.round(hue * 360)}, ${saturation}%, ${Math.min(82, lightness + 16)}%)`);
        bg.addColorStop(0.45, `hsl(${Math.round(hue * 360)}, ${saturation}%, ${lightness}%)`);
        bg.addColorStop(1, `hsl(${Math.round(hue * 360)}, ${Math.max(32, saturation - 14)}%, ${Math.max(26, lightness - 18)}%)`);
        ctx.fillStyle = bg;
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        if (gasGiant) {
          for (let i = 0; i < 18; i++) {
            const y = (i / 18) * canvas.height + seededRange(`band-y:${body.id}:${i}:${seed}`, -3, 3);
            const height = seededRange(`band-h:${body.id}:${i}:${seed}`, 5, 13);
            const bandHue = (hue + seededRange(`band-hue:${body.id}:${i}:${seed}`, -0.07, 0.08) + 1) % 1;
            const bandSat = Math.max(28, Math.min(96, saturation + seededRange(`band-sat:${body.id}:${i}:${seed}`, -22, 18)));
            const bandLight = Math.max(24, Math.min(88, lightness + seededRange(`band-light:${body.id}:${i}:${seed}`, -18, 20)));
            ctx.globalAlpha = seededRange(`band-alpha:${body.id}:${i}:${seed}`, 0.32, 0.68);
            ctx.fillStyle = `hsl(${Math.round(bandHue * 360)}, ${Math.round(bandSat)}%, ${Math.round(bandLight)}%)`;
            ctx.fillRect(0, y, canvas.width, height);
          }

          for (let i = 0; i < 5; i++) {
            const x = seededRange(`storm-x:${body.id}:${i}:${seed}`, 32, 224);
            const y = seededRange(`storm-y:${body.id}:${i}:${seed}`, 32, 96);
            const rx = seededRange(`storm-rx:${body.id}:${i}:${seed}`, 7, 22);
            const ry = seededRange(`storm-ry:${body.id}:${i}:${seed}`, 3, 9);
            ctx.globalAlpha = seededRange(`storm-alpha:${body.id}:${i}:${seed}`, 0.16, 0.36);
            ctx.fillStyle = `hsl(${Math.round(((hue + 0.08) % 1) * 360)}, ${Math.min(96, saturation + 8)}%, ${Math.min(86, lightness + 20)}%)`;
            ctx.beginPath();
            ctx.ellipse(x, y, rx, ry, seededRange(`storm-rot:${body.id}:${i}:${seed}`, -0.4, 0.4), 0, Math.PI * 2);
            ctx.fill();
          }
        } else {
          for (let i = 0; i < 24; i++) {
            const x = seededRange(`continent-x:${body.id}:${i}:${seed}`, -20, canvas.width + 20);
            const y = seededRange(`continent-y:${body.id}:${i}:${seed}`, -8, canvas.height + 8);
            const rx = seededRange(`continent-rx:${body.id}:${i}:${seed}`, 8, 34);
            const ry = seededRange(`continent-ry:${body.id}:${i}:${seed}`, 3, 16);
            const landHue = (hue + seededRange(`continent-hue:${body.id}:${i}:${seed}`, 0.1, 0.28)) % 1;
            ctx.globalAlpha = seededRange(`continent-alpha:${body.id}:${i}:${seed}`, 0.2, 0.52);
            ctx.fillStyle = `hsl(${Math.round(landHue * 360)}, ${Math.max(32, saturation - 18)}%, ${Math.max(30, lightness - 8)}%)`;
            ctx.beginPath();
            ctx.ellipse(x, y, rx, ry, seededRange(`continent-rot:${body.id}:${i}:${seed}`, -0.8, 0.8), 0, Math.PI * 2);
            ctx.fill();
          }

          ctx.globalAlpha = 0.22;
          ctx.strokeStyle = "rgba(255, 255, 255, 0.85)";
          ctx.lineWidth = 1.2;

          for (let i = 0; i < 12; i++) {
            const y = seededRange(`cloud-y:${body.id}:${i}:${seed}`, 16, 112);
            ctx.beginPath();
            ctx.moveTo(0, y);

            for (let x = 0; x <= canvas.width; x += 18) {
              const offset = Math.sin(x * 0.035 + i + seed * 12) * seededRange(`cloud-a:${body.id}:${i}:${seed}`, 2, 7);
              ctx.lineTo(x, y + offset);
            }

            ctx.stroke();
          }
        }
      }

      ctx.globalAlpha = 1;
      const texture = new THREE.CanvasTexture(canvas);
      texture.colorSpace = THREE.SRGBColorSpace;
      texture.wrapS = THREE.RepeatWrapping;
      texture.wrapT = THREE.ClampToEdgeWrapping;
      texture.needsUpdate = true;
      return texture;
    }

    function createBodyMaterial(body) {
      const color = stellarColorForBody(body);

      if (body.type === "blackHole") {
        return new THREE.MeshBasicMaterial({
          color: 0x000000,
          transparent: false,
          depthWrite: true,
        });
      }

      if (body.type === "gas" || body.type === "supernovaEvent") {
        return new THREE.MeshBasicMaterial({
          color,
          transparent: true,
          opacity: body.type === "supernovaEvent" ? 0.82 : 0.42,
          blending: THREE.AdditiveBlending,
          depthWrite: false,
        });
      }

      if (PLANET_TYPES.has(body.type) || body.type === "moon") {
        const material = new THREE.MeshStandardMaterial({
          color: 0xffffff,
          map: createPlanetSurfaceTexture(body),
          emissive: color.clone().multiplyScalar(body.type === "moon" ? 0.015 : 0.035),
          roughness: body.type === "moon" ? 0.86 : 0.68,
          metalness: 0.0,
        });

        material.userData.surfaceBody = true;
        return material;
      }

      const emissiveStrength = isStarLike(body) ? 0.75 : (body.type === "blob" ? 0.32 : 0.02);
      const emissive = color.clone().multiplyScalar(emissiveStrength);

      return new THREE.MeshStandardMaterial({
        color,
        emissive,
        roughness: body.type === "fragment" ? 0.6 : 0.34,
        metalness: body.type === "fragment" ? 0.1 : 0.0,
      });
    }

    function createBodyMesh(body) {
      let geometry = stellarSphereGeometry;

      if (body.type === "gas") {
        geometry = gasSphereGeometry;
      } else if (body.type === "fragment") {
        geometry = fragmentGeometry;
      } else if (body.type === "supernovaEvent") {
        geometry = supernovaGeometry;
      }

      const mesh = new THREE.Mesh(geometry, createBodyMaterial(body));
      mesh.position.copy(body.position);
      mesh.scale.setScalar(body.radius);

      if (body.type === "blackHole") {
        const glow = new THREE.Mesh(
          new THREE.SphereGeometry(1, 32, 16),
          new THREE.MeshBasicMaterial({
            color: 0xffffff,
            transparent: true,
            opacity: 0.18,
            blending: THREE.AdditiveBlending,
            depthWrite: false,
          })
        );

        glow.name = "black-hole-glow";
        glow.scale.setScalar(1.75);
        mesh.add(glow);
      }

      if (body.type === "star" || body.type === "unstableStar" || body.type === "redGiant") {
        const halo = new THREE.Mesh(
          new THREE.SphereGeometry(1, 24, 12),
          new THREE.MeshBasicMaterial({
            color: stellarColorForBody(body),
            transparent: true,
            opacity: body.type === "unstableStar" ? 0.32 : 0.22,
            blending: THREE.AdditiveBlending,
            depthWrite: false,
          })
        );

        halo.name = "star-halo";
        halo.scale.setScalar(body.type === "redGiant" ? 2.2 : 1.85);
        mesh.add(halo);
      }

      if (body.type === "neutronStar") {
        const beamGeometry = new THREE.BufferGeometry();
        beamGeometry.setAttribute(
          "position",
          new THREE.Float32BufferAttribute([
            0, 0, -2.4,
            0, 0, 2.4,
          ], 3)
        );

        const beam = new THREE.Line(
          beamGeometry,
          new THREE.LineBasicMaterial({
            color: 0xb9d4ff,
            transparent: true,
            opacity: 0.75,
            blending: THREE.AdditiveBlending,
          })
        );

        beam.name = "pulsar-beam";
        mesh.add(beam);
      }

      return mesh;
    }

    function trimStellarBodies() {
      while (stellarBodies.length > MAX_STELLAR_BODIES) {
        let smallestIndex = -1;
        let smallestScore = Infinity;

        for (let i = 0; i < stellarBodies.length; i++) {
          const body = stellarBodies[i];
          let score = body.mass;

          if (isStarLike(body) || PLANET_TYPES.has(body.type) || body.type === "moon") {
            score += 10000;
          }

          if (score < smallestScore) {
            smallestScore = score;
            smallestIndex = i;
          }
        }

        if (smallestIndex < 0) {
          return;
        }

        removeBody(stellarBodies[smallestIndex]);
      }
    }

    function scaledBodyRadius(type, mass, scaleValue) {
      const radiusScale = Math.max(0.00001, Number(scaleValue) || 1);

      return Math.max(0.001, bodyRadiusFromMass(type, mass) * radiusScale);
    }

    function createBody(type, options = {}) {
      const position = options.position ? options.position.clone() : randomUnitVector().multiplyScalar(0.5);
      const velocity = options.velocity ? options.velocity.clone() : randomUnitVector().multiplyScalar(0.02);
      const mass = Math.max(0.05, options.mass ?? 10);
      const id = nextBodyId++;
      const radiusScale = bodyRadiusScaleFromContext(options);
      const radius = scaledBodyRadius(type, mass, radiusScale);
      const color = options.color ? (options.color.clone ? options.color.clone() : new THREE.Color(options.color)) : null;
      const parent = options.parent ?? null;
      const defaultOrbitType = isStarLike({ type })
        ? "galactic"
        : (type === "moon" ? "planetary" : (type === "planet" ? "stellar" : (type === "roguePlanet" ? "rogue" : "free")));

      const body = {
        id,
        type,
        mass,
        radius,
        radiusScale,
        position,
        velocity,
        spin: options.spin ?? Math.random() * 0.7,
        temperature: options.temperature ?? (type === "star" ? 5600 : 2200),
        luminosity: options.luminosity ?? 0,
        age: options.age ?? 0,
        fuel: options.fuel ?? 1,
        instability: options.instability ?? 0,
        metallicity: options.metallicity ?? 1,
        remnantType: options.remnantType ?? null,
        parentSystem: options.parentSystem ?? null,
        parentSystemId: options.parentSystemId ?? (options.parentSystem?.id ?? null),
        localPosition: options.localPosition ? options.localPosition.clone() : null,
        localVelocity: options.localVelocity ? options.localVelocity.clone() : null,
        parent,
        parentId: options.parentId ?? (parent ? parent.id : null),
        orbitType: options.orbitType ?? defaultOrbitType,
        captureState: options.captureState ?? (parent ? "stableOrbit" : "free"),
        orbitElements: cloneOrbitElements(options.orbitElements),
        orbitParentId: options.orbitParentId ?? (options.orbitElements?.parentId ?? (parent ? parent.id : null)),
        orbitLockedThisFrame: false,
        alive: true,
        collisionHistory: [],
        absorbedMassHistory: [],
        angularMomentum: options.angularMomentum ? options.angularMomentum.clone() : randomUnitVector().multiplyScalar(0.02),
        spinAxis: options.spinAxis ? options.spinAxis.clone().normalize() : randomUnitVector().normalize(),
        stableOrbitAge: options.stableOrbitAge ?? 0,
        protectedUntil: options.protectedUntil ?? (stellarClock + (options.protectedAge ?? 0)),
        capturedDebrisMass: options.capturedDebrisMass ?? 0,
        pendingParentSwitch: null,
        lastMajorCollisionTime: options.lastMajorCollisionTime ?? -Infinity,
        ttl: options.ttl ?? Infinity,
        createdAt: stellarClock,
        trail: [],
        trailLine: null,
        trailCapacity: 0,
        lastTrailSampleTime: -Infinity,
        ring: options.ring ?? null,
        ringCandidate: typeof options.ringCandidate === "boolean" ? options.ringCandidate : null,
        surfaceSeed: Number.isFinite(Number(options.surfaceSeed))
          ? Number(options.surfaceSeed)
          : seededUnit(`${type}:${id}:${mass.toFixed(3)}:${position.x.toFixed(3)}:${position.y.toFixed(3)}:${position.z.toFixed(3)}`),
        visualType: null,
        color,
        mesh: null,
      };

      body.luminosity = bodyLuminosity(body);
      body.mesh = createBodyMesh(body);
      body.visualType = body.type;
      stellarGroup.add(body.mesh);
      stellarBodies.push(body);
      trimStellarBodies();
      return body;
    }

    function removeBody(body) {
      if (!body || !body.alive) {
        return;
      }

      if (body.parentSystemId && starSystemContainers.has(body.parentSystemId)) {
        const system = starSystemContainers.get(body.parentSystemId);
        system.bodies = system.bodies.filter(member => member !== body);
      }

      body.parentSystem = null;
      body.parentSystemId = null;
      body.localPosition = null;
      body.localVelocity = null;
      body.alive = false;
      const index = stellarBodies.indexOf(body);

      if (index >= 0) {
        stellarBodies.splice(index, 1);
      }

      if (body.mesh) {
        stellarGroup.remove(body.mesh);
        disposeObjectMaterials(body.mesh);
      }

      if (body.trailLine) {
        bodyTrailGroup.remove(body.trailLine);
        body.trailLine.geometry.dispose();
        body.trailLine.material.dispose();
        body.trailLine = null;
      }

      if (body.ring?.mesh) {
        disposeRingMesh(body.ring);
      }
    }

    function clearStarSystemContainers() {
      for (const system of starSystemContainers.values()) {
        disposeStarSystemVisuals(system);
      }

      starSystemContainers.clear();
      nextStarSystemId = 1;
      systemTravelStats.lastPromotedSystem = "none";
      systemTravelStats.lastDestabilisedSystem = "none";
      systemTravelStats.selectedSystemId = "none";
    }

    function clearStellarBodies() {
      while (stellarBodies.length > 0) {
        removeBody(stellarBodies[0]);
      }

      recentStellarEvents.length = 0;
      stellarStats.lastEvent = "none";
      stellarStats.highestInstability = 0;
      stellarStats.largestMass = 0;
      clearStarSystemContainers();
      clearGroup(orbitGroup);
      clearGroup(parentLinkGroup);
      updateStellarStatsDisplay();
    }

    function recordStellarEvent(text) {
      stellarStats.lastEvent = text;
      recentStellarEvents.push({
        time: stellarClock,
        text,
      });

      while (recentStellarEvents.length > 12) {
        recentStellarEvents.shift();
      }
    }

    function classifyBodyAfterGrowth(body, config) {
      if (isStarLike(body)) {
        return;
      }

      const planetCanCollapseToStar =
        body.type === "roguePlanet" &&
        body.mass >= config.lowMassStarMax * 1.7 &&
        body.age >= config.planetFormation.minimumPlanetFormationAge * 2;

      if (body.mass >= config.lowMassStarMax && (!PLANET_TYPES.has(body.type) || planetCanCollapseToStar) && body.type !== "moon") {
        body.type = "star";
        body.parent = null;
        body.parentId = null;
        body.orbitType = "galactic";
        body.captureState = "stableOrbit";
        clearOrbitElements(body);
        body.fuel = Math.max(body.fuel, 0.9);
        body.temperature = 5400 + Math.min(5200, body.mass * 8);
        body.luminosity = bodyLuminosity(body);
        body.protectedUntil = stellarClock + config.planetFormation.protectedAge;
        recordStellarEvent(`new star formed (${body.mass.toFixed(0)})`);
      } else if (
        body.mass >= config.planetFormation.planetMassThreshold &&
        body.age >= config.planetFormation.minimumPlanetFormationAge
      ) {
        body.type = body.parent && isStarLike(body.parent) ? "planet" : "roguePlanet";
        body.orbitType = body.type === "planet" ? "stellar" : "rogue";
        body.captureState = body.parent ? "stableOrbit" : "free";
        if (!body.parent) {
          clearOrbitElements(body);
        }
        body.temperature = Math.min(body.temperature, 1600);
        body.protectedUntil = stellarClock + config.planetFormation.protectedAge;
        body.spinAxis.normalize();
      } else {
        body.type = "blob";
        body.orbitType = body.parent ? "stellar" : "free";
        if (!body.parent) {
          clearOrbitElements(body);
        }
      }
    }

    function updateBodyVisual(body, dt) {
      updateBodyVisualType(body);
      body.radius = scaledBodyRadius(body.type, body.mass, body.radiusScale);
      body.luminosity = bodyLuminosity(body);
      body.mesh.position.copy(body.position);

      let pulse = 1;

      if (body.type === "unstableStar") {
        pulse += Math.sin(stellarClock * 8 + body.id) * 0.08 * Math.min(3, body.instability);
      } else if (body.type === "supernovaEvent") {
        const age = stellarClock - body.createdAt;
        pulse = 1 + age * 5;
      } else if (body.type === "blob") {
        pulse += Math.sin(stellarClock * 4 + body.id) * 0.08;
      }

      body.mesh.scale.setScalar(Math.max(0.001, body.radius * pulse));

      if (body.mesh.material) {
        const color = stellarColorForBody(body);

        if (body.mesh.material.color) {
          if (body.mesh.material.userData?.surfaceBody) {
            body.mesh.material.color.set(0xffffff);
          } else {
            body.mesh.material.color.copy(color);
          }
        }

        if (body.mesh.material.emissive) {
          const emissiveScale = body.mesh.material.userData?.surfaceBody
            ? (body.type === "moon" ? 0.012 : 0.035)
            : (body.type === "blob" ? 0.75 : bodyLuminosity(body) * 0.25);
          body.mesh.material.emissive.copy(color).multiplyScalar(emissiveScale);
        }

        if (body.type === "supernovaEvent") {
          body.mesh.material.opacity = Math.max(0, 0.9 - (stellarClock - body.createdAt) * 0.75);
        } else if (body.type === "gas") {
          const config = getStellarConfig();
          const age = stellarClock - body.createdAt;
          const fade = age > config.gas.fadeStartAge
            ? Math.max(0, 1 - (age - config.gas.fadeStartAge) / Math.max(1, body.ttl - config.gas.fadeStartAge))
            : 1;
          body.mesh.material.opacity = config.showGasCloud
            ? Math.max(0.04, Math.min(0.36, config.gas.opacity + body.mass * 0.01) * fade)
            : 0;
        }
      }

      const pulsarBeam = body.mesh.getObjectByName("pulsar-beam");

      if (pulsarBeam) {
        pulsarBeam.visible = getStellarConfig().enablePulsarBeams;
        pulsarBeam.rotation.z += dt * (2 + body.spin);
        pulsarBeam.rotation.x = 0.7;
      }
    }

    function disposeObjectMaterials(object) {
      object.traverse(child => {
        if (child.material) {
          const materials = Array.isArray(child.material) ? child.material : [child.material];

          for (const material of materials) {
            if (material.map) {
              material.map.dispose();
            }

            if (material.alphaMap) {
              material.alphaMap.dispose();
            }

            material.dispose();
          }
        }

        if (child.geometry && child !== object) {
          child.geometry.dispose();
        }
      });
    }

    function updateBodyVisualType(body) {
      if (body.visualType === body.type) {
        return;
      }

      if (body.mesh) {
        stellarGroup.remove(body.mesh);
        disposeObjectMaterials(body.mesh);
      }

      body.radius = scaledBodyRadius(body.type, body.mass, body.radiusScale);
      body.mesh = createBodyMesh(body);
      stellarGroup.add(body.mesh);
      body.visualType = body.type;
    }

    function bodyTrailLimit(body, config) {
      if (body.type === "gas") {
        return 0;
      }

      if (body.type === "fragment") {
        return config.showFragmentTrails ? config.trails.fragments : 0;
      }

      if (body.type === "blob") {
        return config.trails.blobs;
      }

      if (body.type === "moon") {
        return config.showMoonOrbits ? config.trails.moons : 0;
      }

      if (PLANET_TYPES.has(body.type)) {
        return config.showPlanetOrbits ? config.trails.planets : 0;
      }

      if (isStarLike(body)) {
        return config.trails.stars;
      }

      return 0;
    }

    function resetBodyTrail(body) {
      if (!body) {
        return;
      }

      body.trail = [];
      body.trailCapacity = 0;
      body.lastTrailSampleTime = -Infinity;

      if (body.trailLine) {
        bodyTrailGroup.remove(body.trailLine);
        body.trailLine.geometry.dispose();
        body.trailLine.material.dispose();
        body.trailLine = null;
      }
    }

    function shouldBreakBodyTrailSegment(previousPosition, nextPosition) {
      const block = galaxyManager?.activeBlock;

      if (!block?.isDustCube || !previousPosition || !nextPosition) {
        return false;
      }

      const halfSize = getBlockSimulationDisplaySize(block) * 0.5;

      if (halfSize <= 0) {
        return false;
      }

      const previousLocal = blockLocalPositionFromWorld(previousPosition, block);
      const nextLocal = blockLocalPositionFromWorld(nextPosition, block);

      return (
        Math.abs(nextLocal.x - previousLocal.x) > halfSize ||
        Math.abs(nextLocal.y - previousLocal.y) > halfSize ||
        Math.abs(nextLocal.z - previousLocal.z) > halfSize
      );
    }

    function updateBodyTrail(body, config) {
      const limit = Math.round(bodyTrailLimit(body, config));

      if (limit <= 1) {
        resetBodyTrail(body);
        return;
      }

      if (stellarClock - body.lastTrailSampleTime > 0.045) {
        const previousPosition = body.trail[body.trail.length - 1];

        if (shouldBreakBodyTrailSegment(previousPosition, body.position)) {
          resetBodyTrail(body);
        }

        body.trail.push(body.position.clone());
        body.lastTrailSampleTime = stellarClock;
      }

      if (body.trail.length > limit) {
        body.trail = body.trail.slice(body.trail.length - limit);
      }

      if (!body.trailLine || body.trailCapacity !== limit) {
        if (body.trailLine) {
          bodyTrailGroup.remove(body.trailLine);
          body.trailLine.geometry.dispose();
          body.trailLine.material.dispose();
        }

        const geometry = new THREE.BufferGeometry();
        const buffer = new Float32Array(limit * 3);
        const attribute = new THREE.BufferAttribute(buffer, 3);
        attribute.setUsage(THREE.DynamicDrawUsage);
        geometry.setAttribute("position", attribute);
        geometry.setDrawRange(0, body.trail.length);

        const color = stellarColorForBody(body);
        const material = new THREE.LineBasicMaterial({
          color,
          transparent: true,
          opacity: body.type === "fragment" ? 0.26 : (isStarLike(body) ? 0.28 : 0.42),
          depthWrite: false,
        });

        body.trailLine = new THREE.Line(geometry, material);
        bodyTrailGroup.add(body.trailLine);
        body.trailCapacity = limit;
      }

      const positions = body.trailLine.geometry.attributes.position.array;

      for (let i = 0; i < body.trail.length; i++) {
        positions[i * 3 + 0] = body.trail[i].x;
        positions[i * 3 + 1] = body.trail[i].y;
        positions[i * 3 + 2] = body.trail[i].z;
      }

      body.trailLine.geometry.setDrawRange(0, body.trail.length);
      body.trailLine.geometry.attributes.position.needsUpdate = true;
    }

    function clearGroup(group) {
      while (group.children.length > 0) {
        const child = group.children.pop();

        if (child.geometry) {
          child.geometry.dispose();
        }

        if (child.material) {
          child.material.dispose();
        }
      }
    }

    function getPlaneBasis(normal) {
      const n = normal.clone().normalize();
      const helper = Math.abs(n.z) < 0.9 ? new THREE.Vector3(0, 0, 1) : new THREE.Vector3(0, 1, 0);
      const u = new THREE.Vector3().crossVectors(helper, n).normalize();
      const v = new THREE.Vector3().crossVectors(n, u).normalize();
      return { u, v, n };
    }

    function addOrbitLine(center, radius, normal, color, opacity, segments = 96) {
      if (!Number.isFinite(radius) || radius <= 0.02) {
        return;
      }

      const { u, v } = getPlaneBasis(normal);
      const positions = new Float32Array((segments + 1) * 3);

      for (let i = 0; i <= segments; i++) {
        const angle = (i / segments) * Math.PI * 2;
        const point = center.clone()
          .add(u.clone().multiplyScalar(Math.cos(angle) * radius))
          .add(v.clone().multiplyScalar(Math.sin(angle) * radius));

        positions[i * 3 + 0] = point.x;
        positions[i * 3 + 1] = point.y;
        positions[i * 3 + 2] = point.z;
      }

      const geometry = new THREE.BufferGeometry();
      geometry.setAttribute("position", new THREE.BufferAttribute(positions, 3));
      const material = new THREE.LineBasicMaterial({
        color,
        transparent: true,
        opacity,
        depthWrite: false,
      });

      orbitGroup.add(new THREE.Line(geometry, material));
    }

    function addParentLinkLine(child, parent, color, opacity = 0.22) {
      const geometry = new THREE.BufferGeometry();
      geometry.setAttribute(
        "position",
        new THREE.Float32BufferAttribute([
          child.position.x, child.position.y, child.position.z,
          parent.position.x, parent.position.y, parent.position.z,
        ], 3)
      );
      const material = new THREE.LineBasicMaterial({
        color,
        transparent: true,
        opacity,
        depthWrite: false,
      });
      parentLinkGroup.add(new THREE.Line(geometry, material));
    }

    function updateOrbitAndLinkVisuals(config, params) {
      orbitVisualFrame++;

      if (orbitVisualFrame % 8 !== 0) {
        return;
      }

      clearGroup(orbitGroup);
      clearGroup(parentLinkGroup);

      const simulationScale = getSimulationWorldScale(params);
      const reach = getPendulumLocalReach(params) * simulationScale;
      const drawFullOrbitGuides = !galaxyManager?.activeBlock?.isDustCube;
      galacticPlaneMesh.visible = config.galaxyPlane.showPlaneDisc && config.enableGalacticPlaneSettling;
      galacticPlaneMesh.scale.setScalar(reach * 1.85);
      galacticPlaneMesh.quaternion.setFromUnitVectors(new THREE.Vector3(0, 0, 1), galacticNormal.clone().normalize());

      for (const body of stellarBodies) {
        if (!body.alive || body.type === "gas" || body.type === "fragment" || body.type === "blob") {
          continue;
        }

        const parentSystem = body.parentSystemId ? starSystemContainers.get(body.parentSystemId) : null;

        if (parentSystem?.collapsed) {
          continue;
        }

        if (drawFullOrbitGuides && isStarLike(body) && !parentSystem && config.showStarGalacticOrbits) {
          const orbitOrigin = galaxyManager?.activeBlock?.isDustCube ? activeSimulationOrigin : galacticCenter;
          const radius = Math.max(0.12 * simulationScale, body.position.clone().sub(orbitOrigin).length());
          addOrbitLine(orbitOrigin, radius, galacticNormal, 0xffd46d, 0.16, 128);
        } else if (drawFullOrbitGuides && PLANET_TYPES.has(body.type) && body.parent?.alive && config.showPlanetOrbits) {
          const radius = Math.max(0.04 * simulationScale, body.position.distanceTo(body.parent.position));
          addOrbitLine(body.parent.position, radius, galacticNormal, stellarColorForBody(body), body.type === "roguePlanet" ? 0.18 : 0.42, 96);
        } else if (drawFullOrbitGuides && body.type === "moon" && body.parent?.alive && config.showMoonOrbits) {
          const radius = Math.max(0.025 * simulationScale, body.position.distanceTo(body.parent.position));
          addOrbitLine(body.parent.position, radius, body.parent.spinAxis || galacticNormal, stellarColorForBody(body.parent), 0.55, 64);
        }

        if (config.showParentLinks && body.parent?.alive) {
          addParentLinkLine(body, body.parent, body.type === "moon" ? 0x66aaff : 0xffdd66);
        }
      }
    }

    function computeGalacticNormal() {
      const angularMomentum = new THREE.Vector3();
      let contributors = 0;

      for (const body of stellarBodies) {
        if (!body.alive || body.type === "gas" || body.type === "fragment" || body.mass < 18) {
          continue;
        }

        const relativePosition = body.position.clone().sub(galacticCenter);
        const momentum = body.velocity.clone().multiplyScalar(body.mass);
        angularMomentum.add(relativePosition.cross(momentum));
        contributors++;
      }

      if (contributors >= 2 && angularMomentum.lengthSq() > 1e-7) {
        galacticNormal.lerp(angularMomentum.normalize(), 0.025).normalize();
      } else {
        galacticNormal.lerp(defaultGalacticNormal, 0.015).normalize();
      }
    }

    function applyGalacticPlaneAndRotation(body, dt, config) {
      if (body.parentSystemId && config.systemTravel?.preserveLocalPhysics) {
        return;
      }

      if (body.type === "supernovaEvent" || body.type === "moon") {
        return;
      }

      const isLocalDustSimulation = Boolean(galaxyManager?.activeBlock?.isDustCube);
      const planeOrigin = isLocalDustSimulation ? activeSimulationOrigin : galacticCenter;
      const relative = body.position.clone().sub(planeOrigin);

      if (config.enableGalacticPlaneSettling) {
        const distanceFromPlane = relative.dot(galacticNormal);
        const verticalVelocity = body.velocity.dot(galacticNormal);
        let settlingStrength = config.galaxyPlane.settlingStrength;

        if (isStarLike(body)) {
          settlingStrength = config.galaxyPlane.starSettlingStrength;
        } else if (body.type === "gas") {
          settlingStrength = config.galaxyPlane.gasSettlingStrength;
        } else if (body.type === "fragment") {
          settlingStrength = config.galaxyPlane.fragmentSettlingStrength;
        }

        body.velocity.addScaledVector(galacticNormal, -distanceFromPlane * settlingStrength * dt);
        body.velocity.addScaledVector(galacticNormal, -verticalVelocity * config.galaxyPlane.verticalDamping * dt);
      }

      if (isLocalDustSimulation || !config.enableGalacticRotation || relative.lengthSq() < 1e-8) {
        return;
      }

      const radial = relative.clone().projectOnPlane(galacticNormal);

      if (radial.lengthSq() < 1e-8) {
        return;
      }

      const radius = Math.max(config.galaxy.minRadius, radial.length());
      const tangent = new THREE.Vector3().crossVectors(galacticNormal, radial.normalize()).normalize();
      const desiredSpeed = Math.sqrt(config.galaxy.mass / radius) * config.galaxy.rotationScale * 0.025;
      const currentTangentialSpeed = body.velocity.dot(tangent);
      const speedError = desiredSpeed - currentTangentialSpeed;
      let coupling = config.galaxy.spinCoupling;

      if (body.type === "gas") {
        coupling = config.gas.galacticSpinCoupling;
      } else if (body.type === "fragment") {
        coupling = config.galaxy.fragmentSpinCoupling;
      } else if (isStarLike(body)) {
        coupling = config.galaxy.starSpinCoupling;
      }

      body.velocity.addScaledVector(tangent, speedError * coupling * dt);
    }

    function stableOrbitalVelocity(body, parent, normal, speedScale = 1) {
      const relative = body.position.clone().sub(parent.position);

      if (relative.lengthSq() < 1e-8) {
        relative.copy(randomUnitVector()).multiplyScalar(parent.radius * 4);
      }

      const simulationScale = Math.max(
        0.00001,
        Number(body.radiusScale) || Number(parent.radiusScale) || getSimulationWorldScale()
      );
      const radius = Math.max(0.035 * simulationScale, relative.length());
      let tangent = new THREE.Vector3().crossVectors(normal.clone().normalize(), relative.clone().normalize());

      if (tangent.lengthSq() < 1e-8) {
        tangent = randomUnitVector().cross(relative).normalize();
      } else {
        tangent.normalize();
      }

      if (body.velocity.clone().sub(parent.velocity).dot(tangent) < 0) {
        tangent.multiplyScalar(-1);
      }

      const scaledOrbitalG = ORBITAL_G * Math.pow(simulationScale, 3);
      const speed = Math.sqrt(Math.max(0.0000001, scaledOrbitalG * effectiveAttractorMass(parent) / radius)) * speedScale;
      return parent.velocity.clone().add(tangent.multiplyScalar(speed));
    }

    function cloneOrbitElements(elements) {
      if (!elements) {
        return null;
      }

      const semiMajorAxis = Number(elements.semiMajorAxis);
      const eccentricity = Number(elements.eccentricity);
      const phase = Number(elements.phase);

      if (!Number.isFinite(semiMajorAxis) || semiMajorAxis <= 0 || !Number.isFinite(eccentricity)) {
        return null;
      }

      const normal = elements.normal?.clone ? elements.normal.clone() : toVec3(elements.normal || [0, 0, 1]);
      const periapsisAxis = elements.periapsisAxis?.clone ? elements.periapsisAxis.clone() : toVec3(elements.periapsisAxis || [1, 0, 0]);
      const tangentAxis = elements.tangentAxis?.clone ? elements.tangentAxis.clone() : toVec3(elements.tangentAxis || [0, 1, 0]);

      if (normal.lengthSq() < 1e-9 || periapsisAxis.lengthSq() < 1e-9 || tangentAxis.lengthSq() < 1e-9) {
        return null;
      }

      return {
        semiMajorAxis,
        eccentricity: THREE.MathUtils.clamp(eccentricity, KEPLER_MIN_ECCENTRICITY, KEPLER_MAX_ECCENTRICITY),
        phase: Number.isFinite(phase) ? phase : 0,
        direction: Number(elements.direction) < 0 ? -1 : 1,
        speedScale: Math.max(0.15, Number(elements.speedScale) || 1),
        normal: normal.normalize(),
        periapsisAxis: periapsisAxis.normalize(),
        tangentAxis: tangentAxis.normalize(),
      };
    }

    function clearOrbitElements(body) {
      body.orbitElements = null;
      body.orbitParentId = null;
      body.orbitLockedThisFrame = false;
    }

    function getParentSystemForBody(body) {
      return body?.parentSystemId ? starSystemContainers.get(body.parentSystemId) : null;
    }

    function getOrbitBoundaryRadius(parent, body = null) {
      const parentSystem = getParentSystemForBody(parent) || getParentSystemForBody(body);

      if (parentSystem?.outerCubeHalfSize) {
        return Math.max(parent.radius * 4, parentSystem.outerCubeHalfSize * 0.88 - parent.radius - (body?.radius || 0));
      }

      const block = galaxyManager?.activeBlock;

      if (block?.isDustCube) {
        const localParent = blockLocalPositionFromWorld(parent.position, block);
        const halfSize = getBlockSimulationDisplaySize(block) * 0.5;
        const margin = parent.radius + (body?.radius || 0) * 1.5;
        const room = Math.min(
          halfSize - Math.abs(localParent.x),
          halfSize - Math.abs(localParent.y),
          halfSize - Math.abs(localParent.z)
        ) - margin;

        return Math.max(parent.radius * 4, room);
      }

      return Infinity;
    }

    function constrainOrbitElementsToBoundary(body, parent, elements) {
      if (!elements) {
        return null;
      }

      const boundaryRadius = getOrbitBoundaryRadius(parent, body);

      if (!Number.isFinite(boundaryRadius) || boundaryRadius <= parent.radius * 4) {
        return elements;
      }

      const e = THREE.MathUtils.clamp(elements.eccentricity, KEPLER_MIN_ECCENTRICITY, KEPLER_MAX_ECCENTRICITY);
      const maxSemiMajorAxis = boundaryRadius / Math.max(1.01, 1 + e);
      const minimumSemiMajorAxis = Math.max(parent.radius * (body.type === "moon" ? 2.8 : 4.2), (body.radius || 0) * 5);
      elements.semiMajorAxis = THREE.MathUtils.clamp(
        elements.semiMajorAxis,
        minimumSemiMajorAxis,
        Math.max(minimumSemiMajorAxis, maxSemiMajorAxis)
      );
      return elements;
    }

    function maxOrbitSemiMajorAxis(parent) {
      const systemBoundary = getOrbitBoundaryRadius(parent, null);

      if (Number.isFinite(systemBoundary)) {
        return Math.max(parent.radius * 6, systemBoundary * 0.54);
      }

      const block = galaxyManager?.activeBlock;

      if (block?.isDustCube) {
        return Math.max(parent.radius * 6, getBlockSimulationDisplaySize(block) * 0.28);
      }

      return Math.max(parent.radius * 6, getGalaxyConfig().radius * 0.5);
    }

    function buildEllipticalOrbitElements(body, parent, normal = galacticNormal, speedScale = 1) {
      const relative = body.position.clone().sub(parent.position);

      if (relative.lengthSq() < 1e-10) {
        relative.copy(randomUnitVector()).multiplyScalar(Math.max(parent.radius * 3.5, 0.08 * getSimulationWorldScale()));
      }

      const radius = Math.max(parent.radius * 2.2, relative.length());
      const rHat = relative.clone().normalize();
      const relVelocity = body.velocity.clone().sub(parent.velocity);
      let orbitNormal = new THREE.Vector3().crossVectors(relative, relVelocity);

      if (orbitNormal.lengthSq() < 1e-9) {
        orbitNormal = normal.clone();
      }

      orbitNormal.normalize();

      if (orbitNormal.dot(normal) < 0) {
        orbitNormal.multiplyScalar(-1);
      }

      let tangent = new THREE.Vector3().crossVectors(orbitNormal, rHat);

      if (tangent.lengthSq() < 1e-9) {
        tangent = randomUnitVector().cross(rHat);
      }

      tangent.normalize();

      if (relVelocity.dot(tangent) < 0) {
        tangent.multiplyScalar(-1);
      }

      const simulationScale = Math.max(
        0.00001,
        Number(body.radiusScale) || Number(parent.radiusScale) || getSimulationWorldScale()
      );
      const mu = Math.max(0.0000001, ORBITAL_G * Math.pow(simulationScale, 3) * effectiveAttractorMass(parent));
      const circularSpeed = Math.sqrt(mu / Math.max(radius, 0.0001));
      const radialSpeed = relVelocity.dot(rHat);
      const tangentialSpeed = Math.abs(relVelocity.dot(tangent));
      const directionAngle = Math.abs(radialSpeed) / Math.max(0.0001, tangentialSpeed + Math.abs(radialSpeed));
      const speedMismatch = Math.abs(tangentialSpeed - circularSpeed) / Math.max(circularSpeed, 0.0001);
      const eccentricity = THREE.MathUtils.clamp(
        KEPLER_MIN_ECCENTRICITY + directionAngle * 0.48 + speedMismatch * 0.18,
        KEPLER_MIN_ECCENTRICITY,
        body.type === "moon" ? 0.58 : KEPLER_MAX_ECCENTRICITY
      );
      const phase = radialSpeed >= 0 ? Math.PI * 0.16 : Math.PI * 1.84;
      const semiMajorAxis = THREE.MathUtils.clamp(
        radius * (1 + eccentricity * Math.cos(phase)) / Math.max(0.12, 1 - eccentricity * eccentricity),
        Math.max(radius * 0.62, parent.radius * (body.type === "moon" ? 3.2 : 4.5)),
        Math.max(radius * 1.08, maxOrbitSemiMajorAxis(parent))
      );
      const periapsisAxis = rHat.clone().multiplyScalar(Math.cos(phase)).add(tangent.clone().multiplyScalar(-Math.sin(phase))).normalize();
      const tangentAxis = new THREE.Vector3().crossVectors(orbitNormal, periapsisAxis).normalize();

      if (tangentAxis.dot(tangent) < 0) {
        tangentAxis.multiplyScalar(-1);
      }

      return {
        semiMajorAxis,
        eccentricity,
        phase,
        direction: tangentAxis.dot(tangent) >= 0 ? 1 : -1,
        speedScale,
        normal: orbitNormal,
        periapsisAxis,
        tangentAxis,
      };
    }

    function initialiseEllipticalOrbit(body, parent, normal = galacticNormal, speedScale = 1) {
      if (
        !body.orbitElements ||
        body.orbitParentId !== parent.id ||
        !Number.isFinite(body.orbitElements.semiMajorAxis) ||
        body.orbitElements.semiMajorAxis <= 0
      ) {
        body.orbitElements = buildEllipticalOrbitElements(body, parent, normal, speedScale);
        body.orbitParentId = parent.id;
      }

      constrainOrbitElementsToBoundary(body, parent, body.orbitElements);
      return body.orbitElements;
    }

    function ellipseRelativePosition(elements, phase = elements.phase) {
      const e = THREE.MathUtils.clamp(elements.eccentricity, KEPLER_MIN_ECCENTRICITY, KEPLER_MAX_ECCENTRICITY);
      const denominator = Math.max(0.08, 1 + e * Math.cos(phase));
      const radius = elements.semiMajorAxis * (1 - e * e) / denominator;

      return elements.periapsisAxis.clone()
        .multiplyScalar(Math.cos(phase) * radius)
        .add(elements.tangentAxis.clone().multiplyScalar(Math.sin(phase) * radius));
    }

    function advanceEllipticalOrbit(body, parent, dt, strength, normal = galacticNormal) {
      const elements = initialiseEllipticalOrbit(body, parent, normal, body.type === "moon" ? 1.08 : 1);

      if (!elements) {
        return false;
      }

      const previousRelative = body.position.clone().sub(parent.position);
      const simulationScale = Math.max(
        0.00001,
        Number(body.radiusScale) || Number(parent.radiusScale) || getSimulationWorldScale()
      );
      const mu = Math.max(0.0000001, ORBITAL_G * Math.pow(simulationScale, 3) * effectiveAttractorMass(parent));
      constrainOrbitElementsToBoundary(body, parent, elements);
      const e = THREE.MathUtils.clamp(elements.eccentricity, KEPLER_MIN_ECCENTRICITY, KEPLER_MAX_ECCENTRICITY);
      const meanMotion = Math.sqrt(mu / Math.max(0.000001, Math.pow(elements.semiMajorAxis, 3))) * elements.speedScale;
      const trueAnomalyRate = meanMotion * Math.pow(1 + e * Math.cos(elements.phase), 2) / Math.max(0.18, Math.pow(1 - e * e, 1.5));
      const phaseStep = THREE.MathUtils.clamp(
        trueAnomalyRate * dt * elements.direction,
        -KEPLER_MAX_TRUE_ANOMALY_STEP,
        KEPLER_MAX_TRUE_ANOMALY_STEP
      );
      const previousPhase = elements.phase;
      elements.phase = (elements.phase + phaseStep) % (Math.PI * 2);

      if (elements.phase < 0) {
        elements.phase += Math.PI * 2;
      }

      const targetRelative = ellipseRelativePosition(elements);
      const previousOrbitRelative = ellipseRelativePosition(elements, previousPhase);
      const targetPosition = parent.position.clone().add(targetRelative);
      const targetVelocity = parent.velocity.clone().add(
        targetRelative.clone().sub(previousOrbitRelative).divideScalar(Math.max(0.0001, dt))
      );
      const blend = THREE.MathUtils.clamp(strength * dt * 95, 0.08, 1);

      if (previousRelative.lengthSq() > elements.semiMajorAxis * elements.semiMajorAxis * 9) {
        body.position.copy(targetPosition);
        body.velocity.copy(targetVelocity);
      } else {
        body.position.lerp(targetPosition, blend);
        body.velocity.lerp(targetVelocity, Math.min(1, blend * 1.6));
      }

      body.orbitLockedThisFrame = true;
      return true;
    }

    function stabiliseOrbitingBody(body, parent, dt, strength, normal = galacticNormal) {
      if (!parent?.alive) {
        body.parent = null;
        body.parentId = null;
        body.captureState = "free";
        body.orbitType = PLANET_TYPES.has(body.type) ? "rogue" : "free";
        clearOrbitElements(body);
        return;
      }

      const solved = advanceEllipticalOrbit(body, parent, dt, strength, normal);

      if (!solved) {
        const targetVelocity = stableOrbitalVelocity(body, parent, normal);
        body.velocity.lerp(targetVelocity, Math.min(1, strength * dt * 60));
      }

      body.stableOrbitAge += dt;
      body.captureState = body.stableOrbitAge > 1.5 ? "stableOrbit" : body.captureState;
    }

    function countChildren(parent, predicate = null) {
      let count = 0;

      for (const body of stellarBodies) {
        if (body.alive && body.parentId === parent.id && (!predicate || predicate(body))) {
          count++;
        }
      }

      return count;
    }

    function findDominantStar(body, config) {
      let best = null;
      let bestInfluence = 0;

      for (const star of stellarBodies) {
        if (!star.alive || !isStarLike(star) || isCompactObject(star)) {
          continue;
        }

        const distanceSq = Math.max(0.0025, body.position.distanceToSquared(star.position));
        const distance = Math.sqrt(distanceSq);
        const influenceRadius = Math.max(0.45, star.radius * config.parentAssignment.starInfluenceRadiusMultiplier + Math.sqrt(star.mass) * 0.025);
        const relativeSpeed = body.velocity.distanceTo(star.velocity);
        const influence = effectiveAttractorMass(star) / distanceSq;

        if (
          distance <= influenceRadius &&
          relativeSpeed <= config.parentAssignment.starCaptureSpeedThreshold &&
          influence > config.parentAssignment.starInfluenceThreshold &&
          influence > bestInfluence
        ) {
          best = star;
          bestInfluence = influence;
        }
      }

      return best ? { star: best, influence: bestInfluence } : null;
    }

    function assignParentStar(body, config, dt) {
      if (!body.alive || isStarLike(body) || body.type === "moon" || body.type === "gas" || body.type === "supernovaEvent") {
        return;
      }

      const dominant = findDominantStar(body, config);

      if (!dominant) {
        if (body.type === "planet") {
          body.type = "roguePlanet";
          body.orbitType = "rogue";
        }

        if (!body.parent || isStarLike(body.parent)) {
          body.parent = null;
          body.parentId = null;
          clearOrbitElements(body);
        }

        return;
      }

      if (!body.parent || !body.parent.alive || !isStarLike(body.parent)) {
        body.parent = dominant.star;
        body.parentId = dominant.star.id;
        body.orbitType = body.type === "fragment" || body.type === "blob" ? "stellar" : "stellar";
        body.captureState = "capturedByStar";
        body.stableOrbitAge = 0;
        clearOrbitElements(body);
        return;
      }

      if (body.parentId === dominant.star.id) {
        return;
      }

      const currentDistanceSq = Math.max(0.0025, body.position.distanceToSquared(body.parent.position));
      const currentInfluence = body.parent.mass / currentDistanceSq;

      if (dominant.influence > currentInfluence * config.parentAssignment.starSwitchRatio) {
        if (!body.pendingParentSwitch || body.pendingParentSwitch.id !== dominant.star.id) {
          body.pendingParentSwitch = {
            id: dominant.star.id,
            time: 0,
          };
        }

        body.pendingParentSwitch.time += dt;

        if (body.pendingParentSwitch.time > config.parentAssignment.starSwitchDelay) {
          body.parent = dominant.star;
          body.parentId = dominant.star.id;
          body.pendingParentSwitch = null;
          body.stableOrbitAge = 0;
          body.captureState = "capturedByStar";
          clearOrbitElements(body);
        }
      } else {
        body.pendingParentSwitch = null;
      }
    }

    function ensureRingCandidate(planet, config) {
      if (typeof planet.ringCandidate === "boolean") {
        return planet.ringCandidate;
      }

      const spinBonus = THREE.MathUtils.clamp((planet.spin - config.ring.minPlanetSpin) * 0.16, 0, 0.16);
      const massBonus = THREE.MathUtils.clamp((planet.mass - config.ring.minPlanetMass) / 480, 0, 0.12);
      const chance = THREE.MathUtils.clamp((Number(config.ring.occurrenceChance) || 0.32) + spinBonus + massBonus, 0.08, 0.58);
      planet.ringCandidate = seededUnit(`ring:${planet.surfaceSeed}:${planet.id}:${planet.mass.toFixed(2)}:${planet.spin.toFixed(2)}`) < chance;
      return planet.ringCandidate;
    }

    function canFormRing(planet, config) {
      return (
        config.enableRingFormation &&
        config.ring.enabled &&
        PLANET_TYPES.has(planet.type) &&
        ensureRingCandidate(planet, config) &&
        planet.mass >= config.ring.minPlanetMass &&
        planet.spin >= config.ring.minPlanetSpin &&
        planet.capturedDebrisMass >= config.ring.minCapturedDebrisMass
      );
    }

    function getPlanetRingRadii(planet, config) {
      const planetRadius = Math.max(0.0001, planet.radius || 0);
      const maxOuterMultiplier = THREE.MathUtils.clamp(
        Number(config.ring.outerRadiusMultiplier) || RING_MAX_OUTER_DIAMETER_MULTIPLIER,
        RING_START_OUTER_DIAMETER_MULTIPLIER + 0.02,
        RING_MAX_OUTER_DIAMETER_MULTIPLIER
      );
      const innerMultiplier = THREE.MathUtils.clamp(
        Number(config.ring.innerRadiusMultiplier) || 1.04,
        1.01,
        RING_START_OUTER_DIAMETER_MULTIPLIER - 0.02
      );
      const density = THREE.MathUtils.clamp(Number(planet.ring?.density) || 0, 0, 1);
      const outerMultiplier = THREE.MathUtils.lerp(
        RING_START_OUTER_DIAMETER_MULTIPLIER,
        maxOuterMultiplier,
        density
      );
      const innerRadius = planetRadius * innerMultiplier;
      const minimumBandWidth = Math.max(planetRadius * 0.025, 0.0005);
      const outerRadius = Math.max(planetRadius * outerMultiplier, innerRadius + minimumBandWidth);

      return { innerRadius, outerRadius };
    }

    function createPlanetRing(planet, config) {
      if (planet.ring) {
        return planet.ring;
      }

      const color = stellarColorForBody(planet).lerp(new THREE.Color(0xffffff), 0.45);
      const { innerRadius, outerRadius } = getPlanetRingRadii(planet, config);
      planet.ring = {
        mass: 0,
        innerRadius,
        outerRadius,
        thickness: config.ring.ringThickness,
        opacity: config.ring.ringOpacity,
        tiltAxis: planet.spinAxis.clone().normalize(),
        particleCount: Math.min(config.ring.maxRingParticles, 80),
        color,
        density: 0,
        bandCount: Math.max(3, Math.round(config.ring.bandCount || 7)),
        age: 0,
        mesh: null,
        lastOuterRadius: 0,
        lastBandCount: 0,
      };

      renderPlanetRing(planet, config);
      recordStellarEvent(`ring formed around planet ${planet.id}`);
      return planet.ring;
    }

    function disposeRingMesh(ring) {
      if (!ring?.mesh) {
        return;
      }

      stellarGroup.remove(ring.mesh);
      ring.mesh.traverse(child => {
        if (child.geometry) {
          child.geometry.dispose();
        }

        if (child.material) {
          const materials = Array.isArray(child.material) ? child.material : [child.material];
          for (const material of materials) {
            material.dispose();
          }
        }
      });
      ring.mesh = null;
    }

    function ringBandColor(planet, ring, index, bandCount) {
      const planetColor = stellarColorForBody(planet);
      const seed = planet.surfaceSeed ?? planet.id;
      const baseHsl = { h: 0, s: 0, l: 0 };
      planetColor.getHSL(baseHsl);
      const bandHue = (baseHsl.h + seededRange(`ring-hue:${seed}:${index}`, -0.045, 0.055) + 1) % 1;
      const bandSaturation = THREE.MathUtils.clamp(baseHsl.s * 0.55 + seededRange(`ring-sat:${seed}:${index}`, 0.08, 0.28), 0.12, 0.76);
      const bandLightness = THREE.MathUtils.clamp(0.48 + seededRange(`ring-lit:${seed}:${index}`, -0.14, 0.22), 0.28, 0.78);
      const color = new THREE.Color().setHSL(bandHue, bandSaturation, bandLightness);

      if (index % 3 === 1) {
        color.lerp(new THREE.Color(0xfff0c4), 0.28);
      } else if (index % 3 === 2) {
        color.lerp(new THREE.Color(0xb9d2ff), 0.16);
      }

      return color.lerp(ring.color, 0.18);
    }

    function renderPlanetRing(planet, config) {
      const ring = planet.ring;

      if (!ring) {
        return;
      }

      const { innerRadius: targetInner, outerRadius: targetOuter } = getPlanetRingRadii(planet, config);
      ring.innerRadius = Math.min(ring.innerRadius, targetInner * 1.15);
      ring.outerRadius = Math.min(ring.outerRadius, planet.radius * RING_MAX_OUTER_DIAMETER_MULTIPLIER);
      ring.innerRadius = THREE.MathUtils.lerp(ring.innerRadius, targetInner, 0.15);
      ring.outerRadius = THREE.MathUtils.lerp(ring.outerRadius, targetOuter, 0.08);

      const bandCount = Math.max(3, Math.min(12, Math.round((config.ring.bandCount || ring.bandCount || 7) + ring.density * 3)));

      if (!ring.mesh || Math.abs(ring.lastOuterRadius - ring.outerRadius) > 0.025 || ring.lastBandCount !== bandCount) {
        disposeRingMesh(ring);

        const group = new THREE.Group();
        const totalWidth = Math.max(0.0005, ring.outerRadius - ring.innerRadius);
        const seed = planet.surfaceSeed ?? planet.id;
        let cursor = ring.innerRadius;

        for (let i = 0; i < bandCount; i++) {
          const remaining = ring.outerRadius - cursor;

          if (remaining <= totalWidth * 0.025) {
            break;
          }

          const gap = i === 0 ? 0 : totalWidth * seededRange(`ring-gap:${seed}:${i}`, 0.006, 0.026);
          const bandInner = Math.min(ring.outerRadius, cursor + gap);
          const widthShare = seededRange(`ring-width:${seed}:${i}`, 0.7, 1.35) / Math.max(1, bandCount - i);
          const bandWidth = Math.max(totalWidth * 0.035, remaining * widthShare);
          const bandOuter = Math.min(ring.outerRadius, bandInner + bandWidth);

          if (bandOuter - bandInner <= totalWidth * 0.015) {
            cursor = bandOuter;
            continue;
          }

          const geometry = new THREE.RingGeometry(bandInner, bandOuter, 192, 2);
          const baseOpacity = ring.opacity * seededRange(`ring-opacity:${seed}:${i}`, 0.46, 0.95);
          const material = new THREE.MeshBasicMaterial({
            color: ringBandColor(planet, ring, i, bandCount),
            transparent: true,
            opacity: config.showPlanetRings ? baseOpacity : 0,
            side: THREE.DoubleSide,
            depthWrite: false,
          });

          material.userData.baseOpacity = baseOpacity;
          const band = new THREE.Mesh(geometry, material);
          band.renderOrder = 2;
          group.add(band);
          cursor = bandOuter;
        }

        ring.mesh = group;
        stellarGroup.add(ring.mesh);
        ring.lastOuterRadius = ring.outerRadius;
        ring.lastBandCount = bandCount;
      }
    }

    function updatePlanetRing(planet, dt, config) {
      if (!planet.ring) {
        return;
      }

      const ring = planet.ring;
      ring.age += dt;
      ring.opacity = Math.min(config.ring.ringOpacity, 0.1 + ring.mass / Math.max(1, config.ring.maxRingMass) * config.ring.ringOpacity);
      ring.particleCount = Math.min(config.ring.maxRingParticles, Math.max(40, Math.round(40 + ring.mass * 4)));
      renderPlanetRing(planet, config);

      if (!ring.mesh) {
        return;
      }

      ring.mesh.visible = config.showPlanetRings;
      ring.mesh.position.copy(planet.position);
      ring.mesh.quaternion.setFromUnitVectors(new THREE.Vector3(0, 0, 1), ring.tiltAxis.clone().normalize());
      ring.mesh.rotateZ(ring.age * config.ring.ringParticleSpeed);

      ring.mesh.traverse(child => {
        if (child.material) {
          const baseOpacity = Number(child.material.userData?.baseOpacity) || ring.opacity;
          child.material.opacity = config.showPlanetRings ? baseOpacity : 0;
        }
      });
    }

    function absorbIntoRing(planet, fragment, config) {
      planet.capturedDebrisMass += fragment.mass;

      if (!canFormRing(planet, config) && !planet.ring) {
        return false;
      }

      const ring = createPlanetRing(planet, config);
      ring.mass = Math.min(config.ring.maxRingMass, ring.mass + fragment.mass);
      ring.density = Math.min(1, ring.mass / Math.max(1, config.ring.maxRingMass));
      planet.spin = Math.min(14, planet.spin + fragment.mass * 0.01);
      removeBody(fragment);
      return true;
    }

    function tryCreateMoon(planet, fragment, config) {
      if (!config.enableMoonFormation || !config.moons.enabled || !fragment.alive) {
        return false;
      }

      if (fragment.mass < config.moons.moonMassThreshold) {
        return false;
      }

      if (countChildren(planet, child => child.type === "moon") >= config.moons.maxMoonsPerPlanet) {
        return false;
      }

      const distance = fragment.position.distanceTo(planet.position);
      const influenceRadius = Math.max(planet.radius * config.moons.moonInfluenceRadiusMultiplier, 0.22);
      const relativeSpeed = fragment.velocity.distanceTo(planet.velocity);
      const rocheLimit = planet.radius * config.ring.rocheMultiplier;

      if (
        distance <= rocheLimit ||
        distance > influenceRadius ||
        relativeSpeed > config.moons.moonCaptureSpeedThreshold
      ) {
        return false;
      }

      fragment.type = "moon";
      fragment.parent = planet;
      fragment.parentId = planet.id;
      fragment.orbitType = "planetary";
      fragment.captureState = "stableOrbit";
      fragment.stableOrbitAge = 0;
      fragment.protectedUntil = stellarClock + config.planetFormation.protectedAge;
      fragment.spinAxis = planet.spinAxis.clone().lerp(randomUnitVector(), 0.2).normalize();
      clearOrbitElements(fragment);
      initialiseEllipticalOrbit(fragment, planet, planet.spinAxis, 1.08);
      fragment.velocity.copy(stableOrbitalVelocity(fragment, planet, planet.spinAxis, 1.05));
      recordStellarEvent(`moon captured by planet ${planet.id}`);
      return true;
    }

    function updateDebrisCapture(config) {
      const planets = stellarBodies.filter(body => body.alive && PLANET_TYPES.has(body.type));

      if (planets.length === 0) {
        return;
      }

      for (const debris of [...stellarBodies]) {
        if (!debris.alive || !(debris.type === "fragment" || debris.type === "blob")) {
          continue;
        }

        let bestPlanet = null;
        let bestInfluence = 0;

        for (const planet of planets) {
          const distanceSq = Math.max(0.0025, debris.position.distanceToSquared(planet.position));
          const distance = Math.sqrt(distanceSq);
          const influenceRadius = Math.max(planet.radius * config.parentAssignment.planetInfluenceRadiusMultiplier, 0.22);

          if (distance > influenceRadius) {
            continue;
          }

          const influence = planet.mass / distanceSq;

          if (influence > bestInfluence) {
            bestInfluence = influence;
            bestPlanet = planet;
          }
        }

        if (!bestPlanet) {
          continue;
        }

        const distance = debris.position.distanceTo(bestPlanet.position);
        const relativeSpeed = debris.velocity.distanceTo(bestPlanet.velocity);
        const rocheLimit = bestPlanet.radius * config.ring.rocheMultiplier;

        debris.parent = bestPlanet;
        debris.parentId = bestPlanet.id;
        debris.captureState = "capturedByPlanet";

        if (distance < bestPlanet.radius * 1.12) {
          mergeMatterBodies(bestPlanet, debris, config, relativeSpeed);
        } else if (distance < rocheLimit && relativeSpeed < config.moons.moonCaptureSpeedThreshold * 1.35) {
          if (!absorbIntoRing(bestPlanet, debris, config) && debris.alive) {
            mergeMatterBodies(bestPlanet, debris, config, relativeSpeed);
          }
        } else if (relativeSpeed < config.moons.moonCaptureSpeedThreshold) {
          tryCreateMoon(bestPlanet, debris, config);
        }
      }
    }

    function updateParentingAndOrbits(dt, config) {
      for (const body of stellarBodies) {
        if (body.parentSystemId && config.systemTravel?.preserveLocalPhysics) {
          continue;
        }

        assignParentStar(body, config, dt);

        if (PLANET_TYPES.has(body.type) && body.parent?.alive && isStarLike(body.parent)) {
          body.type = "planet";
          body.orbitType = "stellar";
          stabiliseOrbitingBody(body, body.parent, dt, 0.055, galacticNormal);
        } else if (body.type === "moon" && body.parent?.alive) {
          stabiliseOrbitingBody(body, body.parent, dt, config.moons.orbitStabilisationStrength, body.parent.spinAxis || galacticNormal);
        }
      }

      updateDebrisCapture(config);
    }

    function calculateSystemBarycentre(bodies) {
      const centre = new THREE.Vector3();
      let totalMass = 0;

      for (const body of bodies) {
        if (!body?.alive) {
          continue;
        }

        const mass = Math.max(0.001, body.mass);
        centre.addScaledVector(body.position, mass);
        totalMass += mass;
      }

      return totalMass > 0 ? centre.divideScalar(totalMass) : new THREE.Vector3();
    }

    function calculateSystemBarycentricVelocity(bodies) {
      const velocity = new THREE.Vector3();
      let totalMass = 0;

      for (const body of bodies) {
        if (!body?.alive) {
          continue;
        }

        const mass = Math.max(0.001, body.mass);
        velocity.addScaledVector(body.velocity, mass);
        totalMass += mass;
      }

      return totalMass > 0 ? velocity.divideScalar(totalMass) : new THREE.Vector3();
    }

    function getLocalSystemBodiesForStar(star) {
      const bodies = [];
      const seen = new Set();

      const addBody = body => {
        if (!body?.alive || seen.has(body.id) || body.type === "supernovaEvent") {
          return;
        }

        seen.add(body.id);
        bodies.push(body);
      };

      addBody(star);

      for (const body of stellarBodies) {
        if (!body.alive || body === star || body.type === "supernovaEvent" || body.type === "gas") {
          continue;
        }

        if (body.parentId === star.id) {
          addBody(body);
        }
      }

      let changed = true;

      while (changed) {
        changed = false;

        for (const body of stellarBodies) {
          if (!body.alive || seen.has(body.id) || body.type === "supernovaEvent" || body.type === "gas") {
            continue;
          }

          if (body.parentId && seen.has(body.parentId)) {
            addBody(body);
            changed = true;
          }
        }
      }

      return bodies;
    }

    function countRecentSystemEvents(system, pattern, windowSeconds) {
      return recentStellarEvents.filter(event =>
        stellarClock - event.time <= windowSeconds &&
        pattern.test(event.text)
      ).length;
    }

    function buildStarSystemCandidate(star, config, dt) {
      const bodies = getLocalSystemBodiesForStar(star);
      const planets = bodies.filter(body => PLANET_TYPES.has(body.type) && body.parentId === star.id && body.captureState === "stableOrbit");
      const moons = bodies.filter(body => body.type === "moon");
      const totalMass = bodies.reduce((sum, body) => sum + Math.max(0, body.mass), 0);
      const previousMass = Number(star.systemCandidateMass) || totalMass;
      const massChangeRate = Math.abs(totalMass - previousMass) / Math.max(1, previousMass) / Math.max(dt, 0.001);
      const bodyCountChanged = Number.isFinite(star.systemCandidateBodyCount)
        ? Math.abs(bodies.length - star.systemCandidateBodyCount)
        : 0;
      const recentCollisions = bodies.filter(body => stellarClock - body.lastMajorCollisionTime < config.systemStability.minStableAge).length;
      const recentExplosions = countRecentSystemEvents(
        null,
        /supernova|kilonova|direct-collapse|shattered|runaway/i,
        config.systemStability.minStableAge
      );
      const oldEnoughPlanets = planets.filter(planet => planet.stableOrbitAge >= config.systemStability.minStableAge * 0.25);
      const dominance = totalMass > 0 ? star.mass / totalMass : 1;
      const orbitQuality = planets.length === 0
        ? 0
        : oldEnoughPlanets.length / Math.max(1, planets.length);
      const baseStable =
        star.type !== "unstableStar" &&
        !isCompactObject(star) &&
        planets.length >= config.systemStability.minPlanets &&
        recentCollisions <= config.systemStability.maxRecentCollisions &&
        recentExplosions <= config.systemStability.maxRecentExplosions &&
        massChangeRate <= config.systemStability.maxMassChangeRate &&
        bodyCountChanged <= 2 &&
        orbitQuality >= config.systemStability.orbitTolerance &&
        (!config.systemStability.requireDominantStar || dominance >= 0.52);

      star.systemCandidateAge = baseStable
        ? (Number(star.systemCandidateAge) || 0) + dt
        : Math.max(0, (Number(star.systemCandidateAge) || 0) - dt * 2);
      star.systemCandidateMass = totalMass;
      star.systemCandidateBodyCount = bodies.length;

      return {
        id: star.parentSystemId ?? `candidate-${star.id}`,
        centralStar: star,
        bodies,
        planets,
        moons,
        stableAge: star.systemCandidateAge,
        totalMass,
        massChangeRate,
        recentCollisions,
        recentExplosions,
        dominance,
      };
    }

    function isStarSystemStable(system, config = getStellarConfig()) {
      if (!system?.centralStar?.alive) {
        return false;
      }

      return (
        system.centralStar.type !== "unstableStar" &&
        !isCompactObject(system.centralStar) &&
        system.planets.length >= config.systemStability.minPlanets &&
        system.stableAge >= config.systemStability.minStableAge &&
        system.recentCollisions <= config.systemStability.maxRecentCollisions &&
        system.recentExplosions <= config.systemStability.maxRecentExplosions &&
        system.massChangeRate <= config.systemStability.maxMassChangeRate &&
        (!config.systemStability.requireDominantStar || system.dominance >= 0.52)
      );
    }

    function createSystemMarker(system, config) {
      const starColor = stellarColorForBody(system.centralStar).clone().lerp(new THREE.Color(0xffffff), 0.2);
      const geometry = new THREE.SphereGeometry(0.06, 24, 12);
      const material = new THREE.MeshBasicMaterial({
        color: starColor,
        transparent: true,
        opacity: 0.92,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
      });
      const marker = new THREE.Mesh(geometry, material);
      marker.scale.setScalar(Math.max(0.35, config.systemTravel.systemMarkerSize * 0.06));
      marker.position.copy(system.rootObject.position);
      marker.userData.systemId = system.id;
      starSystemTravelGroup.add(marker);
      system.marker = marker;
    }

    function createSystemGalacticOrbitPath(system, config = getStellarConfig()) {
      if (system.orbitPath) {
        starSystemTravelGroup.remove(system.orbitPath);
        system.orbitPath.geometry.dispose();
        system.orbitPath.material.dispose();
        system.orbitPath = null;
      }

      const radius = Math.max(0.05, system.galacticRadius);
      const segments = 192;
      const positions = new Float32Array((segments + 1) * 3);

      for (let i = 0; i <= segments; i++) {
        const angle = (i / segments) * Math.PI * 2;
        positions[i * 3 + 0] = Math.cos(angle) * radius;
        positions[i * 3 + 1] = Math.sin(angle) * radius;
        positions[i * 3 + 2] = system.galacticHeight;
      }

      const geometry = new THREE.BufferGeometry();
      geometry.setAttribute("position", new THREE.BufferAttribute(positions, 3));
      const material = new THREE.LineBasicMaterial({
        color: 0x7fb6ff,
        transparent: true,
        opacity: config.systemTravel.showGalacticOrbitPath ? config.systemTravel.galacticOrbitPathOpacity : 0,
        depthWrite: false,
        blending: THREE.AdditiveBlending,
      });

      system.orbitPath = new THREE.Line(geometry, material);
      system.orbitPath.userData.systemId = system.id;
      starSystemTravelGroup.add(system.orbitPath);
    }

    function updateSystemGalacticOrbitPath(system, config = getStellarConfig()) {
      if (!system.orbitPath) {
        createSystemGalacticOrbitPath(system, config);
      }

      if (system.orbitPath?.material) {
        system.orbitPath.material.opacity = config.systemTravel.showGalacticOrbitPath
          ? config.systemTravel.galacticOrbitPathOpacity
          : 0;
      }
    }

    function disposeStarSystemVisuals(system) {
      if (system?.marker) {
        starSystemTravelGroup.remove(system.marker);
        system.marker.geometry?.dispose();
        system.marker.material?.dispose();
        system.marker = null;
      }

      if (system?.orbitPath) {
        starSystemTravelGroup.remove(system.orbitPath);
        system.orbitPath.geometry?.dispose();
        system.orbitPath.material?.dispose();
        system.orbitPath = null;
      }

      if (system?.rootObject?.parent) {
        system.rootObject.parent.remove(system.rootObject);
      }
    }

    function stopPendulumAfterStableSystem(system, config) {
      if (!config.systemStability.stopPendulumOnStableSystem || !running) {
        return;
      }

      const planets = system.bodies.filter(body =>
        body.alive &&
        PLANET_TYPES.has(body.type) &&
        body.parentId === system.centralStar.id &&
        body.captureState === "stableOrbit"
      );

      if (planets.length < config.systemStability.minPlanets) {
        return;
      }

      running = false;

      if (galaxyManager?.activeBlock) {
        galaxyManager.activeBlock.isSimulated = false;
        galaxyManager.activeBlock.saveEvolutionState();
      }

      statusEl.textContent = `Pendulum stopped | stable solar system ${system.id} formed`;
      recordStellarEvent(`pendulum stopped after stable system ${system.id}`);
    }

    function promoteToStableTravellingSystem(candidate, config = getStellarConfig()) {
      if (!candidate?.centralStar?.alive || candidate.centralStar.parentSystemId) {
        return null;
      }

      const parentBlock = galaxyManager?.activeBlock?.isDustCube ? galaxyManager.activeBlock : null;
      const systemOrigin = candidate.centralStar.position.clone();
      const baryVelocity = calculateSystemBarycentricVelocity(candidate.bodies);
      const systemVelocity = candidate.centralStar.velocity.lengthSq() > 1e-8
        ? candidate.centralStar.velocity.clone()
        : baryVelocity.clone();
      const fallbackHalfSize = Math.max(
        candidate.centralStar.radius * 8,
        ...candidate.bodies.map(body => body.position.distanceTo(candidate.centralStar.position) + body.radius * 2)
      );
      const system = {
        id: `S.${nextStarSystemId++}`,
        parentBlockId: parentBlock?.id ?? null,
        rootObject: new THREE.Group(),
        centralStar: candidate.centralStar,
        bodies: [...candidate.bodies],
        localOrigin: systemOrigin.clone(),
        localRotation: new THREE.Quaternion(),
        localScale: 1,
        galacticRadius: Math.max(config.galaxy.minRadius, Math.hypot(systemOrigin.x, systemOrigin.y)),
        galacticAngle: Math.atan2(systemOrigin.y, systemOrigin.x),
        galacticHeight: systemOrigin.z,
        galacticVelocity: systemVelocity.clone(),
        galacticAngularVelocity: 0,
        galacticOrbitPlaneNormal: galacticNormal.clone(),
        galacticOrbitTangent: new THREE.Vector3(),
        stableAge: candidate.stableAge,
        isStable: true,
        isTravellingAroundGalaxy: true,
        recentCollisions: candidate.recentCollisions,
        recentExplosions: candidate.recentExplosions,
        massChangeRate: candidate.massChangeRate,
        lastMass: candidate.totalMass,
        lastMassSampleTime: stellarClock,
        outerCubeHalfSize: parentBlock ? getBlockSimulationDisplaySize(parentBlock) * 0.5 : fallbackHalfSize,
        collapsed: false,
        marker: null,
        orbitPath: null,
      };

      system.rootObject.position.copy(systemOrigin);
      scene.add(system.rootObject);

      for (const body of system.bodies) {
        body.parentSystem = system;
        body.parentSystemId = system.id;
        body.localPosition = body.position.clone().sub(systemOrigin);
        body.localVelocity = body.velocity.clone().sub(systemVelocity);
      }

      createSystemMarker(system, config);
      createSystemGalacticOrbitPath(system, config);
      starSystemContainers.set(system.id, system);
      systemTravelStats.lastPromotedSystem = system.id;
      recordStellarEvent(`stable system promoted ${system.id}`);
      stopPendulumAfterStableSystem(system, config);
      return system;
    }

    function checkSystemDestabilisation(system, config = getStellarConfig()) {
      if (!system?.centralStar?.alive || system.centralStar.type === "unstableStar") {
        return true;
      }

      const bodies = stellarBodies.filter(body => body.alive && body.parentSystemId === system.id);
      const planets = bodies.filter(body => PLANET_TYPES.has(body.type) && body.parentId === system.centralStar.id);

      if (planets.length < config.systemStability.minPlanets) {
        return true;
      }

      const totalMass = bodies.reduce((sum, body) => sum + Math.max(0, body.mass), 0);
      const elapsed = Math.max(0.001, stellarClock - system.lastMassSampleTime);
      system.massChangeRate = Math.abs(totalMass - system.lastMass) / Math.max(1, system.lastMass) / elapsed;

      if (elapsed > 1) {
        system.lastMass = totalMass;
        system.lastMassSampleTime = stellarClock;
      }

      system.recentCollisions = bodies.filter(body => stellarClock - body.lastMajorCollisionTime < config.systemStability.minStableAge).length;
      system.recentExplosions = countRecentSystemEvents(
        system,
        /supernova|kilonova|direct-collapse|shattered|runaway/i,
        config.systemStability.minStableAge
      );

      return (
        system.massChangeRate > config.systemStability.maxMassChangeRate * 3 ||
        system.recentCollisions > config.systemStability.maxRecentCollisions + 2 ||
        system.recentExplosions > config.systemStability.maxRecentExplosions
      );
    }

    function destabiliseStarSystem(system, reason = "instability") {
      if (!system || !starSystemContainers.has(system.id)) {
        return;
      }

      for (const body of stellarBodies) {
        if (body.parentSystemId === system.id) {
          body.parentSystem = null;
          body.parentSystemId = null;
          body.localPosition = null;
          body.localVelocity = null;
          body.mesh.visible = true;
        }
      }

      disposeStarSystemVisuals(system);
      system.isStable = false;
      system.isTravellingAroundGalaxy = false;
      starSystemContainers.delete(system.id);
      systemTravelStats.lastDestabilisedSystem = system.id;
      recordStellarEvent(`stable system destabilised ${system.id}: ${reason}`);
    }

    function findSystemBodyById(system, id) {
      return system?.bodies?.find(body => body.alive && body.id === id) ?? null;
    }

    function applyTravellingSystemCorkscrewOffset(body, parent, system, config) {
      if (!config.systemTravel.corkscrewPlanetOrbits || !PLANET_TYPES.has(body.type)) {
        return;
      }

      const tangent = system.galacticOrbitTangent.lengthSq() > 1e-9
        ? system.galacticOrbitTangent.clone().normalize()
        : new THREE.Vector3(-Math.sin(system.galacticAngle), Math.cos(system.galacticAngle), 0).normalize();
      const radius = Math.max(body.radius * 3, body.position.distanceTo(parent.position));
      const wake = Math.min(
        Math.max(body.radius * 1.5, radius * 0.38),
        Math.max(body.radius * 2, system.outerCubeHalfSize * 0.22)
      );

      body.position.addScaledVector(tangent, -wake);
      body.velocity.addScaledVector(tangent, -Math.min(wake, system.outerCubeHalfSize * 0.12) * Math.max(0.0001, system.galacticAngularVelocity));
    }

    function updateTravellingOrbitBody(body, system, dt, config) {
      const parent = body.parentId === system.centralStar.id
        ? system.centralStar
        : findSystemBodyById(system, body.parentId);

      if (!parent?.alive) {
        return;
      }

      const normal = body.type === "moon"
        ? (parent.spinAxis || system.galacticOrbitPlaneNormal || galacticNormal)
        : (system.galacticOrbitPlaneNormal || galacticNormal);
      const strength = body.type === "moon"
        ? config.moons.orbitStabilisationStrength
        : 0.16;

      stabiliseOrbitingBody(body, parent, dt, strength, normal);
      applyTravellingSystemCorkscrewOffset(body, parent, system, config);
      body.localPosition = body.position.clone().sub(system.rootObject.position);
      body.localVelocity = body.velocity.clone().sub(system.galacticVelocity);
    }

    function updateSystemGalacticOrbit(system, dt, config = getStellarConfig()) {
      const r = Math.max(config.galaxy.minRadius, system.galacticRadius);
      const speed = getGalacticOrbitalSpeed(r, getGalaxyConfig()) * config.systemTravel.systemOrbitSpeedScale;
      const angularVelocity = speed / r;
      const oldPosition = system.rootObject.position.clone();
      const oldVelocity = system.galacticVelocity.clone();

      system.galacticAngularVelocity = angularVelocity;
      system.galacticAngle += angularVelocity * dt;
      system.galacticHeight *= Math.exp(-config.systemTravel.systemPlaneDamping * dt);

      const nextPosition = new THREE.Vector3(
        Math.cos(system.galacticAngle) * r,
        Math.sin(system.galacticAngle) * r,
        system.galacticHeight
      );
      const nextVelocity = new THREE.Vector3(
        -Math.sin(system.galacticAngle) * speed,
        Math.cos(system.galacticAngle) * speed,
        0
      );
      const delta = nextPosition.clone().sub(oldPosition);
      const velocityDelta = nextVelocity.clone().sub(oldVelocity);

      system.rootObject.position.copy(nextPosition);
      system.rootObject.rotation.z += angularVelocity * dt * config.systemTravel.systemYawFollowStrength;
      system.galacticVelocity.copy(nextVelocity);
      system.galacticOrbitTangent.copy(nextVelocity).normalize();
      system.stableAge += dt;

      system.bodies = stellarBodies.filter(body => body.alive && body.parentSystemId === system.id);
      system.centralStar.position.copy(nextPosition);
      system.centralStar.velocity.copy(nextVelocity);
      system.centralStar.localPosition = new THREE.Vector3();
      system.centralStar.localVelocity = new THREE.Vector3();

      const planets = system.bodies.filter(body => PLANET_TYPES.has(body.type) && body.parentId === system.centralStar.id);
      const moons = system.bodies.filter(body => body.type === "moon");
      const otherBodies = system.bodies.filter(body => body !== system.centralStar && !PLANET_TYPES.has(body.type) && body.type !== "moon");

      for (const body of planets) {
        updateTravellingOrbitBody(body, system, dt, config);
      }

      for (const body of moons) {
        updateTravellingOrbitBody(body, system, dt, config);
      }

      for (const body of otherBodies) {
        body.position.add(delta);
        body.velocity.add(velocityDelta);
        body.localPosition = body.position.clone().sub(system.rootObject.position);
        body.localVelocity = body.velocity.clone().sub(system.galacticVelocity);
      }

      for (const body of system.bodies) {
        if (PLANET_TYPES.has(body.type)) {
          updatePlanetRing(body, dt, config);
        }

        updateBodyVisual(body, dt);
        updateBodyTrail(body, config);
      }

      if (system.marker) {
        system.marker.position.copy(system.rootObject.position);
        system.marker.scale.setScalar(Math.max(0.12, config.systemTravel.systemMarkerSize * 0.06 + Math.sqrt(system.centralStar.mass) * 0.004));
      }

      updateSystemGalacticOrbitPath(system, config);
    }

    function updateStarSystemLOD(system, config = getStellarConfig()) {
      const distance = camera.position.distanceTo(system.rootObject.position);
      const collapsed = config.systemTravel.collapseDetailWhenZoomedOut &&
        distance > Math.max(config.lod.systemCollapseDistance, config.lod.systemDetailDistance);

      system.collapsed = collapsed;

      if (system.marker) {
        system.marker.visible = config.systemTravel.enabled && (collapsed || currentLODMode !== "system");
      }

      if (system.orbitPath) {
        system.orbitPath.visible = config.systemTravel.enabled && config.systemTravel.showGalacticOrbitPath;
      }

      for (const body of system.bodies) {
        if (body.mesh) {
          body.mesh.visible = !collapsed;
        }

        if (body.ring?.mesh) {
          body.ring.mesh.visible = !collapsed && config.showPlanetRings;
        }

        if (body.trailLine) {
          body.trailLine.visible = !collapsed;
        }
      }
    }

    function updateStarSystemContainers(dt, config = getStellarConfig()) {
      if (!config.systemTravel.enabled) {
        starSystemTravelGroup.visible = false;

        for (const system of starSystemContainers.values()) {
          system.collapsed = false;

          for (const body of system.bodies) {
            if (body.mesh) {
              body.mesh.visible = true;
            }

            if (body.ring?.mesh) {
              body.ring.mesh.visible = config.showPlanetRings;
            }
          }
        }

        return;
      }

      starSystemTravelGroup.visible = true;

      if (config.systemTravel.autoStartWhenStable) {
        for (const star of stellarBodies) {
          if (!star.alive || !isStarLike(star) || isCompactObject(star) || star.parentSystemId) {
            continue;
          }

          const candidate = buildStarSystemCandidate(star, config, dt);

          if (isStarSystemStable(candidate, config)) {
            promoteToStableTravellingSystem(candidate, config);
          }
        }
      }

      for (const system of [...starSystemContainers.values()]) {
        if (checkSystemDestabilisation(system, config)) {
          destabiliseStarSystem(system, "stability check");
          continue;
        }

        if (system.isTravellingAroundGalaxy) {
          updateSystemGalacticOrbit(system, dt, config);
        }

        updateStarSystemLOD(system, config);
      }
    }

    function createGasBurst(position, velocity, totalMass, count, speed, hot = false) {
      const config = getStellarConfig();
      const simulationScale = getSimulationWorldScale();
      const existingGas = stellarBodies.reduce((sum, body) => sum + (body.type === "gas" ? 1 : 0), 0);
      const availableGasSlots = Math.max(0, config.gas.maxParticles - existingGas);
      const safeCount = Math.max(0, Math.min(220, availableGasSlots, Math.round(count)));

      if (safeCount <= 0) {
        return;
      }

      const massPerParticle = Math.max(0.05, totalMass / safeCount);

      for (let i = 0; i < safeCount; i++) {
        const direction = randomUnitVector();
        const color = hot
          ? new THREE.Color().setHSL(0.08 + Math.random() * 0.06, 1, 0.66 + Math.random() * 0.2)
          : new THREE.Color().setHSL(Math.random(), 0.86, 0.62);

        createBody("gas", {
          mass: massPerParticle,
          position: position.clone().add(direction.clone().multiplyScalar((0.015 + Math.random() * 0.04) * simulationScale)),
          velocity: velocity.clone().multiplyScalar(0.4).add(direction.multiplyScalar(speed * simulationScale * (0.35 + Math.random() * 0.85))),
          ttl: config.gas.lifetime * (0.72 + Math.random() * 0.56),
          temperature: hot ? 9000 : 1800,
          color,
        });
      }
    }

    function createFragments(position, velocity, totalMass, count, speed) {
      const simulationScale = getSimulationWorldScale();
      const safeCount = Math.max(1, Math.min(90, Math.round(count)));
      const massPerFragment = Math.max(0.08, totalMass / safeCount);

      for (let i = 0; i < safeCount; i++) {
        const direction = randomUnitVector();

        createBody("fragment", {
          mass: massPerFragment,
          position: position.clone().add(direction.clone().multiplyScalar((0.02 + Math.random() * 0.04) * simulationScale)),
          velocity: velocity.clone().multiplyScalar(0.55).add(direction.multiplyScalar(speed * simulationScale * (0.28 + Math.random() * 0.75))),
          spin: 2 + Math.random() * 5,
          ttl: 22 + Math.random() * 24,
          color: new THREE.Color(0xffffff),
        });
      }
    }

    function collisionData(a, b) {
      const relativeVelocity = b.velocity.clone().sub(a.velocity);
      const impactSpeed = relativeVelocity.length();
      const totalMass = a.mass + b.mass;
      const reducedMass = (a.mass * b.mass) / Math.max(0.001, totalMass);
      const impactEnergy = 0.5 * reducedMass * impactSpeed * impactSpeed * 10000;
      const position = a.position.clone().multiplyScalar(a.mass).add(
        b.position.clone().multiplyScalar(b.mass)
      ).divideScalar(totalMass);
      const velocity = a.velocity.clone().multiplyScalar(a.mass).add(
        b.velocity.clone().multiplyScalar(b.mass)
      ).divideScalar(totalMass);
      const offset = b.position.clone().sub(a.position);
      const angularMomentum = offset.cross(relativeVelocity).multiplyScalar(reducedMass);
      const distance = a.position.distanceTo(b.position);
      const combinedRadius = Math.max(0.001, a.radius + b.radius);
      const overlap = Math.max(0, combinedRadius - distance) / combinedRadius;
      const grazing = distance / combinedRadius > 0.72;

      return {
        totalMass,
        reducedMass,
        impactSpeed,
        impactEnergy,
        position,
        velocity,
        angularMomentum,
        overlap,
        grazing,
      };
    }

    function createMergedStar(a, b, data, config, unstable = false) {
      const gasLoss = unstable ? Math.min(0.18, 0.05 + data.impactEnergy / 220000) : Math.min(0.06, data.impactEnergy / 500000);
      const ejectedMass = data.totalMass * gasLoss;
      const mass = Math.max(1, data.totalMass - ejectedMass);
      const type = unstable || data.impactEnergy > config.collisionSupernovaEnergyThreshold * 0.35 ? "unstableStar" : "star";
      const star = createBody(type, {
        mass,
        position: data.position,
        velocity: data.velocity,
        spin: Math.min(16, data.angularMomentum.length() / Math.max(1, mass) + data.impactSpeed * 0.7),
        temperature: 5600 + Math.min(12000, data.impactEnergy / 4),
        fuel: Math.min(a.fuel ?? 1, b.fuel ?? 1, 1),
        instability: unstable ? Math.max(1.1, data.impactEnergy / config.collisionSupernovaEnergyThreshold) : data.impactEnergy / (config.collisionSupernovaEnergyThreshold * 2),
        angularMomentum: data.angularMomentum,
      });

      if (ejectedMass > 0.5) {
        createGasBurst(data.position, data.velocity, ejectedMass, 20 + ejectedMass * 0.2, Math.min(config.maxSupernovaParticleSpeed, data.impactSpeed * 0.8 + 0.8), unstable);
      }

      removeBody(a);
      removeBody(b);
      recordStellarEvent(`${type === "unstableStar" ? "unstable" : "stable"} star merger (${mass.toFixed(0)})`);
      return star;
    }

    function createWhiteDwarf(position, velocity, mass, options = {}) {
      return createBody("whiteDwarf", {
        mass,
        position,
        velocity,
        spin: options.spin ?? 2.5,
        temperature: options.temperature ?? 11000,
        luminosity: 1.4,
        remnantType: "whiteDwarf",
        fuel: 0,
        color: new THREE.Color(0xdaf8ff),
      });
    }

    function createNeutronStar(position, velocity, mass, options = {}) {
      return createBody("neutronStar", {
        mass,
        position,
        velocity,
        spin: options.spin ?? 8.5,
        temperature: options.temperature ?? 90000,
        luminosity: 4,
        remnantType: "neutronStar",
        fuel: 0,
        color: new THREE.Color(0x8eb8ff),
      });
    }

    function createBlackHole(position, velocity, mass, options = {}) {
      return createNeutronStar(position, velocity, Math.max(25, mass * 0.88), {
        spin: options.spin ?? 9,
        temperature: 100000,
      });
    }

    function determineRemnantType(star, eventType, remnantMass, config) {
      if (eventType === "typeIa" || eventType === "pairInstability") {
        return "none";
      }

      if (eventType === "directCollapse" || remnantMass >= config.blackHoleFormationMass) {
        return "neutronStar";
      }

      if (remnantMass >= config.neutronStarMassThreshold) {
        return "neutronStar";
      }

      if (star.mass <= config.whiteDwarfFormationMassMax || remnantMass > 18) {
        return "whiteDwarf";
      }

      return "none";
    }

    function handlePlanetarySystemAfterStellarEvent(source, shockSpeed) {
      for (const body of [...stellarBodies]) {
        if (!body.alive || body === source || body.type === "supernovaEvent") {
          continue;
        }

        const distance = body.position.distanceTo(source.position);

        if (distance > source.radius * 18 + 1.6) {
          continue;
        }

        const direction = body.position.clone().sub(source.position);

        if (direction.lengthSq() < 1e-9) {
          direction.copy(randomUnitVector());
        }

        direction.normalize();

        if (body.type === "planet" && distance < source.radius * 6) {
          createGasBurst(body.position, body.velocity, body.mass * 0.45, 18, shockSpeed * 0.5, true);
          createFragments(body.position, body.velocity, body.mass * 0.35, 14, shockSpeed * 0.35);
          removeBody(body);
        } else {
          body.velocity.addScaledVector(direction, shockSpeed / Math.max(8, distance * 10));
          body.parentSystem = null;
        }
      }
    }

    function triggerSupernova(star, reason, options = {}) {
      const config = getStellarConfig();

      if (!star.alive) {
        return null;
      }

      if (!config.enableSupernovae) {
        const remnantMass = Math.max(8, star.mass * 0.52);
        const remnant = config.enableCompactObjects
          ? createWhiteDwarf(star.position.clone(), star.velocity.clone(), remnantMass, { spin: star.spin })
          : null;

        removeBody(star);
        recordStellarEvent(`quiet stellar collapse (${reason})`);
        return remnant;
      }

      let eventType = options.eventType;

      if (!eventType) {
        if (star.type === "whiteDwarf" || options.typeIa) {
          eventType = "typeIa";
        } else if (star.mass >= config.directCollapseMassThreshold) {
          eventType = "directCollapse";
        } else if (star.mass >= config.pairInstabilityMassThreshold) {
          eventType = "pairInstability";
        } else {
          eventType = "coreCollapse";
        }
      }

      let gasFraction = config.supernovaGasFraction;

      if (eventType === "typeIa") {
        gasFraction = config.typeIaGasFraction;
      } else if (eventType === "pairInstability") {
        gasFraction = config.pairInstabilityGasFraction;
      } else if (eventType === "directCollapse") {
        gasFraction = config.directCollapseGasFraction;
      }

      const ejectedMass = Math.max(0.1, star.mass * Math.min(1, gasFraction));
      const remnantMass = Math.max(0, star.mass - ejectedMass);

      const event = createBody("supernovaEvent", {
        mass: Math.max(1, star.mass * 0.1),
        position: star.position.clone(),
        velocity: star.velocity.clone(),
        ttl: 1.2,
        color: new THREE.Color(0xfff4ba),
      });

      const gasCount = Math.min(config.maxSupernovaGasParticles, 36 + Math.round(ejectedMass * 0.55));
      const fragmentCount = Math.min(config.maxSupernovaFragments, Math.round(ejectedMass * config.supernovaFragmentFraction));
      const shockSpeed = eventType === "directCollapse" ? config.supernovaShockSpeed * 0.35 : config.supernovaShockSpeed;

      createGasBurst(star.position, star.velocity, ejectedMass * 0.8, gasCount, Math.min(config.maxSupernovaParticleSpeed, shockSpeed), true);

      if (fragmentCount > 0 && eventType !== "pairInstability") {
        createFragments(star.position, star.velocity, ejectedMass * 0.2, fragmentCount, Math.min(config.maxSupernovaParticleSpeed, shockSpeed * 0.75));
      }

      handlePlanetarySystemAfterStellarEvent(star, shockSpeed);
      removeBody(star);

      let remnant = null;
      const remnantType = config.enableCompactObjects ? determineRemnantType(star, eventType, remnantMass, config) : "none";

      if (remnantType === "blackHole") {
        remnant = createBlackHole(event.position.clone(), event.velocity.clone(), Math.max(25, remnantMass), { spin: star.spin + 1 });
      } else if (remnantType === "neutronStar") {
        remnant = createNeutronStar(event.position.clone(), event.velocity.clone(), Math.max(25, remnantMass), { spin: star.spin + 3 });
      } else if (remnantType === "whiteDwarf") {
        remnant = createWhiteDwarf(event.position.clone(), event.velocity.clone(), Math.max(12, remnantMass), { spin: star.spin + 1 });
      }

      stellarStats.supernovae++;

      if (eventType === "coreCollapse") {
        stellarStats.coreCollapse++;
      } else if (eventType === "typeIa") {
        stellarStats.typeIa++;
      } else if (eventType === "pairInstability") {
        stellarStats.pairInstability++;
      } else if (eventType === "directCollapse") {
        stellarStats.directCollapse++;
      }

      recordStellarEvent(`${eventType} supernova -> ${remnantType} (${reason})`);
      return remnant;
    }

    function createKilonova(a, b, data, config) {
      const ejectedMass = Math.max(1, data.totalMass * 0.18);
      createGasBurst(data.position, data.velocity, ejectedMass, Math.min(config.maxKilonovaGasParticles, 48 + ejectedMass), config.supernovaShockSpeed * 0.7, true);
      createFragments(data.position, data.velocity, ejectedMass * 0.22, 24, config.supernovaShockSpeed * 0.55);
      stellarStats.kilonova++;
      recordStellarEvent("kilonova compact merger");
    }

    function handleCompactObjectCollision(a, b, config) {
      const data = collisionData(a, b);
      const hasBlackHole = a.type === "blackHole" || b.type === "blackHole";

      if (hasBlackHole) {
        const blackHole = a.type === "blackHole" ? a : b;
        const other = blackHole === a ? b : a;
        const totalMass = blackHole.mass + other.mass;

        if (other.type !== "blackHole" && config.enableTidalDisruption) {
          createGasBurst(other.position, data.velocity, Math.min(other.mass * 0.24, 80), 36, config.supernovaShockSpeed * 0.35, true);
        }

        blackHole.velocity.multiplyScalar(blackHole.mass).add(other.velocity.clone().multiplyScalar(other.mass)).divideScalar(totalMass);
        blackHole.mass = totalMass;
        blackHole.spin = Math.min(18, blackHole.spin + data.angularMomentum.length() / Math.max(1, totalMass));
        removeBody(other);
        recordStellarEvent(`black hole absorbed ${other.type}`);
        return;
      }

      if (a.type === "whiteDwarf" && b.type === "whiteDwarf") {
        if (data.totalMass >= config.whiteDwarfChandrasekharLimit) {
          const synthetic = createBody("whiteDwarf", {
            mass: data.totalMass,
            position: data.position,
            velocity: data.velocity,
            spin: a.spin + b.spin,
            fuel: 0,
          });

          removeBody(a);
          removeBody(b);
          triggerSupernova(synthetic, "white-dwarf merger", { eventType: "typeIa" });
        } else {
          removeBody(a);
          removeBody(b);
          createWhiteDwarf(data.position, data.velocity, data.totalMass, { spin: a.spin + b.spin });
          recordStellarEvent("white dwarf merger");
        }

        return;
      }

      if (a.type === "neutronStar" && b.type === "neutronStar") {
        removeBody(a);
        removeBody(b);
        createKilonova(a, b, data, config);

        if (data.totalMass >= config.blackHoleFormationMass) {
          createBlackHole(data.position, data.velocity, data.totalMass * 0.92, { spin: a.spin + b.spin });
        } else {
          createNeutronStar(data.position, data.velocity, data.totalMass * 0.86, { spin: a.spin + b.spin + 2 });
        }

        return;
      }

      const compact = isCompactObject(a) ? a : b;
      const other = compact === a ? b : a;

      if (compact.type === "whiteDwarf" && compact.mass + other.mass * 0.25 > config.whiteDwarfChandrasekharLimit) {
        compact.mass += other.mass * 0.35;
        removeBody(other);
        triggerSupernova(compact, "runaway accretion", { eventType: "typeIa" });
        return;
      }

      if (compact.type === "neutronStar" && data.totalMass >= config.blackHoleFormationMass) {
        removeBody(a);
        removeBody(b);
        createKilonova(a, b, data, config);
        createBlackHole(data.position, data.velocity, data.totalMass * 0.88, { spin: compact.spin + 2 });
        return;
      }

      compact.velocity.multiplyScalar(compact.mass).add(other.velocity.clone().multiplyScalar(other.mass * 0.35)).divideScalar(compact.mass + other.mass * 0.35);
      compact.mass += other.mass * 0.18;
      createGasBurst(other.position, data.velocity, other.mass * 0.42, 24, data.impactSpeed + 0.8, true);
      removeBody(other);
      recordStellarEvent(`${compact.type} stripped ${other.type}`);
    }

    function handleGrazingStarCollision(a, b, data, config) {
      const smaller = a.mass <= b.mass ? a : b;
      const larger = smaller === a ? b : a;
      const strippedMass = Math.min(smaller.mass * 0.35, data.totalMass * 0.12);

      smaller.mass = Math.max(1, smaller.mass - strippedMass);
      larger.mass += strippedMass * 0.18;
      smaller.velocity.add(data.angularMomentum.clone().normalize().multiplyScalar(0.04 + data.impactSpeed * 0.2));
      larger.spin = Math.min(20, larger.spin + data.angularMomentum.length() / Math.max(1, larger.mass));
      larger.instability += data.impactEnergy / (config.collisionSupernovaEnergyThreshold * 3);
      createGasBurst(data.position, data.velocity, strippedMass * 0.72, 20 + strippedMass * 0.2, Math.min(6, data.impactSpeed + 0.8), true);

      if (smaller.mass < 2) {
        removeBody(smaller);
      }

      recordStellarEvent("grazing stellar strip");
    }

    function decideStarCollisionOutcome(a, b, data, config) {
      if (isCompactObject(a) || isCompactObject(b)) {
        return "compact";
      }

      if (data.grazing && data.overlap < 0.45) {
        return "grazing";
      }

      if (data.totalMass >= config.directCollapseMassThreshold || data.impactEnergy >= config.collapseEnergyThreshold) {
        return "directCollapse";
      }

      if (data.totalMass >= config.pairInstabilityMassThreshold && data.impactEnergy > config.collisionSupernovaEnergyThreshold * 0.45) {
        return "pairInstability";
      }

      if (data.impactEnergy >= config.collisionSupernovaEnergyThreshold) {
        return "supernova";
      }

      if (data.impactEnergy >= config.collisionSupernovaEnergyThreshold * 0.28 || data.impactSpeed > 1.2) {
        return "unstableMerge";
      }

      return "stableMerge";
    }

    function handleStarCollision(a, b, config) {
      const data = collisionData(a, b);
      const outcome = decideStarCollisionOutcome(a, b, data, config);

      a.collisionHistory.push({ time: stellarClock, with: b.type, energy: data.impactEnergy });
      b.collisionHistory.push({ time: stellarClock, with: a.type, energy: data.impactEnergy });
      a.lastMajorCollisionTime = stellarClock;
      b.lastMajorCollisionTime = stellarClock;

      if (outcome === "compact") {
        handleCompactObjectCollision(a, b, config);
        return;
      }

      if (outcome === "grazing") {
        handleGrazingStarCollision(a, b, data, config);
        return;
      }

      if (outcome === "directCollapse") {
        removeBody(a);
        removeBody(b);
        createGasBurst(data.position, data.velocity, data.totalMass * config.directCollapseGasFraction, 40, config.supernovaShockSpeed * 0.4, true);
        createBlackHole(data.position, data.velocity, data.totalMass * 0.9, { spin: a.spin + b.spin });
        stellarStats.directCollapse++;
        recordStellarEvent("direct-collapse neutron remnant");
        return;
      }

      if (outcome === "pairInstability") {
        const synthetic = createMergedStar(a, b, data, config, true);
        triggerSupernova(synthetic, "pair instability collision", { eventType: "pairInstability" });
        return;
      }

      if (outcome === "supernova" && config.enableSupernovae) {
        const synthetic = createMergedStar(a, b, data, config, true);
        triggerSupernova(synthetic, "collision energy", { eventType: "coreCollapse" });
        return;
      }

      createMergedStar(a, b, data, config, outcome === "unstableMerge");
    }

    function mergeMatterBodies(larger, smaller, config, impactSpeed = 0) {
      const totalMass = larger.mass + smaller.mass;
      larger.velocity.multiplyScalar(larger.mass).add(smaller.velocity.clone().multiplyScalar(smaller.mass)).divideScalar(totalMass);
      larger.position.multiplyScalar(larger.mass).add(smaller.position.clone().multiplyScalar(smaller.mass)).divideScalar(totalMass);
      larger.mass = totalMass;
      larger.absorbedMassHistory.push({ time: stellarClock, mass: smaller.mass, type: smaller.type });
      larger.temperature += Math.min(900, impactSpeed * 60);
      larger.spin = Math.min(14, larger.spin + impactSpeed * 0.18);

      if (impactSpeed > 1.6 && smaller.mass > 2) {
        const sprayMass = Math.min(smaller.mass * 0.2, totalMass * 0.05);
        larger.mass -= sprayMass;
        createFragments(smaller.position, larger.velocity, sprayMass * 0.4, 8, impactSpeed);
        createGasBurst(smaller.position, larger.velocity, sprayMass * 0.6, 12, impactSpeed * 0.7, false);
      }

      removeBody(smaller);
      classifyBodyAfterGrowth(larger, config);

      if ((PLANET_TYPES.has(larger.type) || larger.type === "moon") && larger.parent?.alive) {
        clearOrbitElements(larger);
      }
    }

    function handleGenericCollision(a, b, config) {
      if (!a.alive || !b.alive) {
        return;
      }

      if (isStarLike(a) || isStarLike(b)) {
        if (isStarLike(a) && isStarLike(b)) {
          handleStarCollision(a, b, config);
          return;
        }

        const star = isStarLike(a) ? a : b;
        const other = star === a ? b : a;

        if (star.type === "blackHole" || isCompactObject(star)) {
          handleCompactObjectCollision(star, other, config);
          return;
        }

        const data = collisionData(star, other);
        const isProtectedPlanet =
          (PLANET_TYPES.has(other.type) || other.type === "moon") &&
          (other.captureState === "stableOrbit" || stellarClock < other.protectedUntil);

        if (isProtectedPlanet) {
          const distance = other.position.distanceTo(star.position);
          const captureRadius = star.radius * config.planetFormation.planetSafeZoneMultiplier;

          if (distance > captureRadius) {
            const pushDirection = other.position.clone().sub(star.position);

            if (pushDirection.lengthSq() < 1e-9) {
              pushDirection.copy(randomUnitVector());
            }

            pushDirection.normalize();
            other.velocity.addScaledVector(pushDirection, Math.min(0.18, data.impactSpeed * 0.08 + 0.02));
            other.velocity.lerp(stableOrbitalVelocity(other, other.parent?.alive ? other.parent : star, galacticNormal), config.planetFormation.stablePlanetAbsorptionResistance * 0.08);
            recordStellarEvent(`${other.type} resisted star absorption`);
            return;
          }
        }

        if (other.mass > star.mass * 0.45 && data.impactEnergy > config.collisionSupernovaEnergyThreshold * 0.3) {
          const syntheticOther = createBody("star", {
            mass: other.mass,
            position: other.position.clone(),
            velocity: other.velocity.clone(),
            spin: other.spin,
            fuel: 0.7,
          });

          removeBody(other);
          handleStarCollision(star, syntheticOther, config);
          return;
        }

        mergeMatterBodies(star, other, config, data.impactSpeed);
        star.instability += data.impactEnergy / (config.collisionSupernovaEnergyThreshold * 5);
        return;
      }

      const larger = a.mass >= b.mass ? a : b;
      const smaller = larger === a ? b : a;
      const impactSpeed = a.velocity.distanceTo(b.velocity);
      mergeMatterBodies(larger, smaller, config, impactSpeed);
    }

    function updateStellarAging(body, dt, config) {
      if (!config.enableStellarAging || !isStarLike(body) || isCompactObject(body)) {
        return;
      }

      body.age += dt * 1200;
      const burn = config.starFuelBurnRate * dt * Math.pow(Math.max(0.5, body.mass / 100), 1.35);
      body.fuel = Math.max(0, body.fuel - burn);

      if (body.fuel > 0) {
        return;
      }

      if (body.mass >= config.massiveStarThreshold && config.enableSupernovae) {
        triggerSupernova(body, "fuel exhausted", { eventType: body.mass >= config.directCollapseMassThreshold ? "directCollapse" : "coreCollapse" });
      } else {
        const remnantMass = Math.max(8, body.mass * 0.42);
        createGasBurst(body.position, body.velocity, body.mass - remnantMass, 28, 1.4, true);
        createWhiteDwarf(body.position.clone(), body.velocity.clone(), remnantMass, { spin: body.spin + 1 });
        removeBody(body);
        recordStellarEvent("white dwarf from aged star");
      }
    }

    function updateUnstableStar(body, dt, config) {
      if (body.type !== "unstableStar") {
        return;
      }

      body.instability = Math.max(0, body.instability - config.unstableCoolingRate * dt);

      if (Math.random() < Math.min(0.15, body.instability * config.unstableGasSheddingRate * dt)) {
        const shedMass = Math.min(body.mass * 0.012, 2.5);
        body.mass = Math.max(1, body.mass - shedMass);
        createGasBurst(body.position, body.velocity, shedMass, 8, 1.5 + body.instability, true);
      }

      if (body.instability >= config.supernovaInstabilityThreshold && config.enableSupernovae) {
        triggerSupernova(body, "runaway instability", { eventType: "coreCollapse" });
      } else if (body.instability < config.unstableStarInstabilityThreshold * 0.35) {
        body.type = "star";
        recordStellarEvent("unstable star cooled");
      }
    }

    function updateCompactObjectCapture(body, dt, config) {
      if (body.type !== "blackHole") {
        return;
      }

      const captureRadius = body.radius * (config.enableTidalDisruption ? 3.4 : 1.8) * Math.max(0.08, config.galaxy.blackHoleEffectStrength);

      for (const other of [...stellarBodies]) {
        if (!other.alive || other === body || other.type === "supernovaEvent") {
          continue;
        }

        const distance = body.position.distanceTo(other.position);

        if (distance > captureRadius + other.radius) {
          continue;
        }

        const totalMass = body.mass + other.mass;
        body.velocity.multiplyScalar(body.mass).add(other.velocity.clone().multiplyScalar(other.mass)).divideScalar(totalMass);
        body.mass = totalMass;
        body.spin = Math.min(24, body.spin + other.mass / Math.max(1, body.mass));

        if (config.enableTidalDisruption && other.type !== "gas") {
          createGasBurst(other.position, body.velocity, Math.min(other.mass * 0.16, 40), 18, 0.9, true);
        }

        removeBody(other);
        recordStellarEvent(`black hole captured ${other.type}`);
      }
    }

    function isLocalCentralStar(body, params = null) {
      const block = galaxyManager?.activeBlock;

      if (!block?.isDustCube || !isStarLike(body) || isCompactObject(body)) {
        return false;
      }

      const local = blockLocalPositionFromWorld(body.position, block);
      const centralHalfSize = getBlockDisplaySize(block) * 0.5;
      return local.length() <= centralHalfSize * 0.9;
    }

    function effectiveAttractorMass(body, params = null) {
      if (isLocalCentralStar(body, params)) {
        return body.mass * CENTRAL_STAR_GRAVITY_MULTIPLIER;
      }

      return body.mass;
    }

    function updateStellarGravity(dt, params, config) {
      const gravityBase = 0.00018;
      const simulationScale = getSimulationWorldScale(params);
      const scaledGravityBase = gravityBase * Math.pow(simulationScale, 3);
      const softening = Math.max(0.0005, 0.06 * simulationScale);
      const gravityRange = Math.max(
        (params.L1 + params.L2 + 1.5) * 2.4 * simulationScale,
        getSimulationReferenceReach(params) * 2.2
      );

      for (let i = 0; i < stellarBodies.length; i++) {
        const a = stellarBodies[i];

        if (!a.alive || a.type === "supernovaEvent") {
          continue;
        }

        for (let j = i + 1; j < stellarBodies.length; j++) {
          const b = stellarBodies[j];

          if (!b.alive || b.type === "supernovaEvent") {
            continue;
          }

          if ((a.type === "gas" || a.type === "fragment") && (b.type === "gas" || b.type === "fragment")) {
            continue;
          }

          if (
            (a.type === "moon" && a.parentId !== b.id) ||
            (b.type === "moon" && b.parentId !== a.id)
          ) {
            continue;
          }

          if (
            config.systemTravel?.preserveLocalPhysics &&
            (a.parentSystemId || b.parentSystemId)
          ) {
            continue;
          }

          const direction = b.position.clone().sub(a.position);
          const distanceSq = Math.max(softening * softening, direction.lengthSq());
          const distance = Math.sqrt(distanceSq);

          if (distance > gravityRange) {
            continue;
          }

          direction.divideScalar(distance);

          let multiplier = 1;

          if (isCompactObject(a) || isCompactObject(b)) {
            multiplier *= config.compactObjectGravityStrength;
          }

          if (a.type === "gas" || b.type === "gas") {
            multiplier *= 0.35;
          }

          if (
            (PLANET_TYPES.has(a.type) && a.captureState === "stableOrbit" && a.parentId !== b.id) ||
            (PLANET_TYPES.has(b.type) && b.captureState === "stableOrbit" && b.parentId !== a.id)
          ) {
            multiplier *= 0.18;
          }

          const acceleration = scaledGravityBase * multiplier / distanceSq;
          a.velocity.addScaledVector(direction, acceleration * effectiveAttractorMass(b, params) * dt);
          b.velocity.addScaledVector(direction, -acceleration * effectiveAttractorMass(a, params) * dt);
        }
      }
    }

    function handlePendulumBodyCollisions(params, config) {
      const simulationScale = getSimulationWorldScale(params);
      const m2Position = localSimulationToWorld(p2, params);
      const m2Velocity = localSimulationVelocityToWorld(v2, params);
      const m2CollisionMass = Math.max(8, params.m2 * 22);
      const m2Radius = Math.max(0.003, 0.055 * simulationScale);

      for (const body of [...stellarBodies]) {
        if (!body.alive || body.type === "gas" || body.type === "supernovaEvent" || isCompactObject(body)) {
          continue;
        }

        const distance = body.position.distanceTo(m2Position);
        const hitRadius = body.radius + m2Radius;

        if (
          distance > hitRadius ||
          stellarClock < body.protectedUntil ||
          stellarClock - body.lastMajorCollisionTime < 0.12
        ) {
          continue;
        }

        const impactSpeed = m2Velocity.distanceTo(body.velocity);
        const impactEnergy = 0.5 * m2CollisionMass * impactSpeed * impactSpeed * 1000;
        body.lastMajorCollisionTime = stellarClock;

        if (body.mass <= m2CollisionMass * 1.2) {
          const totalMass = body.mass;
          createFragments(body.position, body.velocity.clone().add(m2Velocity.clone().multiplyScalar(0.04)), totalMass * 0.62, 20 + totalMass * 0.4, Math.min(6, impactSpeed * 0.55 + 1));
          createGasBurst(body.position, body.velocity, totalMass * 0.28, 18, Math.min(5, impactSpeed * 0.45 + 0.8), true);
          removeBody(body);
          recordStellarEvent(`m2 shattered ${body.type}`);
        } else {
          const direction = body.position.clone().sub(m2Position);

          if (direction.lengthSq() < 1e-9) {
            direction.copy(randomUnitVector());
          }

          direction.normalize();
          body.velocity.addScaledVector(direction, impactSpeed * 0.025 * m2CollisionMass / body.mass);
          body.instability += impactEnergy / (config.collisionSupernovaEnergyThreshold * 4);
          createGasBurst(body.position, body.velocity, Math.min(body.mass * 0.02, 4), 8, 1.2, true);
          recordStellarEvent(`m2 deflected ${body.type}`);
        }
      }
    }

    function spawnCrossingBlob(params) {
      const position = localSimulationToWorld(p2, params);
      const speed = norm(v2);
      const worldVelocity = localSimulationVelocityToWorld(v2, params);
      const mass = 7 + params.m2 * 8 + Math.min(16, speed * 0.45);
      const radial = position.clone().sub(activeSimulationOrigin);

      if (radial.lengthSq() < 1e-9) {
        radial.copy(randomUnitVector());
      }

      radial.normalize();

      const velocity = worldVelocity.multiplyScalar(0.012)
        .add(radial.multiplyScalar(0.018 * getSimulationWorldScale(params)))
        .add(randomUnitVector().multiplyScalar(0.025 * getSimulationWorldScale(params)));

      const body = createBody("blob", {
        mass,
        position,
        velocity,
        temperature: 1200 + speed * 120,
        protectedAge: 0.45,
        lastMajorCollisionTime: stellarClock,
      });

      galaxyManager?.growthManager?.pendulumAdapter.recordCreatedMass(galaxyManager.activeBlock, body.mass, body.type);
      recordStellarEvent(`path crossing blob (${mass.toFixed(0)})`);
    }

    function detectPathCrossingAndCreateBody(params, config) {
      const current = toVec3(p2);

      if (mathTrail.length >= 2 && config.enableStellarOutcomes) {
        const previous = toVec3(mathTrail[mathTrail.length - 1]);
        const currentDirection = current.clone().sub(previous);
        const maxSegment = mathTrail.length - CROSSING_SKIP_SEGMENTS - 1;
        const threshold = 0.075 + params.m2 * 0.012;
        const thresholdSq = threshold * threshold;

        if (currentDirection.lengthSq() > 1e-9) {
          currentDirection.normalize();
        }

        for (let i = 0; i < maxSegment; i++) {
          if (i === lastCrossingSegment && stepCount - lastCrossingStep < CROSSING_COOLDOWN_STEPS * 2) {
            continue;
          }

          const a = toVec3(mathTrail[i]);
          const b = toVec3(mathTrail[i + 1]);
          const trailDirection = b.clone().sub(a);

          if (trailDirection.lengthSq() <= 1e-9 || currentDirection.lengthSq() <= 1e-9) {
            continue;
          }

          trailDirection.normalize();

          if (Math.abs(currentDirection.dot(trailDirection)) > 0.94) {
            continue;
          }

          if (segmentDistanceSq(previous, current, a, b) <= thresholdSq) {
            if (stepCount - lastCrossingStep >= CROSSING_COOLDOWN_STEPS) {
              spawnCrossingBlob(params);
              lastCrossingStep = stepCount;
              lastCrossingSegment = i;
            }

            break;
          }
        }
      }

      mathTrail.push(fromVec3(current));

      if (mathTrail.length > MAX_MATH_TRAIL_POINTS) {
        mathTrail = mathTrail.slice(mathTrail.length - MAX_MATH_TRAIL_POINTS);
      }
    }

    function ensureBlockSummaryState(block) {
      if (!block.savedDetailState) {
        block.savedDetailState = {
          starsCreated: 0,
          planetsCreated: 0,
          moonsCreated: 0,
          ringedPlanets: 0,
          gasRemaining: 0,
          dustRemaining: 0,
          largestStarMass: 0,
          totalStellarMass: 0,
          totalPlanetMass: 0,
          metallicity: block.metallicity,
          averageVelocity: block.averageVelocity,
          angularMomentum: galacticNormal.toArray(),
          numberOfSolarSystems: 0,
          activeSupernovaRemnants: 0,
          lastEvolutionTime: stellarClock,
          savedSeedOffset: block.seed,
          majorBodiesSummary: [],
          storedBodies: [],
        };
      }

      return block.savedDetailState;
    }

    function serializeOrbitElementsForBlock(elements, block) {
      const cloned = cloneOrbitElements(elements);

      if (!cloned) {
        return null;
      }

      return {
        semiMajorAxis: cloned.semiMajorAxis,
        eccentricity: cloned.eccentricity,
        phase: cloned.phase,
        direction: cloned.direction,
        speedScale: cloned.speedScale,
        normal: blockLocalVectorFromWorld(cloned.normal, block).toArray(),
        periapsisAxis: blockLocalVectorFromWorld(cloned.periapsisAxis, block).toArray(),
        tangentAxis: blockLocalVectorFromWorld(cloned.tangentAxis, block).toArray(),
      };
    }

    function restoreOrbitElementsFromBlock(elements, block) {
      if (!elements) {
        return null;
      }

      return cloneOrbitElements({
        ...elements,
        normal: blockWorldVectorFromLocal(elements.normal || [0, 0, 1], block),
        periapsisAxis: blockWorldVectorFromLocal(elements.periapsisAxis || [1, 0, 0], block),
        tangentAxis: blockWorldVectorFromLocal(elements.tangentAxis || [0, 1, 0], block),
      });
    }

    function serializeBodyForBlock(body, block) {
      return {
        bodyId: body.id,
        parentBodyId: body.parent?.id ?? body.parentId ?? null,
        type: body.type === "blackHole" ? "neutronStar" : body.type,
        mass: body.mass,
        radius: body.radius,
        radiusScale: body.radiusScale,
        position: blockLocalPositionFromWorld(body.position, block).toArray(),
        velocity: blockLocalVectorFromWorld(body.velocity, block).toArray(),
        coordinateFrame: "blockLocal",
        spin: body.spin,
        temperature: body.temperature,
        age: body.age,
        fuel: body.fuel,
        instability: body.instability,
        metallicity: body.metallicity,
        orbitType: body.orbitType,
        captureState: body.captureState,
        stableOrbitAge: body.stableOrbitAge,
        spinAxis: blockLocalVectorFromWorld(body.spinAxis, block).toArray(),
        angularMomentum: blockLocalVectorFromWorld(body.angularMomentum, block).toArray(),
        capturedDebrisMass: body.capturedDebrisMass,
        ringMass: body.ring?.mass ?? 0,
        ringCandidate: body.ringCandidate,
        surfaceSeed: body.surfaceSeed,
        orbitElements: serializeOrbitElementsForBlock(body.orbitElements, block),
      };
    }

    function wrapCoordinateIntoHalfExtent(value, halfExtent) {
      const span = halfExtent * 2;
      let wrapped = value;

      if (span <= 0) {
        return wrapped;
      }

      while (wrapped > halfExtent) {
        wrapped -= span;
      }

      while (wrapped < -halfExtent) {
        wrapped += span;
      }

      return wrapped;
    }

    function expandActiveSimulationSpace(block) {
      if (!block?.isDustCube || getBlockSimulationSpaceMultiplier(block) >= ACTIVE_SPACE_EXPANDED_MULTIPLIER) {
        return false;
      }

      block.simulationSpaceMultiplier = ACTIVE_SPACE_EXPANDED_MULTIPLIER;
      updateBlockBounds(block);
      updateSceneScale(getParams(), true);
      galaxyManager?.updateBlockObjectPositions?.();
      statusEl.textContent = `Expanded ${block.systemName || block.id} to 3x3x3 space`;
      return true;
    }

    function confinePlanetaryBodyInsideBlock(body, block, expandedHalfSize) {
      if (!block?.isDustCube || !(PLANET_TYPES.has(body.type) || body.type === "moon")) {
        return false;
      }

      const margin = Math.max(body.radius * 1.5, getSimulationWorldScale(null, block) * 0.018);
      const safeHalfSize = Math.max(0.0001, expandedHalfSize - margin);
      const local = blockLocalPositionFromWorld(body.position, block);
      const outside = (
        Math.abs(local.x) > safeHalfSize ||
        Math.abs(local.y) > safeHalfSize ||
        Math.abs(local.z) > safeHalfSize
      );

      if (!outside) {
        return false;
      }

      if (body.parent?.alive) {
        const normal = body.type === "moon"
          ? (body.parent.spinAxis || galacticNormal)
          : galacticNormal;

        initialiseEllipticalOrbit(body, body.parent, normal, body.type === "moon" ? 1.08 : 1);
        constrainOrbitElementsToBoundary(body, body.parent, body.orbitElements);

        if (body.orbitElements) {
          const targetLocal = blockLocalPositionFromWorld(
            body.parent.position.clone().add(ellipseRelativePosition(body.orbitElements)),
            block
          );
          targetLocal.x = THREE.MathUtils.clamp(targetLocal.x, -safeHalfSize, safeHalfSize);
          targetLocal.y = THREE.MathUtils.clamp(targetLocal.y, -safeHalfSize, safeHalfSize);
          targetLocal.z = THREE.MathUtils.clamp(targetLocal.z, -safeHalfSize, safeHalfSize);
          body.position.copy(blockWorldPositionFromLocal(targetLocal.toArray(), block));
          clearOrbitElements(body);
          initialiseEllipticalOrbit(body, body.parent, normal, body.type === "moon" ? 1.08 : 1);
          body.velocity.copy(stableOrbitalVelocity(body, body.parent, normal, body.type === "moon" ? 1.05 : 1));
          body.orbitLockedThisFrame = true;
          resetBodyTrail(body);
          return true;
        }
      }

      const velocityLocal = blockLocalVectorFromWorld(body.velocity, block);

      if (local.x > safeHalfSize) {
        local.x = safeHalfSize;
        velocityLocal.x = -Math.abs(velocityLocal.x) * 0.72;
      } else if (local.x < -safeHalfSize) {
        local.x = -safeHalfSize;
        velocityLocal.x = Math.abs(velocityLocal.x) * 0.72;
      }

      if (local.y > safeHalfSize) {
        local.y = safeHalfSize;
        velocityLocal.y = -Math.abs(velocityLocal.y) * 0.72;
      } else if (local.y < -safeHalfSize) {
        local.y = -safeHalfSize;
        velocityLocal.y = Math.abs(velocityLocal.y) * 0.72;
      }

      if (local.z > safeHalfSize) {
        local.z = safeHalfSize;
        velocityLocal.z = -Math.abs(velocityLocal.z) * 0.72;
      } else if (local.z < -safeHalfSize) {
        local.z = -safeHalfSize;
        velocityLocal.z = Math.abs(velocityLocal.z) * 0.72;
      }

      body.position.copy(blockWorldPositionFromLocal(local.toArray(), block));
      body.velocity.copy(blockWorldVectorFromLocal(velocityLocal.toArray(), block));
      resetBodyTrail(body);
      return true;
    }

    function handleBodyLeavingBlock(body, block) {
      if (!block || !body.alive) {
        return false;
      }

      const local = blockLocalPositionFromWorld(body.position, block);
      const centerHalfSize = getBlockDisplaySize(block) * 0.5;

      if (
        block.isDustCube &&
        (
          Math.abs(local.x) > centerHalfSize ||
          Math.abs(local.y) > centerHalfSize ||
          Math.abs(local.z) > centerHalfSize
        )
      ) {
        expandActiveSimulationSpace(block);
      }

      if (block.isDustCube) {
        const expandedHalfSize = getBlockSimulationDisplaySize(block) * 0.5;

        if (
          Math.abs(local.x) <= expandedHalfSize &&
          Math.abs(local.y) <= expandedHalfSize &&
          Math.abs(local.z) <= expandedHalfSize
        ) {
          return false;
        }

        if (confinePlanetaryBodyInsideBlock(body, block, expandedHalfSize)) {
          return false;
        }

        local.set(
          wrapCoordinateIntoHalfExtent(local.x, expandedHalfSize),
          wrapCoordinateIntoHalfExtent(local.y, expandedHalfSize),
          wrapCoordinateIntoHalfExtent(local.z, expandedHalfSize)
        );
        body.position.copy(blockWorldPositionFromLocal(local.toArray(), block));
        resetBodyTrail(body);
        return false;
      }

      const boundary = block.size * 0.62;

      if (
        Math.abs(local.x) <= boundary &&
        Math.abs(local.y) <= boundary &&
        Math.abs(local.z) <= boundary
      ) {
        return false;
      }

      return galaxyManager?.growthManager?.handleBodyLeavingActiveBlock(body, block) ?? false;
    }

    function updateStellarBodies(dt, params) {
      const config = getStellarConfig();
      stellarClock += dt;

      if (!config.enableStellarOutcomes) {
        for (const body of stellarBodies) {
          updateBodyVisual(body, dt);
        }

        return;
      }

      computeGalacticNormal();
      for (const body of stellarBodies) {
        body.orbitLockedThisFrame = false;
      }
      updateParentingAndOrbits(dt, config);
      updateStellarGravity(dt, params, config);
      handlePendulumBodyCollisions(params, config);

      const simulationScale = getSimulationWorldScale(params);
      const universeRadius = getSimulationReferenceReach(params) + 1.65 * simulationScale;
      const simulationOrigin = galaxyManager?.activeBlock ? activeSimulationOrigin : galacticCenter;

      for (const body of [...stellarBodies]) {
        if (!body.alive) {
          continue;
        }

        body.age += dt;
        updateStellarAging(body, dt, config);

        if (!body.alive) {
          continue;
        }

        updateUnstableStar(body, dt, config);

        if (!body.alive) {
          continue;
        }

        updateCompactObjectCapture(body, dt, config);

        if (!body.alive) {
          continue;
        }

        if (body.type === "gas") {
          body.velocity.multiplyScalar(Math.max(0.92, 1 - config.gas.damping * dt));
          body.radius *= 1.0005;
        }

        if (body.type === "fragment") {
          body.mesh.rotation.x += dt * body.spin;
          body.mesh.rotation.y += dt * body.spin * 0.7;
        } else {
          body.mesh.rotation.z += dt * body.spin * 0.15;
        }

        if (body.parentSystemId && config.systemTravel?.preserveLocalPhysics) {
          continue;
        }

        const solvedOrbitThisFrame = body.orbitLockedThisFrame && body.parent?.alive;

        if (!solvedOrbitThisFrame) {
          if (!isStarLike(body) && !PLANET_TYPES.has(body.type) && body.type !== "moon") {
            const outward = body.position.clone().sub(simulationOrigin);

            if (outward.lengthSq() > 1e-9) {
              body.velocity.addScaledVector(outward.normalize(), 0.0015 * simulationScale * dt);
            }
          }

          applyGalacticPlaneAndRotation(body, dt, config);
          clampVectorLength(body.velocity, (body.type === "gas" || body.type === "fragment" ? 7 : 2.2) * simulationScale);
          body.position.addScaledVector(body.velocity, dt);
        }

        if (!body.parentSystemId && galaxyManager?.activeBlock && handleBodyLeavingBlock(body, galaxyManager.activeBlock)) {
          continue;
        }

        if (!finiteVector(body.position) || !finiteVector(body.velocity)) {
          removeBody(body);
          continue;
        }

        if (body.ttl !== Infinity && stellarClock - body.createdAt > body.ttl) {
          removeBody(body);
          continue;
        }

        const simulationRelativePosition = body.position.clone().sub(simulationOrigin);

        if (!body.parentSystemId && simulationRelativePosition.length() > universeRadius * 2.8) {
          if (body.type === "gas" || body.type === "fragment" || body.type === "supernovaEvent") {
            removeBody(body);
            continue;
          }

          if ((PLANET_TYPES.has(body.type) || body.type === "moon") && body.parent?.alive) {
            const relative = body.position.clone().sub(body.parent.position);
            const orbitBoundary = getOrbitBoundaryRadius(body.parent, body);
            const maxOrbitRadius = Number.isFinite(orbitBoundary)
              ? Math.max(body.parent.radius * (body.type === "moon" ? 3.2 : 4.6), orbitBoundary * 0.96)
              : (
                  PLANET_TYPES.has(body.type)
                    ? universeRadius * 0.55
                    : Math.max(body.parent.radius * 8, 0.18 * simulationScale)
                );

            if (relative.lengthSq() > 1e-9) {
              relative.setLength(maxOrbitRadius);
              body.position.copy(body.parent.position).add(relative);
              clearOrbitElements(body);
              initialiseEllipticalOrbit(body, body.parent, body.type === "moon" ? body.parent.spinAxis : galacticNormal, body.type === "moon" ? 1.08 : 1);
              body.velocity.copy(stableOrbitalVelocity(body, body.parent, body.type === "moon" ? body.parent.spinAxis : galacticNormal));
              resetBodyTrail(body);
            }
          } else if ((PLANET_TYPES.has(body.type) || body.type === "moon") && galaxyManager?.activeBlock?.isDustCube) {
            confinePlanetaryBodyInsideBlock(
              body,
              galaxyManager.activeBlock,
              getBlockSimulationDisplaySize(galaxyManager.activeBlock) * 0.5
            );
          } else {
            body.position.copy(simulationOrigin).add(simulationRelativePosition.multiplyScalar(-0.86));
          }
        }

        if (PLANET_TYPES.has(body.type)) {
          updatePlanetRing(body, dt, config);
        }

        updateBodyVisual(body, dt);
        updateBodyTrail(body, config);
      }

      for (let i = 0; i < stellarBodies.length; i++) {
        const a = stellarBodies[i];

        if (!a.alive || a.type === "supernovaEvent") {
          continue;
        }

        for (let j = i + 1; j < stellarBodies.length; j++) {
          const b = stellarBodies[j];

          if (!b.alive || b.type === "supernovaEvent") {
            continue;
          }

          if (
            config.systemTravel?.preserveLocalPhysics &&
            (a.parentSystemId || b.parentSystemId)
          ) {
            continue;
          }

          const hitDistance = (a.radius + b.radius) * 0.92;

          if (a.position.distanceToSquared(b.position) <= hitDistance * hitDistance) {
            handleGenericCollision(a, b, config);
            break;
          }
        }
      }

      updateStarSystemContainers(dt, config);
      updateOrbitAndLinkVisuals(config, params);
      updateStellarStatsDisplay();
    }

    function seedStellarScenario() {
      const params = getParams();
      const config = getStellarConfig();
      const simulationScale = getSimulationWorldScale(params);
      const reach = (params.L1 + params.L2) * simulationScale;
      const systemCenters = [
        new THREE.Vector3(-reach * 0.55, 0, 0.05),
        new THREE.Vector3(reach * 0.55, 0.18, -0.04),
        new THREE.Vector3(0.05, -reach * 0.72, 0.03),
      ];

      for (let i = 0; i < systemCenters.length; i++) {
        const center = activeSimulationOrigin.clone().add(orientSimulationVector(systemCenters[i].clone()));
        const galacticRadial = center.clone().sub(galacticCenter);
        const galacticTangent = new THREE.Vector3().crossVectors(galacticNormal, galacticRadial.clone().normalize()).normalize();
        const star = createBody("star", {
          mass: 115 + i * 35,
          position: center,
          velocity: galacticTangent.multiplyScalar((0.035 + i * 0.012) * simulationScale),
          fuel: 0.85,
          temperature: 5200 + i * 800,
          captureState: "stableOrbit",
          orbitType: "galactic",
        });

        const planetCount = i === 0 ? 3 : 2;

        for (let p = 0; p < planetCount; p++) {
          const orbitRadius = (0.42 + p * 0.24 + i * 0.05) * simulationScale;
          const angle = p * 2.15 + i * 0.9;
          const offset = new THREE.Vector3(
            Math.cos(angle) * orbitRadius,
            Math.sin(angle) * orbitRadius,
            (Math.random() - 0.5) * 0.06 * simulationScale
          );
          orientSimulationVector(offset);
          const planet = createBody("planet", {
            mass: p === 1 && i === 0 ? 78 : 34 + p * 16 + i * 8,
            position: star.position.clone().add(offset),
            velocity: new THREE.Vector3(),
            parent: star,
            orbitType: "stellar",
            captureState: "stableOrbit",
            stableOrbitAge: config.systemStability.minStableAge,
            spin: p === 1 && i === 0 ? 1.25 : 0.45 + Math.random() * 0.45,
            spinAxis: randomUnitVector().lerp(galacticNormal, 0.35).normalize(),
            protectedAge: config.planetFormation.protectedAge,
          });

          planet.velocity.copy(stableOrbitalVelocity(planet, star, galacticNormal, 1));
          planet.age = config.planetFormation.minimumPlanetFormationAge + 1;

          if (p === 0) {
            const moonDirection = planet.spinAxis.clone().cross(offset.clone().normalize());

            if (moonDirection.lengthSq() < 1e-8) {
              moonDirection.copy(randomUnitVector());
            }

            const moonOffset = moonDirection.normalize().multiplyScalar(planet.radius * 5.5);
            const moon = createBody("moon", {
              mass: 5 + i,
              position: planet.position.clone().add(moonOffset),
              velocity: new THREE.Vector3(),
              parent: planet,
              orbitType: "planetary",
              captureState: "stableOrbit",
              stableOrbitAge: config.systemStability.minStableAge,
              spin: 0.25,
              protectedAge: config.planetFormation.protectedAge,
            });

            moon.velocity.copy(stableOrbitalVelocity(moon, planet, planet.spinAxis, 1.05));
          }

          if (p === 1 && i === 0) {
            planet.capturedDebrisMass = config.ring.minCapturedDebrisMass;

            for (let d = 0; d < 5; d++) {
              const debris = createBody("fragment", {
                mass: 2.2,
                position: planet.position.clone().add(randomUnitVector().multiplyScalar(planet.radius * (1.4 + d * 0.18))),
                velocity: planet.velocity.clone().add(randomUnitVector().multiplyScalar(0.02 * simulationScale)),
                ttl: 30,
              });

              absorbIntoRing(planet, debris, config);
            }
          }
        }
      }

      createGasBurst(activeSimulationOrigin, orientSimulationVector(new THREE.Vector3(0.02, 0.01, 0.02).multiplyScalar(simulationScale)), 14, 45, 0.6, false);
      recordStellarEvent("seeded readable solar systems");
      updateStellarStatsDisplay();
    }

    function updateStellarStatsDisplay() {
      if (!stellarStatsEl) {
        return;
      }

      const counts = {
        star: 0,
        unstableStar: 0,
        whiteDwarf: 0,
        neutronStar: 0,
        blackHole: 0,
        planet: 0,
        roguePlanet: 0,
        moon: 0,
        blob: 0,
        gas: 0,
        fragment: 0,
      };

      let largestMass = 0;
      let highestInstability = 0;
      let ringedPlanets = 0;
      let largestRingMass = 0;
      let stableSolarSystems = 0;

      for (const body of stellarBodies) {
        counts[body.type] = (counts[body.type] ?? 0) + 1;
        largestMass = Math.max(largestMass, body.mass);
        highestInstability = Math.max(highestInstability, body.instability ?? 0);

        if (PLANET_TYPES.has(body.type) && body.ring) {
          ringedPlanets++;
          largestRingMass = Math.max(largestRingMass, body.ring.mass);
        }
      }

      for (const body of stellarBodies) {
        if (isStarLike(body) && countChildren(body, child => child.type === "planet" && child.captureState === "stableOrbit") > 0) {
          stableSolarSystems++;
        }
      }

      const averagePlanetsPerStar = counts.star + counts.unstableStar > 0
        ? counts.planet / (counts.star + counts.unstableStar)
        : 0;
      const averageMoonsPerPlanet = counts.planet + counts.roguePlanet > 0
        ? counts.moon / (counts.planet + counts.roguePlanet)
        : 0;
      const config = getStellarConfig();

      stellarStats.largestMass = largestMass;
      stellarStats.highestInstability = highestInstability;

      stellarStatsEl.textContent =
        `Bodies: stars ${counts.star} | unstable ${counts.unstableStar} | WD ${counts.whiteDwarf} | NS ${counts.neutronStar} | BH ${counts.blackHole}\n` +
        `Systems: planets ${counts.planet} | rogue ${counts.roguePlanet} | moons ${counts.moon} | ringed ${ringedPlanets} | stable systems ${stableSolarSystems}\n` +
        `Matter: blobs ${counts.blob} | gas ${counts.gas} | fragments ${counts.fragment}\n` +
        `Events: SN ${stellarStats.supernovae} | Ia ${stellarStats.typeIa} | core ${stellarStats.coreCollapse} | kilo ${stellarStats.kilonova} | pair ${stellarStats.pairInstability} | direct ${stellarStats.directCollapse}\n` +
        `Averages: planets/star ${averagePlanetsPerStar.toFixed(1)} | moons/planet ${averageMoonsPerPlanet.toFixed(1)} | largest ring ${largestRingMass.toFixed(1)}\n` +
        `Galaxy: plane ${config.enableGalacticPlaneSettling ? "on" : "off"} | gas rotation ${config.gas.galacticSpinCoupling.toFixed(2)} | normal z ${galacticNormal.z.toFixed(2)}\n` +
        `Largest mass: ${largestMass.toFixed(1)} | Highest instability: ${highestInstability.toFixed(2)}\n` +
        `Last event: ${stellarStats.lastEvent}`;
    }

    function updateGalaxyStatsDisplay() {
      if (!galaxyStatsEl || !galaxyManager) {
        return;
      }

      const selected = galaxyManager.selectedBlock;
      const active = galaxyManager.activeBlock;
      const memory = renderer.info.memory;
      const selectedSummary = selected
        ? `Selected: ${selected.id} | ${selected.growthState} | mass ${selected.totalMass.toFixed(1)} | stars ${selected.starCount.toLocaleString("en-GB")} | gas ${selected.gasDensity.toFixed(2)}`
        : "Selected: none";
      const growthStats = galaxyManager.growthStats || {};
      const renderedStarPoints =
        (galaxyStarPointGroup.visible ? (galaxyManager.renderedStars || 0) : 0) +
        (focusStarPointGroup.visible ? (galaxyManager.renderedFocusStars || 0) : 0);
      const focusPixels = focusedBlockScreenPixels > 0
        ? `${Math.round(focusedBlockScreenWidth)}x${Math.round(focusedBlockScreenHeight)}px`
        : "n/a";
      const systems = Array.from(starSystemContainers.values());
      const travellingSystems = systems.filter(system => system.isTravellingAroundGalaxy).length;
      const stableSystems = systems.filter(system => system.isStable).length;
      const unstableSystems = Math.max(0, systems.length - stableSystems);
      let selectedSystem = null;
      let selectedSystemDistance = Infinity;

      for (const system of systems) {
        const distance = camera.position.distanceToSquared(system.rootObject.position);

        if (distance < selectedSystemDistance) {
          selectedSystemDistance = distance;
          selectedSystem = system;
        }
      }

      if (selectedSystem) {
        systemTravelStats.selectedSystemId = selectedSystem.id;
        systemTravelStats.selectedSystemRadius = selectedSystem.galacticRadius;
        systemTravelStats.selectedSystemAngularVelocity = selectedSystem.galacticAngularVelocity;
        systemTravelStats.selectedSystemOrbitalSpeed = selectedSystem.galacticVelocity.length();
        systemTravelStats.selectedSystemPlanets = selectedSystem.bodies.filter(body => PLANET_TYPES.has(body.type)).length;
        systemTravelStats.selectedSystemMoons = selectedSystem.bodies.filter(body => body.type === "moon").length;
        systemTravelStats.selectedSystemStabilityAge = selectedSystem.stableAge;
      } else {
        systemTravelStats.selectedSystemId = "none";
        systemTravelStats.selectedSystemRadius = 0;
        systemTravelStats.selectedSystemAngularVelocity = 0;
        systemTravelStats.selectedSystemOrbitalSpeed = 0;
        systemTravelStats.selectedSystemPlanets = 0;
        systemTravelStats.selectedSystemMoons = 0;
        systemTravelStats.selectedSystemStabilityAge = 0;
      }

      galaxyStatsEl.textContent =
        `FPS: ${fpsEstimate.toFixed(0)} | Camera: ${cameraMode} | LOD: ${currentLODMode}\n` +
        `Represented stars: ${galaxyManager.representedStars.toLocaleString("en-GB")} / ${getGalaxyConfig().totalTargetStars.toLocaleString("en-GB")}\n` +
        `Rendered dust cubes: ${renderedStarPoints.toLocaleString("en-GB")} | Visible blocks: ${galaxyManager.visibleBlocks}\n` +
        `Expanded blocks: ${galaxyManager.expandedBlocks} | Active simulated blocks: ${active && active.isSimulated ? 1 : 0} | Cube ${focusPixels}\n` +
        `Growth mass: stars ${Number(growthStats.physicalGrowthMass || 0).toFixed(1)} | inflow ${Number(growthStats.externalMassAdded || 0).toFixed(1)} | pendulum ${Number(growthStats.pendulumCreatedMass || 0).toFixed(1)}\n` +
        `Active detailed bodies: ${stellarBodies.length} | Three objects: ${memory.geometries} geo / ${memory.textures} tex\n` +
        `Systems: stable ${stableSystems} | travelling ${travellingSystems} | unstable ${unstableSystems} | selected ${systemTravelStats.selectedSystemId}\n` +
        `System orbit: r ${systemTravelStats.selectedSystemRadius.toFixed(2)} | speed ${systemTravelStats.selectedSystemOrbitalSpeed.toFixed(3)} | ang ${systemTravelStats.selectedSystemAngularVelocity.toFixed(4)}\n` +
        `System bodies: planets ${systemTravelStats.selectedSystemPlanets} | moons ${systemTravelStats.selectedSystemMoons} | age ${systemTravelStats.selectedSystemStabilityAge.toFixed(1)} | promoted ${systemTravelStats.lastPromotedSystem} | destabilised ${systemTravelStats.lastDestabilisedSystem}\n` +
        `${selectedSummary}`;
    }

    function makeLineSegments(points, color, opacity) {
      const geometry = new THREE.BufferGeometry();
      geometry.setAttribute(
        "position",
        new THREE.Float32BufferAttribute(points.flat(), 3)
      );

      const material = new THREE.LineBasicMaterial({
        color,
        transparent: true,
        opacity,
        depthWrite: false,
      });

      return new THREE.LineSegments(geometry, material);
    }

    function makePlane(size, color, opacity, planeType) {
      const geometry = new THREE.PlaneGeometry(size, size);

      const material = new THREE.MeshBasicMaterial({
        color,
        transparent: true,
        opacity,
        side: THREE.DoubleSide,
        depthWrite: false,
      });

      const mesh = new THREE.Mesh(geometry, material);

      if (planeType === "XZ") {
        mesh.rotation.x = Math.PI / 2;
      }

      if (planeType === "YZ") {
        mesh.rotation.y = Math.PI / 2;
      }

      return mesh;
    }

    function clearReferenceScene() {
      while (referenceGroup.children.length > 0) {
        const child = referenceGroup.children.pop();

        if (child.geometry) {
          child.geometry.dispose();
        }

        if (child.material) {
          child.material.dispose();
        }
      }

      referenceReach = null;
    }

    function drawReferenceScene(reach) {
      clearReferenceScene();

      const r = reach;
      const size = 2 * r;

      const xyPlane = makePlane(size, 0x4aa3ff, PLANE_ALPHA, "XY");
      const xzPlane = makePlane(size, 0xffb347, PLANE_ALPHA, "XZ");
      const yzPlane = makePlane(size, 0x77dd77, PLANE_ALPHA, "YZ");

      referenceGroup.add(xyPlane);
      referenceGroup.add(xzPlane);
      referenceGroup.add(yzPlane);

      const xAxis = makeLineSegments([[-r, 0, 0], [r, 0, 0]], 0xff5555, 0.8);
      const yAxis = makeLineSegments([[0, -r, 0], [0, r, 0]], 0x55ff55, 0.8);
      const zAxis = makeLineSegments([[0, 0, -r], [0, 0, r]], 0x5599ff, 0.8);

      referenceGroup.add(xAxis);
      referenceGroup.add(yAxis);
      referenceGroup.add(zAxis);

      referenceReach = reach;
    }

    function updateSceneScale(params, force = false) {
      if (galaxyManager?.activeBlock?.isDustCube) {
        clearReferenceScene();
        return;
      }

      const reach = getSimulationReferenceReach(params);

      if (force || referenceReach === null || Math.abs(referenceReach - reach) > 1e-9) {
        if (gridVisible) {
          drawReferenceScene(reach);
        } else {
          clearReferenceScene();
        }
      }
    }

    function updateReferenceTransform() {
      referenceGroup.position.copy(activeSimulationOrigin);
      referenceGroup.quaternion.copy(getSimulationOrientation());
    }

    function updateRodDisplay() {
      const params = getParams();
      const simulationScale = getSimulationWorldScale(params);
      const p1World = localSimulationToWorld(p1, params);
      const p2World = localSimulationToWorld(p2, params);

      rodPositions[0] = activeSimulationOrigin.x;
      rodPositions[1] = activeSimulationOrigin.y;
      rodPositions[2] = activeSimulationOrigin.z;

      rodPositions[3] = p1World.x;
      rodPositions[4] = p1World.y;
      rodPositions[5] = p1World.z;

      rodPositions[6] = p2World.x;
      rodPositions[7] = p2World.y;
      rodPositions[8] = p2World.z;

      rodGeometry.attributes.position.needsUpdate = true;

      bob1Mesh.position.copy(p1World);
      bob2Mesh.position.copy(p2World);
      bob1Mesh.scale.setScalar(simulationScale);
      bob2Mesh.scale.setScalar(simulationScale);
    }

    function updateTrailCapacity(maxRecordedSteps) {
      const capacity = Math.max(10, Math.min(5000, Math.round(maxRecordedSteps)));

      if (trail.length > capacity) {
        trail = trail.slice(trail.length - capacity);
      }

      if (capacity !== trailBufferCapacity) {
        trailBufferCapacity = capacity;
        trailPositionBuffer = new Float32Array(trailBufferCapacity * 3);

        trailGeometry.dispose();
        trailGeometry = new THREE.BufferGeometry();

        const attribute = new THREE.BufferAttribute(trailPositionBuffer, 3);
        attribute.setUsage(THREE.DynamicDrawUsage);
        trailGeometry.setAttribute("position", attribute);
        trailGeometry.setDrawRange(0, trail.length);
        trailLine.geometry = trailGeometry;
      }
    }

    function updateTrailDisplay() {
      if (trailBufferCapacity < Math.max(10, trail.length)) {
        updateTrailCapacity(Math.max(10, trail.length));
      }

      for (let i = 0; i < trail.length; i++) {
        trailPositionBuffer[i * 3 + 0] = trail[i][0];
        trailPositionBuffer[i * 3 + 1] = trail[i][1];
        trailPositionBuffer[i * 3 + 2] = trail[i][2];
      }

      trailGeometry.setDrawRange(0, trail.length);

      if (trailGeometry.attributes.position) {
        trailGeometry.attributes.position.needsUpdate = true;
      }
    }

    function clearTrail() {
      trail = [];
      mathTrail = [];
      lastCrossingStep = -Infinity;
      lastCrossingSegment = -1;
      updateTrailDisplay();
    }

    function resetFromSliders() {
      const params = getParams();

      updateTrailCapacity(params.maxRecordedSteps);

      const initial = makeInitialState(params);
      const projected = projectConstraintsFast(
        initial.p1,
        initial.p2,
        initial.v1,
        initial.v2,
        params
      );

      p1 = projected.p1;
      p2 = projected.p2;
      v1 = projected.v1;
      v2 = projected.v2;

      stepCount = 0;

      clearTrail();
      updateSceneScale(params, true);
      updateReferenceTransform();
      updateRodDisplay();

      statusEl.textContent = "Ready";
    }

    document.getElementById("startButton").addEventListener("click", () => {
      if (!galaxyManager?.activeBlock) {
        galaxyManager?.activateSelected();
        return;
      }

      galaxyManager.activeBlock.isSimulated = true;
      running = true;
      statusEl.textContent = "Running";
    });

    document.getElementById("stopButton").addEventListener("click", () => {
      running = false;
      statusEl.textContent = `Stopped | steps: ${stepCount}`;
    });

    document.getElementById("clearButton").addEventListener("click", () => {
      running = false;
      stepCount = 0;
      clearTrail();
      clearStellarBodies();
      statusEl.textContent = "Cleared";
    });

    document.getElementById("resetButton").addEventListener("click", () => {
      running = false;
      setDefaultSliders();
      resetFromSliders();
      statusEl.textContent = "Reset to defaults";
    });

    document.getElementById("gridButton").addEventListener("click", () => {
      gridVisible = !gridVisible;

      const button = document.getElementById("gridButton");

      if (gridVisible) {
        button.textContent = "Hide Grid";
        updateSceneScale(getParams(), true);
      } else {
        button.textContent = "Show Grid";
        clearReferenceScene();
      }
    });

    document.getElementById("seedStarsButton").addEventListener("click", () => {
      seedStellarScenario();
      statusEl.textContent = `Seeded | bodies: ${stellarBodies.length}`;
    });

    document.getElementById("clearBodiesButton").addEventListener("click", () => {
      clearStellarBodies();
      statusEl.textContent = "Cleared stellar bodies";
    });

    document.getElementById("expandBlockButton").addEventListener("click", () => {
      galaxyManager?.expandSelected();
    });

    document.getElementById("collapseBlockButton").addEventListener("click", () => {
      galaxyManager?.collapseSelected();
    });

    document.getElementById("simulateBlockButton").addEventListener("click", () => {
      galaxyManager?.activateSelected();
    });

    document.getElementById("pauseBlockButton").addEventListener("click", () => {
      galaxyManager?.pauseActiveBlock();
    });

    document.getElementById("focusBlockButton").addEventListener("click", () => {
      galaxyManager?.focusSelectedBlock();
    });

    document.getElementById("galaxyViewButton").addEventListener("click", () => {
      galaxyManager?.returnToGalaxyView();
    });

    document.getElementById("saveGalaxyButton").addEventListener("click", () => {
      if (!galaxyManager) {
        return;
      }

      localStorage.setItem("galaxyCreator.blockState", JSON.stringify(galaxyManager.saveState()));
      statusEl.textContent = "Galaxy state saved";
    });

    document.getElementById("loadGalaxyButton").addEventListener("click", () => {
      const raw = localStorage.getItem("galaxyCreator.blockState");

      if (!raw || !galaxyManager) {
        statusEl.textContent = "No saved galaxy state";
        return;
      }

      galaxyManager.loadState(JSON.parse(raw));
      statusEl.textContent = "Galaxy state loaded";
    });

    saveAccountButton.addEventListener("click", saveAccountUniverse);
    loadAccountButton.addEventListener("click", loadAccountUniverse);
    logoutButton.addEventListener("click", signOutAccount);

    let cameraAzim = 0.65;
    let cameraElev = 0.42;
    let cameraDistance = 5.8;

    function getCameraDistanceLimits(config = getGalaxyConfig()) {
      const focusBlock = galaxyManager?.getBuildFocusBlock?.();
      const focusedBlockSize = focusBlock ? getBlockOutlineDisplaySize(focusBlock) : 0;
      const focusedBlockMax = focusBlock ? focusedBlockSize * 110 : 0;
      const minDistance = focusBlock?.isDustCube
        ? Math.max(0.02, focusedBlockSize * 0.35)
        : 0.9;

      return {
        min: minDistance,
        max: Math.max(60, config.radius * 3.0, config.lodDistanceGalaxy * 1.6, focusedBlockMax),
      };
    }

    function setCameraDistance(value) {
      const limits = getCameraDistanceLimits();
      cameraDistance = Math.max(limits.min, Math.min(limits.max, value));
    }

    function updateCamera() {
      const x = cameraDistance * Math.cos(cameraElev) * Math.cos(cameraAzim);
      const y = cameraDistance * Math.cos(cameraElev) * Math.sin(cameraAzim);
      const z = cameraDistance * Math.sin(cameraElev);

      camera.position.set(cameraTarget.x + x, cameraTarget.y + y, cameraTarget.z + z);
      camera.lookAt(cameraTarget);
    }

    function panCameraTarget(horizontal, vertical) {
      camera.updateMatrixWorld();

      const right = new THREE.Vector3().setFromMatrixColumn(camera.matrixWorld, 0);
      const up = new THREE.Vector3().setFromMatrixColumn(camera.matrixWorld, 1);
      const panStep = Math.max(0.04, cameraDistance * 0.085);

      cameraTarget
        .addScaledVector(right, horizontal * panStep)
        .addScaledVector(up, vertical * panStep);

      if (galaxyManager) {
        galaxyManager.blockFocusLocked = false;
      }

      cameraPanLocked = true;
      cameraMode = "Panned View";
      updateCamera();
    }

    function bindPanButton(button, horizontal, vertical) {
      if (!button) {
        return;
      }

      let repeatTimer = null;

      const stopRepeat = () => {
        if (repeatTimer) {
          clearInterval(repeatTimer);
          repeatTimer = null;
        }
      };

      button.addEventListener("pointerdown", event => {
        event.preventDefault();
        event.stopPropagation();
        panCameraTarget(horizontal, vertical);
        stopRepeat();
        repeatTimer = setInterval(() => panCameraTarget(horizontal, vertical), 90);
        button.setPointerCapture(event.pointerId);
      });

      button.addEventListener("pointerup", event => {
        stopRepeat();
        button.releasePointerCapture(event.pointerId);
      });

      button.addEventListener("pointercancel", stopRepeat);
      button.addEventListener("pointerleave", stopRepeat);
      button.addEventListener("keydown", event => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          panCameraTarget(horizontal, vertical);
        }
      });
    }

    bindPanButton(panButtons.left, -1, 0);
    bindPanButton(panButtons.right, 1, 0);
    bindPanButton(panButtons.up, 0, 1);
    bindPanButton(panButtons.down, 0, -1);

    function measureBlockScreenSize(block) {
      if (!block) {
        return { width: 0, height: 0, pixels: 0 };
      }

      const width = renderer.domElement.clientWidth || renderer.domElement.width || 1;
      const height = renderer.domElement.clientHeight || renderer.domElement.height || 1;
      const half = getBlockOutlineDisplaySize(block) * 0.5;
      const projected = new THREE.Vector3();
      const offset = new THREE.Vector3();
      let minX = Infinity;
      let maxX = -Infinity;
      let minY = Infinity;
      let maxY = -Infinity;

      for (const dx of [-half, half]) {
        for (const dy of [-half, half]) {
          for (const dz of [-half, half]) {
            offset.set(dx, dy, dz).applyQuaternion(block.orientation);
            projected.copy(block.center).add(offset);
            projected.project(camera);

            if (!Number.isFinite(projected.x) || !Number.isFinite(projected.y)) {
              continue;
            }

            const screenX = (projected.x * 0.5 + 0.5) * width;
            const screenY = (-projected.y * 0.5 + 0.5) * height;
            minX = Math.min(minX, screenX);
            maxX = Math.max(maxX, screenX);
            minY = Math.min(minY, screenY);
            maxY = Math.max(maxY, screenY);
          }
        }
      }

      if (!Number.isFinite(minX) || !Number.isFinite(minY)) {
        return { width: 0, height: 0, pixels: 0 };
      }

      const measuredWidth = Math.max(0, maxX - minX);
      const measuredHeight = Math.max(0, maxY - minY);

      return {
        width: measuredWidth,
        height: measuredHeight,
        pixels: Math.max(measuredWidth, measuredHeight),
      };
    }

    updateCamera();
    galaxyManager = new GalaxyManager();
    galaxyManager.returnToGalaxyView();
    updateGalaxyStatsDisplay();

    let dragging = false;
    let lastMouseX = 0;
    let lastMouseY = 0;

    renderer.domElement.addEventListener("pointerdown", event => {
      dragging = true;
      lastMouseX = event.clientX;
      lastMouseY = event.clientY;
      renderer.domElement.dataset.pointerStartX = String(event.clientX);
      renderer.domElement.dataset.pointerStartY = String(event.clientY);
      renderer.domElement.setPointerCapture(event.pointerId);
    });

    renderer.domElement.addEventListener("pointermove", event => {
      if (!dragging) {
        return;
      }

      const dx = event.clientX - lastMouseX;
      const dy = event.clientY - lastMouseY;

      lastMouseX = event.clientX;
      lastMouseY = event.clientY;

      cameraAzim -= dx * 0.006;
      cameraElev += dy * 0.006;

      cameraElev = Math.max(-1.35, Math.min(1.35, cameraElev));

      updateCamera();
    });

    renderer.domElement.addEventListener("pointerup", event => {
      const startX = Number(renderer.domElement.dataset.pointerStartX ?? event.clientX);
      const startY = Number(renderer.domElement.dataset.pointerStartY ?? event.clientY);
      const moved = Math.hypot(event.clientX - startX, event.clientY - startY);
      dragging = false;
      renderer.domElement.releasePointerCapture(event.pointerId);

      if (moved < 4) {
        galaxyManager?.pickBlock(event);
      }
    });

    renderer.domElement.addEventListener("wheel", event => {
      event.preventDefault();

      setCameraDistance(cameraDistance * (event.deltaY > 0 ? 1.08 : 0.92));
      galaxyManager?.updateProgressiveCameraFocus(true);
      updateCamera();
    }, { passive: false });

    window.addEventListener("resize", () => {
      applyRendererLayout();
    });

    function animate() {
      requestAnimationFrame(animate);

      const now = performance.now();
      const frameDt = Math.min(0.05, Math.max(0.001, (now - lastFrameTime) / 1000));
      lastFrameTime = now;
      fpsEstimate = fpsEstimate * 0.92 + (1 / frameDt) * 0.08;
      let stellarDetailUpdated = false;

      galaxyManager?.update(frameDt, getGalaxyConfig());

      if (
        running &&
        galaxyManager?.activeBlock?.isSimulated &&
        focusedBlockScreenPixels >= BLOCK_DETAIL_MIN_SCREEN_PX
      ) {
        const params = getParams();
        const stellarConfig = getStellarConfig();

        updateTrailCapacity(params.maxRecordedSteps);

        for (let i = 0; i < PHYSICS_SUBSTEPS_PER_FRAME; i++) {
          physicsStep(params);
          detectPathCrossingAndCreateBody(params, stellarConfig);
        }

        updateStellarBodies(params.dt, params);
        stellarDetailUpdated = true;

        if (stepCount % TRAIL_STRIDE === 0) {
          trail.push(localSimulationToWorld(p2, params).toArray());
          updateTrailCapacity(params.maxRecordedSteps);
        }

        updateRodDisplay();
        updateTrailDisplay();

        if (running) {
          statusEl.textContent =
            `${currentLODMode} | block ${galaxyManager.activeBlock.id} | steps: ${stepCount} | ` +
            `trail: ${trail.length}/${params.maxRecordedSteps} | ` +
            `bodies: ${stellarBodies.length} | ` +
            `max speed: ${params.maxSpeed.toFixed(1)}`;
        }
      } else if (running && galaxyManager?.activeBlock?.isSimulated) {
        statusEl.textContent =
          `Paused detail | block ${galaxyManager.activeBlock.id} | ` +
          `cube ${Math.round(focusedBlockScreenWidth)}x${Math.round(focusedBlockScreenHeight)}px ` +
          `below ${BLOCK_DETAIL_MIN_SCREEN_PX}px`;
      }

      if (!stellarDetailUpdated && stellarBodies.length > 0) {
        const stellarConfig = getStellarConfig();

        if (stellarConfig.enableStellarOutcomes && stellarConfig.systemTravel.enabled) {
          stellarClock += frameDt;
          updateStarSystemContainers(frameDt, stellarConfig);
          updateGalaxyStatsDisplay();
          updateStellarStatsDisplay();
        }
      }

      renderer.render(scene, camera);
    }

    resetFromSliders();
    updateStellarStatsDisplay();
    initialiseAccount();
    animate();
